// BoardDisplay.jsx
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './Board.css';
import { useGame } from './context/GameContext';
import { useSound } from './sfx/SoundProvider';
import GnuAnalysisViewer from './components/GnuAnalysisViewer';
import socket, {
  ensureSocketAuthAndConnect,
  rejoinAndResync
} from './socket'; // ← поправь путь до файла socket.js, если другой

// Логи можно включать для отладки (например, если кубики/ходы отображаются странно).
// В продакшене лучше оставлять DEBUG=false, чтобы консоль не «засорялась».
const DEBUG = false;
const debugLog = (...args) => { if (DEBUG) console.log(...args); };
const debugWarn = (...args) => { if (DEBUG) console.warn(...args); };

const SHORT_BOARD_SKIN_STORAGE_KEY = 'shortBoardSkin';
const SHORT_BOARD_SKINS = ['classic', 'luxury', 'carbon', 'royal'];

const readInitialShortBoardSkin = () => {
  if (typeof window === 'undefined') return 'classic';

  const savedSkin = localStorage.getItem(SHORT_BOARD_SKIN_STORAGE_KEY);

  return SHORT_BOARD_SKINS.includes(savedSkin)
    ? savedSkin
    : 'classic';
};


const SHORT_BOARD_SKIN_OPTIONS = [
  {
    id: 'classic',
    src: '/img/Board33.png',
    labelKey: 'game.short.classicBoard',
    defaultLabel: 'Классическая',
  },
  {
    id: 'luxury',
    src: '/img/BoardRoyalFire_v2.png',
    labelKey: 'game.short.luxuryBoard',
    defaultLabel: 'Премиальная',
  },
  {
    id: 'carbon',
    src: '/img/BoardCarbon_v1.png',
    labelKey: 'game.short.carbonBoard',
    defaultLabel: 'Чёрный мрамор',
  },
  {
    id: 'royal',
    src: '/img/BoardRoyal_v1.png',
    labelKey: 'game.short.royalBoard',
    defaultLabel: 'Королевская',
  },
];


const AutoRollIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="8" cy="8" r="1.25" fill="currentColor" />
    <circle cx="16" cy="8" r="1.25" fill="currentColor" />
    <circle cx="12" cy="12" r="1.25" fill="currentColor" />
    <circle cx="8" cy="16" r="1.25" fill="currentColor" />
    <circle cx="16" cy="16" r="1.25" fill="currentColor" />
  </svg>
);

const AutoMoveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M5 5.5 11 12 5 18.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 5.5 18 12 12 18.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M9.6 3.5h4.8l.7 2.2c.5.2 1 .5 1.5.9l2.2-.5 2.4 4.1-1.6 1.7v1.8l1.6 1.7-2.4 4.1-2.2-.5c-.5.4-1 .7-1.5.9l-.7 2.2H9.6l-.7-2.2c-.5-.2-1-.5-1.5-.9l-2.2.5-2.4-4.1 1.6-1.7v-1.8l-1.6-1.7 2.4-4.1 2.2.5c.5-.4 1-.7 1.5-.9l.7-2.2Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.55"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12.8" r="3" fill="none" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="m6.5 12.5 3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);




const BoardDisplay = ({
  board,
  handleRollDiceClick,
  countdownMessage,
  tournamentPlayer1Name,
  tournamentPlayer2Name,
  opponentName,
  handleTournamentFirstMove,
  firstRolls,
  player1Roll,
  player2Roll,
  countdown,
  tournamentHandleReadyClick,
  schetchik,
  firstRollComplete,
  showReadyButton,
  hasRolledDice,
  confirmInProgress = false,
  currentTurn,
  showUndoButton,
  handleUndoMove,
  numberMove,
  confirmMove,
  whiteOffBoard,
  setWhiteOffBoard,
  blackOffBoard,
  tournamentSetBlackOffBoard,
  onPointClick,
  handleTournamentDragOver,
  handleDragStart,
  onCheckerClick,
  handleDrop,
  handleDragOver,
  handleFirstRollClick,
  firstMoveComplete,
  handleCloseResultModal,
  showResultModal,
  gameResult,
  gameFinished,
  handleLeaveTable,
  // player1Time,
  // player2Time,
   player1TimeRef,
  player2TimeRef,
  mobilePlayer1TimeRef,
  mobilePlayer2TimeRef,
  canOfferDouble,
  betMultiplier,
  onOfferDouble,
player1Name,
player2Name,
  /* -------- модалка удвоения -------- */
  showDoubleDialog,
  doubleOffer,
  onAcceptDouble,
  onDeclineDouble,
  cubeOwner,
  readOnly,
  onSurrenderClick,
  player1Rating,
  player2Rating,
  player1Avatar,
  player2Avatar,
  hideCube,
  isCrawford = false,
  // матчи/очки
  scoreP1,
  scoreP2,
  targetPoints,
  autoRoll,
  toggleAutoRoll,
  autoConfirm,
  toggleAutoConfirm,
  canConfirmMove,
  gnuAnalysis,
  confirmedTurns,
  cubeEvents,

  // ===== матч: навигация по партиям (для анализа) =====
  matchGames,
  selectedMatchGameIndex,
  onSelectMatchGame,
  matchGameKey,
}) => {
  const { t: translate } = useTranslation();
  const {
    capturedCheckers,
    rolledDice,
    readyStatus,
    rollEnabled,
    setRollEnabled,
    tournamentGame,
    setTournamentFirstRollComplete,
    tournamentIsFirstMoveDone,
    tournamentFlagFirst,
    setTournamentFlagFirst,
    setRolledDice,
    boardState,
    setBoardState,
    playerColor,
    setPlayerColor,
    opponentColor,
    setOpponentColor, 
  } = useGame();

  // const [showResignDialog, setShowResignDialog] = useState(false);

// Визуальный скин короткой доски.
// Меняются только фон и изображения шашек.
// Координаты пунктов, клики и drag-and-drop не изменяются.
const [shortBoardSkin, setShortBoardSkin] = useState(readInitialShortBoardSkin);
const [showBoardSettings, setShowBoardSettings] = useState(false);

// Подсказка при клике по заблокированному кубу в партии Кроуфорда.
const [showCrawfordHint, setShowCrawfordHint] = useState(false);
const crawfordHintTimerRef = useRef(null);

const isLuxuryShortBoard = shortBoardSkin === 'luxury';
const isCarbonShortBoard = shortBoardSkin === 'carbon';
const isRoyalShortBoard = shortBoardSkin === 'royal';

const shortBoardImageSrc = isRoyalShortBoard
  ? '/img/BoardRoyal_v1.png'
  : isCarbonShortBoard
    ? '/img/BoardCarbon_v1.png'
    : isLuxuryShortBoard
      ? '/img/BoardRoyalFire_v2.png'
      : '/img/Board33.png';

const shortBoardImageAlt = isRoyalShortBoard
  ? 'Royal luxury backgammon board'
  : isCarbonShortBoard
    ? 'Carbon backgammon board'
    : isLuxuryShortBoard
      ? 'Luxury backgammon board'
      : 'Backgammon board';

const shortBoardGhostClass = isRoyalShortBoard
  ? ' checker-royal-ghost'
  : isCarbonShortBoard
    ? ' checker-carbon-ghost'
    : isLuxuryShortBoard
      ? ' checker-luxury-ghost'
      : '';

const selectShortBoardSkin = React.useCallback((skinId) => {
  if (!SHORT_BOARD_SKINS.includes(skinId)) return;

  setShortBoardSkin(skinId);
  setShowBoardSettings(false);
}, []);

const closeBoardSettings = React.useCallback(() => {
  setShowBoardSettings(false);
}, []);

useEffect(() => {
  if (typeof window === 'undefined') return;

  localStorage.setItem(SHORT_BOARD_SKIN_STORAGE_KEY, shortBoardSkin);

  // Предзагрузка исключает мигание при первом переключении.
  const imagesToPreload = [
    '/img/BoardRoyalFire_v2.png',
    '/img/BoardCarbon_v1.png',
    '/img/BoardRoyal_v1.png',
  ];

  imagesToPreload.forEach((src) => {
    const image = new Image();
    image.src = src;
  });
}, [shortBoardSkin]);


useEffect(() => {
  if (!showBoardSettings || typeof document === 'undefined') return undefined;

  const previousOverflow = document.body.style.overflow;
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setShowBoardSettings(false);
    }
  };

  document.body.style.overflow = 'hidden';
  document.addEventListener('keydown', handleKeyDown);

  return () => {
    document.body.style.overflow = previousOverflow;
    document.removeEventListener('keydown', handleKeyDown);
  };
}, [showBoardSettings]);

useEffect(() => {
  // При выходе из партии Кроуфорда сразу убираем подсказку.
  if (!isCrawford) {
    setShowCrawfordHint(false);
  }

  return () => {
    if (crawfordHintTimerRef.current) {
      clearTimeout(crawfordHintTimerRef.current);
      crawfordHintTimerRef.current = null;
    }
  };
}, [isCrawford]);

// Во время живой партии на обычных экранах блокируем прокрутку страницы.
// ВАЖНО: tablet/compact 768..1366 НЕ блокируем.
// Там анализ расположен ПОД доской, поэтому странице обязательно нужен
// обычный вертикальный скролл вниз.
useEffect(() => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return undefined;
  }

  const html = document.documentElement;
  const body = document.body;

  const syncShortBoardPageLock = () => {
    const vv = window.visualViewport;
    const vw = Math.round(vv?.width || window.innerWidth || 0);

    const tabletScrollable = vw >= 768 && vw <= 1366;

    const shouldLock =
      !gameFinished &&
      !showResultModal &&
      !tabletScrollable;

    html.classList.toggle('short-board-page-lock', shouldLock);
    body.classList.toggle('short-board-page-lock', shouldLock);
  };

  syncShortBoardPageLock();

  window.addEventListener('resize', syncShortBoardPageLock, { passive: true });
  window.visualViewport?.addEventListener(
    'resize',
    syncShortBoardPageLock,
    { passive: true }
  );

  return () => {
    window.removeEventListener('resize', syncShortBoardPageLock);
    window.visualViewport?.removeEventListener(
      'resize',
      syncShortBoardPageLock
    );

    html.classList.remove('short-board-page-lock');
    body.classList.remove('short-board-page-lock');
  };
}, [gameFinished, showResultModal]);
  // const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const [showResignDialog, setShowResignDialog] = useState(false);



// флаг "мобильная раскладка":
// - телефоны/планшеты по userAgent
// - ИЛИ просто узкое окно (например, 1024px и меньше)
const [isMobile, setIsMobile] = useState(() => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 767; // только телефон
});

const [isTablet, setIsTablet] = useState(() => {
  if (typeof window === 'undefined') return false;
  const vw = window.innerWidth;
  return vw >= 768 && vw <= 1366; // планшет
});



const [analysisViewport, setAnalysisViewport] = useState(() => {
  if (typeof window === 'undefined') {
    return { stacked: false, scale: 1 };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // <=1366: tablet/compact — доска сверху, анализ под ней.
  // >=1367: desktop — анализ слева, доска справа.
  const stacked = vw <= 1366;

  const horizontalPadding = vw <= 767 ? 12 : 24;
  const sideReserve = !stacked && vw <= 1366 ? 263 : 0;

  const scaleX = (vw - horizontalPadding - sideReserve) / 1020;
  const scaleY = stacked
    ? (vh - (vw <= 767 ? 170 : 216)) / 760
    : (vh - 16) / 760;

  // Телефон оставляем как раньше: учитываем и ширину, и высоту.
  // Планшет 768..1366: анализ находится под доской, поэтому
  // доску больше НЕ ужимаем по высоте viewport — только по ширине.
  const nextScale =
    vw <= 767
      ? Math.max(0.34, Math.min(1, scaleX, scaleY))
      : vw <= 1366
      ? Math.max(0.34, Math.min(1, scaleX))
      : 1;

  return {
    stacked,
    scale: nextScale,
  };
});


// Правим отображение на всех мониторах

// === TOUCH DnD ===
const touchDrag = useRef({
  dragging: false,
  fromPoint: null,
  fromIndex: null,
  color: null,
  ghost: null
});
// сюда будем складывать данные, которые родитель пишет в e.dataTransfer
const touchDataRef = useRef({});
const touchMovedRef = useRef(false);
// const boardRef = useRef(null);
// const [bestArrows, setBestArrows] = useState([]);
// const [boardVb, setBoardVb] = useState({ w: 0, h: 0 });

const boardRef = useRef(null);

const arrowSvgRef = useRef(null);

const turnsListRef = useRef(null);
const turnsListScrollTopRef = useRef(0);

const setTurnsListNode = React.useCallback((node) => {
  turnsListRef.current = node;

  if (!node) return;

  node.scrollTop = turnsListScrollTopRef.current;
}, []);

const rememberTurnsListScroll = React.useCallback(() => {
  const el = turnsListRef.current;
  if (!el) return;

  turnsListScrollTopRef.current = el.scrollTop;
}, []);

const handleTurnsListScroll = React.useCallback((e) => {
  turnsListScrollTopRef.current = e.currentTarget.scrollTop;
}, []);

// ✅ GNU (правильный ход) — зелёные стрелки
const [gnuArrows, setGnuArrows] = useState([]);

// ✅ Сыгранный нами ход — красные/жёлтые стрелки
const [playedArrows, setPlayedArrows] = useState([]);

// 'red' | 'yellow' | null
const [playedArrowTone, setPlayedArrowTone] = useState(null);

const [boardVb, setBoardVb] = useState({ w: 0, h: 0 });


// 👇 ВСЕГДА используем её, чтобы убрать фантом и сбросить состояние тача
const cleanupTouchDrag = () => {
  try {
    const t = touchDrag.current;
    if (t.ghost) {
      t.ghost.remove(); // убрать div из DOM
    }
    // сбрасываем всё состояние текущего перетаскивания
    touchDrag.current = {
      dragging: false,
      fromPoint: null,
      fromIndex: null,
      color: null,
      ghost: null
    };
  } catch (err) {
    debugWarn('[touchCleanup] error', err);
  }

 
};

const showTouchGhostOnly = (e, color) => {
  cleanupTouchDrag();
  touchMovedRef.current = false;

  if (!e.touches || e.touches.length === 0) return;

  const t = e.touches[0];

  const ghost = document.createElement("div");
  ghost.className = `checker ${color} drag-ghost${shortBoardGhostClass}`;
  ghost.style.position = "fixed";
  ghost.style.left = t.clientX - 20 + "px";
  ghost.style.top = t.clientY - 20 + "px";
  ghost.style.width = "40px";
  ghost.style.height = "40px";
  ghost.style.zIndex = 9999;
  document.body.appendChild(ghost);

  touchDrag.current.ghost = ghost;
};



function buildStandardInitialBoard() {
  const make = (n, color) => Array.from({ length: n }, () => ({ color }));

  const b = Array.from({ length: 24 }, () => []);

  // Белые: 24(2), 13(5), 8(3), 6(5)
  b[23] = make(2, "white"); // point 24
  b[12] = make(5, "white"); // point 13
  b[7]  = make(3, "white"); // point 8
  b[5]  = make(5, "white"); // point 6

  // Чёрные: 1(2), 12(5), 17(3), 19(5)
  b[0]  = make(2, "black"); // point 1
  b[11] = make(5, "black"); // point 12
  b[16] = make(3, "black"); // point 17
  b[18] = make(5, "black"); // point 19

  return b;
}




// ===== SVG иконка шашки (white/black) =====
const CheckerSvg = ({ color = 'white', size = 14, className = '' }) => {
  const isBlack = String(color).toLowerCase() === 'black';

  // ✅ уникальный id на инстанс, чтобы градиенты не конфликтовали
 // ✅ уникальный id на инстанс, чтобы градиенты не конфликтовали
const uid = React.useMemo(() => Math.random().toString(36).slice(2), []);
const gradId = `chkGrad-${isBlack ? 'b' : 'w'}-${uid}`;


  const stroke = isBlack ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)';

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient id={gradId} cx="30%" cy="30%" r="70%">
          {isBlack ? (
            <>
              <stop offset="0%" stopColor="#444" />
              <stop offset="55%" stopColor="#1b1b1b" />
              <stop offset="100%" stopColor="#000" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="55%" stopColor="#f1f1f1" />
              <stop offset="100%" stopColor="#cfcfcf" />
            </>
          )}
        </radialGradient>
      </defs>

      <circle
        cx="32"
        cy="32"
        r="26"
        fill={`url(#${gradId})`}
        stroke={stroke}
        strokeWidth="3"
      />

      <circle
        cx="24"
        cy="22"
        r="10"
        fill={isBlack ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)'}
      />

      <circle
        cx="32"
        cy="32"
        r="20"
        fill="transparent"
        stroke={isBlack ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'}
        strokeWidth="2"
      />
    </svg>
  );
};




// ===== SVG иконка КУБА (для списка ходов) =====
const CubeSvg = ({ size = 16, className = '' }) => {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      {/* контур куба */}
      <path
        d="M21 7.5 12 3 3 7.5 12 12 21 7.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M21 7.5V16.5L12 21 3 16.5V7.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M12 12V21"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {/* точка на грани (для красоты) */}
      <circle cx="12" cy="8.8" r="1.2" fill="currentColor" opacity="0.9" />
    </svg>
  );
};






  // ⬇️ ДОБАВЬ ВОТ ЭТО
  // Масштабируем короткий стол под размер окна
  const [boardScale, setBoardScale] = useState(1);
  const [isPortrait, setIsPortrait] = useState(false); // портрет/альбом
  const [isMobileWidth, setIsMobileWidth] = useState(false);

  const isTouchDevice =
  typeof navigator !== 'undefined' &&
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  
  // 📱 если телефон узкий и держат вертикально — насильно крутим интерфейс в "ландшафт"
const forceLandscape = isMobileWidth && isPortrait;

/*
 * CASH / PHONE LANDSCAPE
 * Отдельный режим только для URL /ShortBackgammonBoard/:id.
 * Турнирные, длинные и остальные доски этот флаг не получают.
 *
 * Важно: ориентируемся на visualViewport, как в большой турнирной доске,
 * потому что на iPhone адресная строка и DevTools могут менять реальную
 * доступную высоту viewport.
 */
const [cashPhoneLandscape, setCashPhoneLandscape] = useState(() => {
  if (typeof window === 'undefined') return false;

  const visualViewport = window.visualViewport;
  const viewportWidth = Math.round(visualViewport?.width || window.innerWidth || 0);
  const viewportHeight = Math.round(visualViewport?.height || window.innerHeight || 0);
  const isCashRoute = /\/ShortBackgammonBoard(?:\/|$)/i.test(
    window.location?.pathname || ''
  );

  return Boolean(
    isCashRoute &&
    viewportWidth > viewportHeight &&
    viewportWidth <= 1100 &&
    viewportHeight <= 600
  );
});

useEffect(() => {
  if (typeof window === 'undefined') return undefined;

  let frameId = 0;

  const syncCashPhoneLandscape = () => {
    window.cancelAnimationFrame(frameId);

    frameId = window.requestAnimationFrame(() => {
      const visualViewport = window.visualViewport;
      const viewportWidth = Math.round(
        visualViewport?.width || window.innerWidth || 0
      );
      const viewportHeight = Math.round(
        visualViewport?.height || window.innerHeight || 0
      );
      const isCashRoute = /\/ShortBackgammonBoard(?:\/|$)/i.test(
        window.location?.pathname || ''
      );

      setCashPhoneLandscape(
        Boolean(
          isCashRoute &&
          viewportWidth > viewportHeight &&
          viewportWidth <= 1100 &&
          viewportHeight <= 600
        )
      );
    });
  };

  syncCashPhoneLandscape();

  window.addEventListener('resize', syncCashPhoneLandscape, { passive: true });
  window.addEventListener('orientationchange', syncCashPhoneLandscape, {
    passive: true,
  });
  window.visualViewport?.addEventListener('resize', syncCashPhoneLandscape, {
    passive: true,
  });

  return () => {
    window.cancelAnimationFrame(frameId);
    window.removeEventListener('resize', syncCashPhoneLandscape);
    window.removeEventListener('orientationchange', syncCashPhoneLandscape);
    window.visualViewport?.removeEventListener(
      'resize',
      syncCashPhoneLandscape
    );
  };
}, []);


// какой ход сейчас раскрыт по кубу (панель справа/снизу)
const [cubeDetailsMoveNum, setCubeDetailsMoveNum] = useState(null);








useEffect(() => {
  // const DESIGN_WIDTH = 1400;
  const DESIGN_WIDTH = 1020;
  // const DESIGN_HEIGHT = 820;
  const DESIGN_HEIGHT = 760;
  // const SAFE_TOP = 120;
  const SAFE_TOP = 8;

  const handleResize = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

setIsPortrait(vh > vw);
setIsMobileWidth(vw <= 767);

const isNarrow = vw <= 767;
const isDesktop = vw > 1200;

// сколько места сверху/снизу съедает интерфейс
const RESERVED_UI = isNarrow ? 170 : isDesktop ? 12 : 28;

// доступное место под доску
const availableWidth = Math.max(vw - 16, 320);
const availableHeight = Math.max(vh - SAFE_TOP - RESERVED_UI, 320);

// считаем масштаб и по ширине, и по высоте
const scaleX = availableWidth / DESIGN_WIDTH;
const scaleY = availableHeight / DESIGN_HEIGHT;

// берём минимальный, чтобы всё влезло
let next = Math.min(scaleX, scaleY);

// на ПК даём чуть сильнее растянуть,
// на остальных оставляем почти как было
next = Math.min(next, isDesktop ? 1.21 : 1.18);

setBoardScale(next > 0 ? next : 1);
  };

  handleResize();
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);



useEffect(() => {
  window.addEventListener("touchmove", handleTouchMove, { passive: false });
  window.addEventListener("touchend", handleTouchEnd);
  window.addEventListener("touchcancel", handleTouchCancel);

  return () => {
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
    window.removeEventListener("touchcancel", handleTouchCancel);
  };
}, []);



useEffect(() => {
  debugLog('435354353',hasRolledDice);
  
}, [hasRolledDice]);




useLayoutEffect(() => {
  const root = boardRef.current;
  if (!root) return;

  let rafId = 0;
  let tries = 0;

  const updateBoardSize = () => {
    // root — это твой div.board.short-board
    const rect = root.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    if (w > 50 && h > 50) {
      // ВАЖНО: не обновляем стейт, если значения те же (чтобы не было лишних перерендеров)
      setBoardVb((prev) => {
        if (prev.w === w && prev.h === h) return prev;
        return { w, h };
      });
      return true;
    }
    return false;
  };

  // попробуем несколько раз, пока доска реально не стала с размерами
  const tick = () => {
    tries += 1;
    const ok = updateBoardSize();
    if (!ok && tries < 20) {
      rafId = requestAnimationFrame(tick);
    }
  };

  tick();

  const ro = new ResizeObserver(() => updateBoardSize());
  ro.observe(root);

  window.addEventListener('resize', updateBoardSize);

  return () => {
    cancelAnimationFrame(rafId);
    ro.disconnect();
    window.removeEventListener('resize', updateBoardSize);
  };
  // зависимости: если доска могла быть НЕ отрисована из-за rotate-hint/моб.логики — эффект запустится заново
}, [isMobile, isMobileWidth, isPortrait]);



useEffect(() => {
  const handleResizeViewportFlags = () => {
    const vw = window.innerWidth;

    setIsMobile(vw <= 767);
    setIsTablet(vw >= 768 && vw <= 1366);
  };

  handleResizeViewportFlags();
  window.addEventListener('resize', handleResizeViewportFlags);

  return () => {
    window.removeEventListener('resize', handleResizeViewportFlags);
  };
}, []);


// НАЧАЛО ТАЧ-ПЕРЕТАСКИВАНИЯ
const handleTouchStart = (e, pointIndex, checkerIndex, color, fromBar = false) => {
  cleanupTouchDrag();

    // ❌ Если сейчас не мой ход или пытаюсь тронуть чужую шашку — просто выходим
  if (!canDragChecker(color)) {
    return;
  }


  if (!e.touches || e.touches.length === 0) return;

  const t = e.touches[0];

  touchDrag.current.dragging = true;
  touchDrag.current.fromPoint = pointIndex;
  touchDrag.current.fromIndex = checkerIndex;
  touchDrag.current.color = color;

    touchDataRef.current = {};

  // создаём "летающий" элемент
  const ghost = document.createElement("div");
  ghost.className = `checker ${color} drag-ghost${shortBoardGhostClass}`;
  ghost.style.position = "fixed";
  ghost.style.left = t.clientX - 20 + "px";
  ghost.style.top = t.clientY - 20 + "px";
  ghost.style.width = "40px";
  ghost.style.height = "40px";
  ghost.style.zIndex = 9999;
  document.body.appendChild(ghost);

  touchDrag.current.ghost = ghost;


  const dragEvent = {
  type: 'dragstart',
  target: e.target,
  currentTarget: e.currentTarget,
  preventDefault() {},
  stopPropagation() {},
  dataTransfer: {
    setData(type, value) {
      const key = type || 'text';
      touchDataRef.current[key] = String(value ?? '');
    },
    getData(type) {
      const key = type || 'text';
      return touchDataRef.current[key] ?? '';
    },
    clearData(type) {
      if (!type) {
        touchDataRef.current = {};
      } else {
        delete touchDataRef.current[type];
      }
    },
    effectAllowed: 'move',
    dropEffect: 'move',
  },
};

  // используем ту же обёртку, что и для мыши
  handleDragStartSfx(dragEvent, pointIndex, checkerIndex, fromBar);
};


// ДВИЖЕНИЕ ТАЧЕМ
const handleTouchMove = (e) => {
  if (!touchDrag.current.ghost) return;
  if (!e.touches || e.touches.length === 0) return;

  touchMovedRef.current = true;

  const t = e.touches[0];
  touchDrag.current.ghost.style.left = t.clientX - 20 + "px";
  touchDrag.current.ghost.style.top = t.clientY - 20 + "px";

  e.preventDefault();
};



// сохраняем "живую" доску и временный снимок для анализа
const [displayBoard, setDisplayBoard] = useState(board);



// === СНИМОК СТАРТОВОЙ ПОЗИЦИИ (чтобы откат работал) ===
const initialBoardRef = useRef(null);
const initialTableIdRef = useRef(null);



// сохраняем стартовую позицию ДО первого хода (пока firstMoveComplete = false)
useEffect(() => {
  if (initialBoardRef.current) return;
  if (!Array.isArray(board) || board.length !== 24) return;

  // ✅ сохраняем старт, только пока НЕТ подтверждённых ходов
  const hasConfirmed = Array.isArray(confirmedTurns) && confirmedTurns.length > 0;
  if (hasConfirmed) return;

  // ✅ и пока кубики ещё не брошены (чтобы не схватить “доску после перемещений”)
  if (hasRolledDice) return;

  initialBoardRef.current = JSON.parse(JSON.stringify(board));
  debugLog('[REPLAY] saved initial board snapshot');
}, [board, confirmedTurns, hasRolledDice]);




// ==== GNU -> quality map по Move number ====
const [gnuByMove, setGnuByMove] = useState({});
const [selectedMoveNum, setSelectedMoveNum] = useState(null);


const COLLAPSED_TURNS_ROWS = 4;
const [isTurnsExpanded, setIsTurnsExpanded] = useState(false);
const handleToggleTurnsExpand = React.useCallback(() => {
  rememberTurnsListScroll();
  setIsTurnsExpanded((prev) => !prev);
}, [rememberTurnsListScroll]);

const [isMatchGamesOpen, setIsMatchGamesOpen] = useState(false);
const matchGamesDropdownRef = useRef(null);

// Если пользователь перелистнул партию в матче —
// важно сбросить выбранный ход, иначе он может оказаться «вне диапазона»
// и реплей/список начнёт вести себя странно.
useEffect(() => {
  if (!matchGameKey) return;

  // ✅ при переключении партии в матче — всегда показываем СТАРТ (ход 0)
  setSelectedMoveNum(0);

  // ✅ сброс “хвостов”, которые визуально тоже могут тянуться между партиями
  setCubeDetailsMoveNum(null);
  setGnuArrows([]);
setPlayedArrows([]);
setPlayedArrowTone(null);

  setDisplayCaptured({ white: 0, black: 0 });
  setDisplayOffCount({ white: 0, black: 0 });

  // мапа GNU-оценок будет пересчитана ниже из текста выбранной партии
  setGnuByMove({});

    try {
    const snap = getStartSnapshot();
    initialBoardRef.current = snap;
    setDisplayBoard(snap);
  } catch {
    initialBoardRef.current = null;
  }
  // ✅ обновим стартовый снимок
  // try {
  //   if (Array.isArray(boardState?.initial) && boardState.initial.length === 24) {
  //     const snap = JSON.parse(JSON.stringify(boardState.initial));
  //     initialBoardRef.current = snap;

  //     // чтобы сразу увидеть старт (без “мигания”)
  //     setDisplayBoard(snap);
  //   } else {
  //     initialBoardRef.current = null;
  //   }
  // } catch {
  //   initialBoardRef.current = null;
  // }
}, [matchGameKey, boardState?.initial]);



useEffect(() => {
  setIsTurnsExpanded(false);
}, [matchGameKey, selectedMatchGameIndex]);




useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      matchGamesDropdownRef.current &&
      !matchGamesDropdownRef.current.contains(event.target)
    ) {
      setIsMatchGamesOpen(false);
    }
  };

  const handleEscape = (event) => {
    if (event.key === 'Escape') {
      setIsMatchGamesOpen(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  document.addEventListener('keydown', handleEscape);

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleEscape);
  };
}, []);


useLayoutEffect(() => {
  const applyScroll = () => {
    const el = turnsListRef.current;
    if (!el) return;

    el.scrollTop = turnsListScrollTopRef.current;
  };

  applyScroll();

  const raf1 = requestAnimationFrame(applyScroll);
  const raf2 = requestAnimationFrame(() => {
    requestAnimationFrame(applyScroll);
  });

  return () => {
    cancelAnimationFrame(raf1);
    cancelAnimationFrame(raf2);
  };
}, [selectedMoveNum, cubeDetailsMoveNum, matchGameKey]);




// 1) Хук для media query
function useMediaQuery(query) {
  const getMatches = () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  };

  


  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const media = window.matchMedia(query);

    const listener = () => setMatches(media.matches);

    // сразу проставим актуальное значение
    setMatches(media.matches);

    // подписка (и старые браузеры тоже)
    if (media.addEventListener) {
      media.addEventListener("change", listener);
      return () => media.removeEventListener("change", listener);
    } else {
      media.addListener(listener);
      return () => media.removeListener(listener);
    }
  }, [query]);

  return matches;
}

const isCompactRightHud = useMediaQuery('(max-width: 1400px)');
const hideLeftAnalysisOnLaptop = useMediaQuery('(max-width: 1400px)');

// ✅ режим просмотра истории/анализа
// null  -> обычная "живая" доска
// 0     -> стартовая позиция (до 1-го подтверждённого хода)
// 1..N  -> позиция после N-го подтверждённого хода
const isReplayMode = selectedMoveNum !== null;

const shortLiveGame =
  !gameFinished &&
  !showResultModal &&
  !isReplayMode;

const cashPhoneLandscapeMode = Boolean(cashPhoneLandscape);

/*
 * Как в большой турнирной доске: в телефонном landscape игровая сцена
 * занимает viewport, а страница не прокручивается.
 * Классы уникальные для cash, поэтому общие стили других досок не меняются.
 */
useEffect(() => {
  if (typeof document === 'undefined') return undefined;
  if (!cashPhoneLandscapeMode) return undefined;

  const html = document.documentElement;
  const body = document.body;

  const previousHtmlOverflow = html.style.overflow;
  const previousBodyOverflow = body.style.overflow;
  const previousBodyMargin = body.style.margin;

  html.classList.add('short-cash-landscape-open');
  body.classList.add('short-cash-landscape-open');

  html.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
  body.style.margin = '0';

  return () => {
    html.style.overflow = previousHtmlOverflow;
    body.style.overflow = previousBodyOverflow;
    body.style.margin = previousBodyMargin;

    html.classList.remove('short-cash-landscape-open');
    body.classList.remove('short-cash-landscape-open');
  };
}, [cashPhoneLandscapeMode]);

// 🔒 Во время партии запрещаем клики по тексту ходов (иначе можно откатывать позицию и получать подсказки)
const canNavigateTurns = isReplayMode || gameFinished;

// Если партия ещё идёт — принудительно выходим из режима просмотра истории
useEffect(() => {
  if (!gameFinished && selectedMoveNum !== null) {
    setSelectedMoveNum(null);
  }
}, [gameFinished]);




useEffect(() => {
  // Если открыт куб на ходе X, а выбран ход Y — стрелки НЕ должны оставаться от Y,
  // иначе создаётся ощущение, что стрелки относятся к кубу.
  if (cubeDetailsMoveNum != null && cubeDetailsMoveNum !== selectedMoveNum) {
    setGnuArrows([]);
    setPlayedArrows([]);
    setPlayedArrowTone(null);
  }
}, [cubeDetailsMoveNum, selectedMoveNum]);



const BAR = 24;
const OFF = 25;

const [displayCaptured, setDisplayCaptured] = useState({ white: 0, black: 0 });
const [displayOffCount, setDisplayOffCount] = useState({ white: 0, black: 0 });

// const totalMoves = Array.isArray(confirmedTurns) ? confirmedTurns.length : 0;
// ===== АНАЛИЗ: "ход" = бросок костей (или реальный from/to).
// Куб/принятие куба НЕ должен считаться ходом, иначе нумерация съезжает.
const getTurnDiceArr = React.useCallback((t) => {
  const d =
    t?.dice ??
    t?.turnDice ??
    t?.turn_dice ??
    t?.rolledDice ??
    t?.roll;

  return Array.isArray(d) ? d : [];
}, []);

const isCountedTurnForAnalysis = React.useCallback(
  (t) => {
    // 1) если есть кости — это ход (даже если moves пустой: бывает "no legal moves")
    const dice = getTurnDiceArr(t);
    if (dice.length > 0) return true;

    // 2) если есть реальные from/to — это тоже считаем ходом
    const mv = Array.isArray(t?.moves) ? t.moves : [];
    for (const m of mv) {
      if (m && m.from != null && m.to != null) return true;
    }

    // иначе: это НЕ ход (обычно строка про куб/принятие/служебное)
    return false;
  },
  [getTurnDiceArr]
);

const { analysisTurns, uiRows, totalMoves } = React.useMemo(() => {
  const src = Array.isArray(confirmedTurns) ? confirmedTurns : [];

  let moveNum = 0;
  const rows = [];
  const turns = [];

  for (let rawIndex = 0; rawIndex < src.length; rawIndex++) {
    const t = src[rawIndex];
    const counted = isCountedTurnForAnalysis(t);

    if (counted) {
      moveNum += 1;

      turns.push(t);

      rows.push({
        kind: 'turn',
        moveNum,                  // НОМЕР ХОДА ДЛЯ GNU/СТРЕЛОК (без куба)
        rawIndex,
        rawMoveNum: rawIndex + 1, // как было раньше (на всякий случай)
        t,
      });
    } else {
      // meta-строка (обычно куб): показываем, но не считаем ходом.
      // Привязываем к "следующему" ходу по умолчанию (часто куб идёт перед броском).
      rows.push({
        kind: 'meta',
        attachMoveNum: moveNum + 1,
        rawIndex,
        rawMoveNum: rawIndex + 1,
        t,
      });
    }
  }

  const maxMoveNum = moveNum;

  // если meta в самом конце — привяжем к последнему ходу, чтобы не улетать за диапазон
  for (const r of rows) {
    if (r.kind === 'meta') {
      if (!maxMoveNum) r.attachMoveNum = 0;
      else if (r.attachMoveNum > maxMoveNum) r.attachMoveNum = maxMoveNum;
    }
  }

  return {
    analysisTurns: turns, // только реальные ходы
    uiRows: rows,         // строки списка (включая meta/куб)
    totalMoves: moveNum,  // количество реальных ходов
  };
}, [confirmedTurns, isCountedTurnForAnalysis]);


const selectPrevMove = () => {
  if (!totalMoves) return;

  rememberTurnsListScroll();
  setCubeDetailsMoveNum(null);

  setSelectedMoveNum((n) => {
    const cur = n ?? (matchGameKey ? 0 : totalMoves);
    return Math.max(0, cur - 1);
  });
};

const selectNextMove = () => {
  if (!totalMoves) return;

  rememberTurnsListScroll();
  setCubeDetailsMoveNum(null);

  setSelectedMoveNum((n) => {
    // ✅ если ещё ничего не выбрано — начинаем с 0 -> 1
    const cur = n ?? 0;
    return Math.min(totalMoves, cur + 1);
  });
};


const [hideResultModalLocal, setHideResultModalLocal] = useState(false);
const [gnuStats, setGnuStats] = useState(null); // сюда положим pr с сервера



const closeResultModalLocal = (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();

  cleanupTouchDrag();
  touchDataRef.current = {};

  // Закрываем кубовую панель, если она была открыта.
  setCubeDetailsMoveNum(null);

  // Только скрываем результат.
  // handleCloseResultModal здесь НЕ вызываем,
  // поэтому в лобби пользователь не уходит.
  setHideResultModalLocal(true);
};


const matchInitialBoard = React.useMemo(() => buildStandardInitialBoard(), []);





// маленький deepClone (чтобы не делить ссылки)
const deepClone = (x) => JSON.parse(JSON.stringify(x));

// ✅ единый способ получить "старт партии"
const getStartSnapshot = () => {
  const isBoard24 = (b) => Array.isArray(b) && b.length === 24;

  // Сигнатура позиции: по каждой точке считаем {w,b}.
  // Это позволяет понять "это реально стартовая позиция?" даже если объекты шашек имеют разные поля.
  const sig = (b) => {
    const res = [];
    for (let i = 0; i < 24; i++) {
      const st = Array.isArray(b?.[i]) ? b[i] : [];
      let w = 0, bl = 0;
      for (const ch of st) {
        const c = String(ch?.color || '').toLowerCase();
        if (c === 'white') w++;
        else if (c === 'black') bl++;
      }
      res.push(`${w}:${bl}`);
    }
    return res.join('|');
  };

  const std = (isBoard24(matchInitialBoard) ? sig(matchInitialBoard) : null);

  const isStdStart = (b) => {
    if (!std) return false;
    if (!isBoard24(b)) return false;
    return sig(b) === std;
  };

  // 1) если сервер прислал "initial" и он выглядит как настоящий старт — берём его
  try {
    if (isStdStart(boardState?.initial)) {
      return deepClone(boardState.initial);
    }
  } catch {}

  // 2) если мы сами когда-то сохранили старт (и он именно старт) — берём его
  try {
    if (isStdStart(initialBoardRef.current)) {
      return deepClone(initialBoardRef.current);
    }
  } catch {}

  // 3) железный фолбэк: стандартная стартовая позиция (всегда ДО 1-го хода)
  try {
    if (isBoard24(matchInitialBoard)) {
      return deepClone(matchInitialBoard);
    }
  } catch {}

  return Array.from({ length: 24 }, () => []);
};



const pickGnuText = (gnu) => {
  return (
    gnu?.text ||
    gnu?.analysis ||   // если вдруг у тебя поле так называется
    ''
  );
};

const parseGnuByMove = (text) => {
  // 1) Сначала собираем как GNU нумерует "Move number N"
  // 2) Отмечаем какие из них реально имеют бросок "Rolled XY"
  // 3) Пересобираем итоговую мапу уже в нумерации "dice-turn": 1..K

  const byGnuMoveNum = {};
  if (!text) return byGnuMoveNum;

  // если GNU печатает ANSI-цвета — убираем, чтобы regex не ломались
  const stripAnsi = (s) => String(s || "").replace(/\x1b\[[0-9;]*m/g, "");

  // вынимаем из строки "1. 13/8 6/5 Eq.: ..." только часть с ходами
  const extractMovesPartFromBestLine = (s) => {
    if (!s) return null;

    // отрежем всё после Eq
    const m = String(s).match(/^(.+?)(?:\s+Eq\.|\s+Eq:|\s+Eq\.\s*:|\s+Eq\.:|$)/i);
    const head = (m ? m[1] : s).trim();

    // берём только токены ходов, где есть '/'
    const tokens = head.split(/\s+/);
    const moveTokens = [];
    for (const tok of tokens) {
      if (tok.includes("/")) moveTokens.push(tok);
      else if (moveTokens.length > 0) break;
    }

    const out = moveTokens.join(" ");
    if (!out || !out.includes("/")) return null;

    return out.replace(/\s*\/\s*/g, "/").replace(/\s+/g, " ").trim();
  };

  const lines = String(text).split(/\r?\n/);

  let curGnuMoveNum = null;

  for (const raw of lines) {
    const line = stripAnsi(raw);

    // ✅ СДЕЛАЛИ regex более терпимым:
    // поддерживает "Move number 7:" и "Move 7:"
    // + поддерживает side: a/b/w/x
    const m = line.match(
      /Move(?:\s+number)?\s+(\d+)\s*:\s*(?:([abwx])\s+to\s+play)?/i
    );

    if (m) {
      curGnuMoveNum = Number(m[1]);
      byGnuMoveNum[curGnuMoveNum] = byGnuMoveNum[curGnuMoveNum] || {
        quality: "ok",
        bestMovesText: null,
        toPlay: null,
        isDiceMove: false,     // 👈 важно
        rolled: null,          // [d1,d2] если найдём Rolled
      };

      const side = (m[2] || "").toLowerCase();
      if (side === "a" || side === "w") byGnuMoveNum[curGnuMoveNum].toPlay = "white";
      if (side === "b" || side === "x") byGnuMoveNum[curGnuMoveNum].toPlay = "black";

      continue;
    }

    if (!curGnuMoveNum) continue;

    // ✅ ловим бросок — это признак "реального" хода с костями
    // обычно GNU пишет "Rolled 64"
    const r = line.match(/\bRolled\s+(\d)(\d)\b/i);
    if (r) {
      const d1 = Number(r[1]);
      const d2 = Number(r[2]);
      byGnuMoveNum[curGnuMoveNum].isDiceMove = true;
      byGnuMoveNum[curGnuMoveNum].rolled = [d1, d2];
      continue;
    }

    const a = line.match(/Alert:\s*(.*)/i);
    if (a) {
      const t = a[1].toLowerCase();

      let q = "ok";
      if (t.includes("very bad") || t.includes("blunder")) q = "blunder";
      else if (t.includes("bad move") || t.includes("error")) q = "bad";
      else if (t.includes("doubtful") || t.includes("dubious")) q = "doubtful";
      else if (t.includes("inaccur")) q = "inaccuracy";

      byGnuMoveNum[curGnuMoveNum].quality = q;
      continue;
    }

    // лучшая линия: "1. 13/8 6/5 Eq.: ..."
    const best = line.match(/^\s*\*?\s*1\.\s*(.*)$/);
    if (best) {
      const s = best[1].trim();
      const movesPart = extractMovesPartFromBestLine(s);
      if (movesPart) {
        byGnuMoveNum[curGnuMoveNum].bestMovesText = movesPart;
      }
      continue;
    }
  }

  // --- ВАЖНОЕ: ПЕРЕИНДЕКСАЦИЯ ---
  // берём ТОЛЬКО те GNU-move, где был "Rolled"
  const diceGnuMoveNums = Object.keys(byGnuMoveNum)
    .map(Number)
    .filter((k) => byGnuMoveNum[k]?.isDiceMove)
    .sort((a, b) => a - b);

  // если почему-то "Rolled" вообще не нашли — возвращаем как было
  if (diceGnuMoveNums.length === 0) {
    return byGnuMoveNum;
  }

  // теперь делаем мапу "1..K" (как confirmedTurns)
  const byTurnIndex = {};
  diceGnuMoveNums.forEach((gnuMoveNum, idx) => {
    const turnNum = idx + 1;
    byTurnIndex[turnNum] = {
      ...byGnuMoveNum[gnuMoveNum],
      gnuMoveNum, // для дебага: какое реальное Move number было в GNU
    };
  });

  return byTurnIndex;
};


// ===== CUBE MOVES -> map по moveNum =====
const cubeMovesByMoveNum = React.useMemo(() => {
  const arr = Array.isArray(gnuAnalysis?.cubeMoves) ? gnuAnalysis.cubeMoves : [];
  const map = {};

  arr.forEach((m) => {
    const keyRaw =
      m?.moveNum ??
      m?.move_number ??
      m?.moveNumber ??
      m?.moveNo ??
      m?.ply ??
      m?.turn ??
      m?.move;

    const n = Number(keyRaw);
    if (!Number.isFinite(n)) return; // ✅ если номера нет — НЕ маппим

    map[n] = m;
  });

  return map;
}, [gnuAnalysis?.cubeMoves]);



useEffect(() => {
  console.log('[CUBE TRACE]', {
    rawCubeMoves: gnuAnalysis?.cubeMoves,
    cubeMoveKeys: Object.keys(cubeMovesByMoveNum),

    rawCubeEvents: cubeEvents,

    rows: uiRows.map((row) => ({
      kind: row.kind,
      moveNum: row.moveNum ?? null,
      attachMoveNum: row.attachMoveNum ?? null,
      rawIndex: row.rawIndex,
      player: row.t?.player,
      isCubeTurn: row.t?.isCubeTurn,
      cubeAction: row.t?.cubeAction,
      dice: row.t?.dice,
    })),
  });

  const arr = Array.isArray(gnuAnalysis?.cubeMoves) ? gnuAnalysis.cubeMoves : [];
  debugLog('[CUBE MOVES] len=', arr.length);
  debugLog('[CUBE MOVES] first=', arr[0]);
  debugLog('[CUBE MOVES] keys=', Object.keys(cubeMovesByMoveNum).slice(0, 20));
}, [
  gnuAnalysis?.cubeMoves,
  cubeMovesByMoveNum,
  cubeEvents,
  uiRows,
]);



// ===== CUBE EVENTS: ФИЛЬТРУЕМ ПО ТЕКУЩЕЙ ПАРТИИ МАТЧА (чтобы события куба не "перетекали" между партиями) =====
const cubeEventsForUi = React.useMemo(() => {
  const arr = Array.isArray(cubeEvents) ? cubeEvents : [];

  if (!matchGameKey) return arr;

  // Если сервер НЕ присылает никаких ключей партии — фильтровать не сможем, оставим как есть.
  const hasAnyGameKey = arr.some((ev) => (
    ev?.matchGameKey != null ||
    ev?.gameKey != null ||
    ev?.gameId != null ||
    ev?.game_id != null ||
    ev?.gameIndex != null ||
    ev?.gameNo != null ||
    ev?.gameNumber != null ||
    ev?.gameNum != null
  ));

  if (!hasAnyGameKey) return arr;

  const keyStr = String(matchGameKey);
  const idxNum = Number(selectedMatchGameIndex);

  return arr.filter((ev) => {
    // 1) самый надёжный вариант: точный ключ партии
    const k =
      ev?.matchGameKey ??
      ev?.gameKey ??
      ev?.gameId ??
      ev?.game_id;

    if (k != null) return String(k) === keyStr;

    // 2) запасной вариант: индекс/номер партии
    const gi =
      ev?.gameIndex ??
      ev?.gameNo ??
      ev?.gameNumber ??
      ev?.gameNum;

    if (gi != null && Number.isFinite(idxNum)) return Number(gi) === idxNum;

    // если ключа нет — лучше скрыть, чем показать "не на том ходе"
    return false;
  });
}, [cubeEvents, matchGameKey]);


// ===== CUBE EVENTS (ФАКТ ИЗ ПАРТИИ) -> map по moveNo =====
const cubeEventsByMoveNum = React.useMemo(() => {
  const arr = Array.isArray(cubeEvents) ? cubeEvents : [];
  const map = {};
  for (const ev of arr) {
    const n = Number(
  ev?.moveNum ??
  ev?.move_number ??
  ev?.moveNumber ??
  ev?.moveNo ??
  ev?.ply ??
  ev?.turn ??
  ev?.move
);

    if (!Number.isFinite(n)) continue;
    if (!map[n]) map[n] = [];
    map[n].push(ev);
  }
  return map; // { [moveNum]: [events...] }
}, [cubeEvents]);

// нормализуем ответ на куб
const normResponder = (v) => {
  const s = String(v || '').trim().toLowerCase();
  if (!s) return null;
  if (s.includes('take')) return 'take';
  if (s.includes('pass')) return 'pass';
  if (s.includes('beaver')) return 'beaver';
  return s;
};

// ВАЖНО: вытащить "что реально сделали" из cubeEvents
// возвращаем: offered: true/false/null, response: 'take'|'pass'|'beaver'|null, prLoss: number|null
const getPlayedCubeFacts = (moveNum) => {
  const list = cubeEventsByMoveNum?.[moveNum] || [];

  let offered = null;     // true = давали куб, false = не давали (если фиксируется), null = нет данных
  let response = null;    // take/pass/beaver
  let prLoss = 0;         // если родитель уже посчитал штраф — суммируем
  let hasPrLoss = false;

  for (const ev of list) {
    // 1) готовые поля, если ты их сделал в родителе
    if (typeof ev?.offered === 'boolean') offered = ev.offered;
    if (typeof ev?.playedDouble === 'boolean') offered = ev.playedDouble;

    if (ev?.response != null) response = normResponder(ev.response);
    if (ev?.decision != null) response = normResponder(ev.decision);
    if (ev?.takePass != null) response = normResponder(ev.takePass);

    // 2) попытка угадать по текстовым полям (если структура другая)
    const type = String(ev?.type ?? ev?.event ?? ev?.kind ?? '').toLowerCase();
    const action = String(ev?.action ?? ev?.cubeAction ?? ev?.what ?? '').toLowerCase();
    const answer = String(ev?.answer ?? ev?.responder ?? '').toLowerCase();

    // offer
    if (offered == null) {
      if (type.includes('offer') || type.includes('double') || action.includes('double')) {
        // если там прямо "no double" — считаем как "не давали"
        if (action.includes('no double')) offered = false;
        else offered = true;
      }
    }

    // response
    if (!response) {
      const r = normResponder(answer);
      if (r) response = r;
    }

    // 3) PR loss / penalty (если родитель положил)
    const loss =
      Number(ev?.prLoss ?? ev?.loss ?? ev?.cubeLoss ?? ev?.errorLoss ?? ev?.penalty);

    if (Number.isFinite(loss)) {
      prLoss += loss;
      hasPrLoss = true;
    }
  }

  return {
    offered,
    response,
    prLoss: hasPrLoss ? prLoss : null,
    raw: list,
  };
};

// нормализуем "Double" / "No double"
const normDoubler = (v) => {
  const s = String(v || '').trim().toLowerCase();
  if (!s) return null;
  if (s.includes('no double')) return 'no double';
  if (s === 'double' || s.includes('double')) return 'double';
  return s;
};




// ✅ Достаём cubeMove для хода максимально надёжно (важно для матча)
const getCubeMoveForMoveNum = (moveNum) => {
  const n = Number(moveNum);

  if (!Number.isFinite(n)) return null;

  // Куб перед ходом N сервер хранит как N - 0.5.
  const cubeBeforeTurn =
    cubeMovesByMoveNum?.[n - 0.5];

  if (cubeBeforeTurn) {
    return cubeBeforeTurn;
  }

  // Обычная запись ровно на ходе.
  const direct =
    cubeMovesByMoveNum?.[n];

  if (direct) {
    return direct;
  }

  // Запасной вариант через реальный номер GNU.
  const gnuKey =
    gnuByMove?.[n]?.gnuMoveNum;

  if (
    gnuKey != null &&
    cubeMovesByMoveNum?.[gnuKey]
  ) {
    return cubeMovesByMoveNum[gnuKey];
  }

  return null;
};




// перевод responder на русский
const responderRu = (v) => {
  const s = String(v || '').trim().toLowerCase();
  if (!s) return '—';
  if (s === 'take') return translate('game.analysis.takeResponse');
  if (s === 'pass') return translate('game.analysis.passResponse');
  if (s === 'beaver') return translate('game.analysis.beaverResponse');
  return s;
};

// считаем Win% из probs (если probs есть)
const getWinPctFromProbs = (cubeMove) => {
  const p = cubeMove?.probs;
  if (!p || typeof p !== 'object') return null;

  // gnubg часто даёт win/winG/winBG (может быть 0..1)
  const win   = Number(p.win   ?? p.Win   ?? 0);
  const winG  = Number(p.winG  ?? p.WinG  ?? 0);
  const winBG = Number(p.winBG ?? p.WinBG ?? 0);

  let total = win + winG + winBG;
  if (!Number.isFinite(total)) return null;

  // если это доли (0..1), переводим в %
  if (total <= 1.0001) total = total * 100;

  // округлим до 0.1%
  return Math.round(total * 10) / 10;
};

// ===== ФАКТ "игрок дал куб" (подстрой под свои данные) =====
// 1) если сервер положит это в cubeMoves (лучший вариант) — например cubeMove.playedDoubler
// 2) или если у тебя это лежит в confirmedTurns — например turn.cubeAction
const getPlayedCubeAction = (turn, cubeMove) => {
  // ВАРИАНТ A: сервер прислал факт в cubeMove
  // ожидаем: "Double" | "No double" | null
  if (cubeMove?.playedDoubler != null) return cubeMove.playedDoubler;

  // ВАРИАНТ B: факт лежит в turn
  // подставь здесь своё поле, если оно у тебя есть:
  if (turn?.cubeAction != null) return turn.cubeAction;

  // ВАРИАНТ C: если у тебя есть массив событий
  // пример:
  // if (Array.isArray(turn?.events)) {
  //   const ev = turn.events.find(x => x?.type === 'double');
  //   if (ev) return 'Double';
  // }

  return null; // пока факта нет
};





// ===== GNU best move text -> UI coords (для чёрных зеркалим 1..24) =====
const convertGnuBestMovesTextForUi = (bestMovesText, moverColor = 'white') => {
  if (!bestMovesText) return '';

  const isBlack = String(moverColor).toLowerCase() === 'black';
  const tokens = String(bestMovesText).trim().split(/\s+/);
  const out = [];

  for (let tok of tokens) {
    if (!tok) continue;

    // 1) отделяем хвостовую пунктуацию типа "," или ";"
    let punct = '';
    const punctMatch = tok.match(/[,;]+$/);
    if (punctMatch) {
      punct = punctMatch[0];
      tok = tok.slice(0, -punct.length);
    }

    // 2) отделяем повтор в конце "(2)" и т.п.
    let repeat = '';
    const repMatch = tok.match(/^(.*?)(\(\d+\))$/);
    if (repMatch) {
      tok = repMatch[1];
      repeat = repMatch[2]; // например "(2)"
    }

    // 3) разбираем цепочку 13/8/5, bar/22*, 6/3*(2) и т.д.
    const parts = tok.split('/').map((partRaw) => {
      let part = partRaw;

      // звёздочки удара могут быть в конце сегмента ("22*", "3**" и т.п.)
      let stars = '';
      const starMatch = part.match(/(\*+)$/);
      if (starMatch) {
        stars = starMatch[1];
        part = part.slice(0, -stars.length);
      }

      const low = part.toLowerCase();

      // bar/off — не зеркалим
      if (low === 'bar' || low === 'off') {
        return low + stars;
      }

      // число 1..24 — зеркалим ТОЛЬКО если чёрные
      // const n = Number(part);
      // if (Number.isInteger(n) && n >= 1 && n <= 24) {
      //   // const mapped = isBlack ? (25 - n) : n; // 22->3, 6->19 и т.д.
      //   // return String(mapped) + stars;
      //   return String(n) + stars;

      // }
const n = Number(part);
if (Number.isInteger(n) && n >= 1 && n <= 24) {
  const mapped = isBlack ? (25 - n) : n;
  return String(mapped) + stars;
}

      // неизвестный формат — возвращаем как есть
      return part + stars;
    });

    out.push(parts.join('/') + repeat + punct);
  }

  return out.join(' ');
};



const parseBestMovesPairs = (bestMovesText, moverColor = 'white') => {
  if (!bestMovesText) return [];

  const tokens = String(bestMovesText).trim().split(/\s+/);
  const pairs = [];

  const toIndex = (x) => {
    const v = String(x).toLowerCase();

    if (v === 'bar') return 24;
    if (v === 'off') return 25;

    const n = Number(v);
    if (!Number.isFinite(n) || n < 1 || n > 24) return null;

    // ✅ GNU даёт номера с точки зрения игрока на ходе
    // белые: 1..24 -> 0..23
    // чёрные: зеркалим 1<->24, 2<->23 ...
    const mover = String(moverColor || 'white').toLowerCase();
    if (mover === 'black') {
      return 24 - n; // n=24 -> 0, n=1 -> 23
    }
    return n - 1;
  };

  for (let raw of tokens) {
    if (!raw) continue;

    // ✅ убираем звёздочки ударов и хвостовую пунктуацию типа "," ";".
    let token = String(raw)
      .replace(/\*/g, '')
      .replace(/[,;]+$/g, '');

    // ✅ повтор вида 6/4(2)
    let repeat = 1;
    const rep = token.match(/^(.*)\((\d+)\)$/);
    if (rep) {
      token = rep[1];
      repeat = parseInt(rep[2], 10) || 1;
    }

    // ✅ поддержка цепочек: 13/8/5 => 13->8 и 8->5
    const parts = token.split('/');
    if (parts.length < 2) continue;

    const points = parts.map(toIndex).filter((v) => v !== null);
    if (points.length < 2) continue;

    for (let r = 0; r < repeat; r++) {
      for (let i = 0; i < points.length - 1; i++) {
        pairs.push({ from: points[i], to: points[i + 1] });
      }
    }
  }

  return pairs;
};


// ===== Fallback: сравнить "мой ход" vs "лучший ход GNU" =====
const normalizeEndpoint = (v, pos) => {
  // pos: 'from' | 'to'
  if (v == null) return null;

  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'bar') return 24;
    if (s === 'off') return 25;
    const n = Number(s);
    if (Number.isFinite(n)) return n;
    return null;
  }

  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return null;

    // обычные точки 0..23
    if (v >= 0 && v <= 23) return v;

    // любые спец-коды >=24:
    // из FROM считаем BAR, из TO считаем OFF
    if (v >= 24) return pos === 'from' ? 24 : 25;
  }

  return null;
};

const turnMovesToPairs = (moves) => {
  const arr = Array.isArray(moves) ? moves : [];
  return arr
    .map((m) => {
      const from = normalizeEndpoint(m?.from, 'from');
      const to   = normalizeEndpoint(m?.to, 'to');
      if (from == null || to == null) return null;
      return { from, to };
    })
    .filter(Boolean);
};



// Схлопывает цепочки ходов: A->B и B->C превращает в A->C,
// чтобы "24/21 21/16" считалось равным "24/16".
const compressPairsChains = (pairs) => {
  let out = (Array.isArray(pairs) ? pairs : []).map(p => ({
    from: p?.from,
    to: p?.to
  })).filter(p => p.from != null && p.to != null);

  let changed = true;

  while (changed) {
    changed = false;

    // считаем степени вершин
    const indeg = new Map();
    const outdeg = new Map();

    for (const p of out) {
      indeg.set(p.to, (indeg.get(p.to) || 0) + 1);
      outdeg.set(p.from, (outdeg.get(p.from) || 0) + 1);
    }

    // ищем узел B, где indeg(B)=1 и outdeg(B)=1, и есть A->B и B->C
    for (let i = 0; i < out.length; i++) {
      const p1 = out[i];
      const mid = p1.to;

      if ((indeg.get(mid) || 0) !== 1) continue;
      if ((outdeg.get(mid) || 0) !== 1) continue;

      const j = out.findIndex((p, idx) => idx !== i && p.from === mid);
      if (j < 0) continue;

      const p2 = out[j];

      // Склеиваем A->B и B->C в A->C
      const merged = { from: p1.from, to: p2.to };

      // удаляем два ребра и добавляем одно
      const a = Math.max(i, j);
      const b = Math.min(i, j);
      out.splice(a, 1);
      out.splice(b, 1);
      out.push(merged);

      changed = true;
      break;
    }
  }

  return out;
};


const samePairMultiset = (aPairs, bPairs) => {
  // ✅ сначала схлопываем цепочки (важно для записей типа 24/16 vs 24/21 21/16)
  const a = compressPairsChains(aPairs);
  const b = compressPairsChains(bPairs);

  if (a.length !== b.length) return false;

  const cnt = (pairs) => {
    const m = new Map();
    for (const p of pairs) {
      const k = `${p.from}-${p.to}`;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  };

  const A = cnt(a);
  const B = cnt(b);
  if (A.size !== B.size) return false;

  for (const [k, v] of A.entries()) {
    if ((B.get(k) || 0) !== v) return false;
  }
  return true;
};



// Разбить фактические пары на "совпавшие с лучшим" и "отличающиеся" (мультисет, с учётом повторов)
// Нужно, чтобы НЕ подсвечивать как ошибку те части хода, которые игрок сделал так же, как GNU,
// даже если вторая часть хода отличается.
const splitActualPairsByBest = (actualPairs, bestPairs) => {
  const a = compressPairsChains(actualPairs);
  const b = compressPairsChains(bestPairs);

  // счётчик пар лучшего хода
  const bestCnt = new Map();
  for (const p of b) {
    const k = `${p.from}-${p.to}`;
    bestCnt.set(k, (bestCnt.get(k) || 0) + 1);
  }

  const matched = [];
  const unmatchedActual = [];

  for (const p of a) {
    const k = `${p.from}-${p.to}`;
    const c = bestCnt.get(k) || 0;
    if (c > 0) {
      matched.push(p);
      bestCnt.set(k, c - 1); // "потребили" одну такую пару
    } else {
      unmatchedActual.push(p);
    }
  }

  const unmatchedBest = [];
  for (const [k, c] of bestCnt.entries()) {
    if (c <= 0) continue;
    const [fromStr, toStr] = k.split('-');
    const from = Number(fromStr);
    const to = Number(toStr);
    for (let i = 0; i < c; i++) unmatchedBest.push({ from, to });
  }

  return { matched, unmatchedActual, unmatchedBest };
};






const getPointElementByIndex = (ptIndex) => {
  const root = boardRef.current;
  if (!root) return null;

  // bar/off
  if (ptIndex === 24) return root.querySelector('.bar');
  if (ptIndex === 25) return root.querySelector('.off-board');

  // ptIndex: 0..23
  // ориентация доски — по классу flipped на самой доске
const flipped = root.classList.contains('flipped');
const isWhiteView = !flipped; // если flipped нет — это "белый" вид


  if (isWhiteView) {
    // top row (point-container pX) это 12..23
    if (ptIndex >= 12) {
      const p = 23 - ptIndex; // 23->0, 12->11
      return root.querySelector(`.point-container.p${p}`);
    }
    // bottom row (point-containerd rX) это 0..11
    return root.querySelector(`.point-containerd.r${ptIndex}`);
  } else {
    // black view
    // top row: 0..11
    if (ptIndex <= 11) {
      return root.querySelector(`.point-container.p${ptIndex}`);
    }
    // bottom row: 12..23
    const r = 23 - ptIndex;
    return root.querySelector(`.point-containerd.r${r}`);
  }
};



// Перевод экранной координаты в реальную систему координат SVG.
// getScreenCTM учитывает CSS scale, translate и изменение раскладки после F5.
const screenPointToArrowSvg = (screenX, screenY) => {
  const svg = arrowSvgRef.current;
  if (!svg) return null;

  const ctm = svg.getScreenCTM();
  if (!ctm) return null;

  const point = svg.createSVGPoint();
  point.x = screenX;
  point.y = screenY;

  const localPoint = point.matrixTransform(ctm.inverse());

  return {
    x: localPoint.x,
    y: localPoint.y,
  };
};

const getArrowAnchorForPoint = (pointIndex, moverColor = 'white') => {
  const root = boardRef.current;
  if (!root) return null;

  const color = String(moverColor || 'white').toLowerCase();
  const myColor = String(myTournamentColor || '').toLowerCase();

  // Обычно сторона пользователя находится снизу.
  // Для зрителя используем ориентацию доски.
  const moverIsBottom =
    myColor === 'white' || myColor === 'black'
      ? color === myColor
      : root.classList.contains('flipped')
        ? color === 'black'
        : color === 'white';

  const centerOfElement = (el) => {
    if (!el) return null;

    const rect = el.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    return screenPointToArrowSvg(
      (rect.left + rect.right) / 2,
      (rect.top + rect.bottom) / 2
    );
  };

  // BAR
  if (pointIndex === 24) {
    const zoneSelector = moverIsBottom
      ? '.bar-myColor'
      : '.bar-opponentColor';

    // Когда шашка ещё видна на баре, привязываемся прямо к ней.
    const checker =
      root.querySelector(`${zoneSelector} .checker`);

    const checkerAnchor = centerOfElement(checker);
    if (checkerAnchor) return checkerAnchor;

    // После воспроизведения хода шашка уже могла уйти с бара.
    // Тогда используем соответствующую половину бара.
    const bar = root.querySelector('.bar');
    if (!bar) return null;

    const rect = bar.getBoundingClientRect();

    return screenPointToArrowSvg(
      (rect.left + rect.right) / 2,
      rect.top + rect.height * (moverIsBottom ? 0.75 : 0.25)
    );
  }

  // OFF
  if (pointIndex === 25) {
    const zoneSelector = moverIsBottom
      ? '.off-board-my'
      : '.off-board-opponent';

    const checker =
      root.querySelector(`${zoneSelector} .checker:last-child`) ||
      root.querySelector(`${zoneSelector} .checker`);

    const checkerAnchor = centerOfElement(checker);
    if (checkerAnchor) return checkerAnchor;

    const offBoard = root.querySelector('.off-board');
    if (!offBoard) return null;

    const rect = offBoard.getBoundingClientRect();

    return screenPointToArrowSvg(
      (rect.left + rect.right) / 2,
      rect.top + rect.height * (moverIsBottom ? 0.86 : 0.18)
    );
  }

  // Обычный пункт 1–24
  const el = getPointElementByIndex(pointIndex);
  if (!el) return null;

  const rect = el.getBoundingClientRect();

  const isTopPoint =
    el.classList.contains('point-container');

  const isBottomPoint =
    el.classList.contains('point-containerd');

  const screenX =
    (rect.left + rect.right) / 2;

  const dynamicPad = Math.max(
    12,
    Math.min(32, rect.height * 0.12)
  );

  let screenY;

  if (isTopPoint) {
    screenY = rect.top + dynamicPad;
  } else if (isBottomPoint) {
    screenY = rect.bottom - dynamicPad;
  } else {
    screenY = (rect.top + rect.bottom) / 2;
  }

  return screenPointToArrowSvg(screenX, screenY);
};

// const getArrowAnchorRelativeToBoard = (el) => {
//   const root = boardRef.current;
//   if (!root || !el) return null;

//   const boardRect = root.getBoundingClientRect();
//   const pointRect = el.getBoundingClientRect();

//   const isTopPoint =
//     el.classList.contains('point-container');

//   const isBottomPoint =
//     el.classList.contains('point-containerd');

//   const x =
//     (pointRect.left + pointRect.right) / 2 -
//     boardRect.left;

//   const pointHeight = pointRect.height || 0;

//   const dynamicPad = Math.max(
//     12,
//     Math.min(32, pointHeight * 0.12)
//   );

//   let y;

//   if (isTopPoint) {
//     y =
//       pointRect.top +
//       dynamicPad -
//       boardRect.top;
//   } else if (isBottomPoint) {
//     y =
//       pointRect.bottom -
//       dynamicPad -
//       boardRect.top;
//   } else {
//     y =
//       (pointRect.top + pointRect.bottom) / 2 -
//       boardRect.top;
//   }

//   return { x, y };
// };

// ===== Якорь для стрелок: строго в "носик" треугольника пункта =====
// ===== Якорь для стрелок: В ОСНОВАНИЕ пункта (где стоят шашки) =====
// const getArrowAnchorRelativeToBoard = (el) => {
//   const root = boardRef.current;
//   if (!root || !el) return null;

//   const r0 = root.getBoundingClientRect();
//   const r1 = el.getBoundingClientRect();

//   const midX = (r1.left + r1.right) / 2;

//   // верхний ряд
//   const isTopPoint = el.classList.contains('point-container');
//   // нижний ряд
//   const isBotPoint = el.classList.contains('point-containerd');

//   // ПОДБОР: это отступ от края внутрь, чтобы попадать в "основание" (зона шашек)
//   // если хочешь прям по самому краю — поставь 6-10
//   // если хочешь по центру шашек — 18-26 (обычно 20-22 самое норм)
//   const BASE_PAD = 20;

//   let x = midX;
//   let y;

//   if (isTopPoint) {
//     // верхние пункты: основание СВЕРХУ (у верхнего края доски)
//     y = r1.top + BASE_PAD;
//   } else if (isBotPoint) {
//     // нижние пункты: основание СНИЗУ (у нижнего края доски)
//     y = r1.bottom - BASE_PAD;
//   } else {
//     // bar/off — пока по центру (можно отдельно доработать позже)
//     y = (r1.top + r1.bottom) / 2;
//   }

//   return {
//     x: x - r0.left,
//     y: y - r0.top,
//   };
// };




const getMoverColorForMove = (moveNum, turn) => {
  // ✅ 1) САМЫЙ надёжный источник — то, что реально произошло в твоей партии
  // (как ты сам и пишешь: "когда ход белых я верно играю")
  if (turn?.player === 'player1') return 'white';
  if (turn?.player === 'player2') return 'black';

  // ✅ 2) если сервер прямо прислал цвет — используем
  if (turn?.color === 'white' || turn?.color === 'black') return turn.color;

  // ✅ 3) GNU toPlay — ОСТАВИМ ТОЛЬКО как запасной вариант
  // (у тебя сейчас именно тут и происходит путаница)
  const g = gnuByMove?.[moveNum];
  if (g?.toPlay === 'white' || g?.toPlay === 'black') return g.toPlay;

  return 'white';
};


const colorForQuality = (q) => {
  // по умолчанию НЕ красим (пусть будет обычный цвет интерфейса)
  if (!q || q === 'ok') return null;

  if (q === 'blunder') return '#ff5555';
  if (q === 'bad') return '#ff9900';
  if (q === 'doubtful' || q === 'inaccuracy') return '#ffd24a';

  return null;
};


useEffect(() => {
  if (!matchGameKey) return;

  // В матче всегда начинаем показ с "ход 0" (стартовая позиция партии)
  setSelectedMoveNum(0);
  setGnuByMove({});
}, [matchGameKey]);

useEffect(() => {
  // Берём текст анализа (поддерживает gnuAnalysis.text или gnuAnalysis.analysis)
  const text = pickGnuText(gnuAnalysis);

  // Если текста пока нет — подсветку не показываем
  if (!text || !String(text).trim()) {
    setGnuByMove({});
    return;
  }

  // Парсим качество ходов из текста GNU
  const parsed = parseGnuByMove(text);

  // Кладём мапу в стейт — именно её читает список ходов для подсветки
  setGnuByMove(parsed || {});
}, [
  matchGameKey,            // переключили партию матча
  gnuAnalysis?.text,       // пришёл/обновился текст анализа
  gnuAnalysis?.analysis,   // на всякий случай, если поле так называется
  gnuAnalysis?.status      // чтобы эффект сработал при смене статуса
]);


useEffect(() => {
  if (showResultModal) setHideResultModalLocal(false);
}, [showResultModal]);




useEffect(() => {
  // ✅ если не выбрано ничего (null) — показываем "живую" доску
  if (selectedMoveNum === null) {
    setDisplayBoard(board);
  }
}, [board, selectedMoveNum]);







// Если система прервала жест (свайп "назад" от края, уведомление и т.д.)
const handleTouchCancel = (e) => {
  touchMovedRef.current = false;
  cleanupTouchDrag();
  touchDataRef.current = {};
};





  const username = localStorage.getItem('username') || '';
  const resultSfxPlayedRef = useRef(false);
const suppressDiceUntilRef = useRef(0);
const suppressDoubleUntilRef = useRef(0);

const prevCanShowRef = React.useRef(false);



// ⬇️ ДОБАВЬ В СПИСОК useState
const [isSocketUp, setIsSocketUp] = useState(true); // моя связь с сервером
const [oppOnline, setOppOnline]   = useState(true); // оппонент online/offline
const [diceOwner, setDiceOwner] = useState(null); 
// 'player1' | 'player2' | null
// ⬆️ ДОБАВЛЕНО

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const spectatorParam = searchParams.get('spectator');
  const isSpectator =
    readOnly ||
    (
      spectatorParam !== null &&
      ['1', 'true', 'yes', 'on', ''].includes(
        String(spectatorParam).toLowerCase()
      )
    );
// Ход, который сейчас выбран в просмотре истории
// selectedMoveNum = 0 -> стартовая позиция, хода ещё нет
const replayTurn = (isReplayMode && selectedMoveNum > 0)
  ? (analysisTurns?.[selectedMoveNum - 1] || null)
  : null;


// Кубики, которые надо показывать
// (поддержка разных названий полей, на всякий случай)
const diceToShow = isReplayMode
  ? (
      replayTurn?.dice ||
      replayTurn?.turnDice ||
      replayTurn?.turn_dice ||
      []
    )
  : rolledDice;

// Показывать ли кубики
// Кто мы по роли (player1/player2) — нужно, чтобы не прятать кубики оппонента.
// hasRolledDice у тебя = "Я уже бросал" (только про себя), поэтому для чужого хода он false.
const myRoleForDice =
  playerColor === 'white' ? 'player1'
  : playerColor === 'black' ? 'player2'
  : null;

const isMyTurnForDice = Boolean(myRoleForDice && currentTurn === myRoleForDice);
const isOppTurnForDice = Boolean(myRoleForDice && currentTurn && currentTurn !== myRoleForDice);
const isSpectatorForDice = !myRoleForDice;
 


useEffect(() => {
  // Если кубики исчезли — очищаем владельца
  if (!Array.isArray(rolledDice) || rolledDice.length === 0) {
    setDiceOwner(null);
    return;
  }

  // Если кубики появились/лежат на столе — запоминаем,
  // чей это бросок на основании ТЕКУЩЕГО игрока в момент появления кубиков.
  // Главное: дальше при переключении currentTurn этот state уже не меняется сам по себе.
  setDiceOwner((prev) => {
    // если владелец уже зафиксирован, не перетираем его
    if (prev) return prev;

    if (currentTurn === 'player1' || currentTurn === 'player2') {
      return currentTurn;
    }

    return prev;
  });
}, [rolledDice, currentTurn]);
 
// Цвет кубиков в режиме просмотра истории (кто делал этот ход)
const replayDiceColor = (isReplayMode && selectedMoveNum > 0)
  ? (
      replayTurn?.player === 'player1' ? 'white'
      : replayTurn?.player === 'player2' ? 'black'
      : null
    )
  : null;



  // ===== кто "владелец" текущих кубиков (цвет картинок) =====
// const diceColorNow = isReplayMode
//   ? (replayDiceColor || 'white')
//   : (currentTurn === 'player2' ? 'black' : 'white'); // player1 -> white по умолчанию
const liveDiceColor =
  diceOwner === 'player2' ? 'black'
  : diceOwner === 'player1' ? 'white'
  : 'white';

const diceColorNow = isReplayMode
  ? (replayDiceColor || 'white')
  : liveDiceColor;


// ===== где показывать кубики: сверху (opp) или снизу (my) =====
// const diceSideNow = isReplayMode
//   ? (
//       // в реплее привяжем к тому, кто делал ход:
//       replayTurn?.player === 'player2' ? 'opp' : 'my'
//     )
//   : (
//       // в живой игре:
//       isOppTurnForDice ? 'opp' : 'my'
//     );
const liveDiceSide = (() => {
  if (!diceOwner) return 'my';

  // если я зритель и не знаю свою роль — просто показываем по стороне владельца
  if (!myRoleForDice) {
    return diceOwner === 'player2' ? 'opp' : 'my';
  }

  // если владелец кубиков совпадает с моей ролью — показываем у меня
  return diceOwner === myRoleForDice ? 'my' : 'opp';
})();

const diceSideNow = isReplayMode
  ? (replayTurn?.player === 'player2' ? 'opp' : 'my')
  : liveDiceSide;

    // --- НАСТРОЙКА ПОЗИЦИИ КУБИКОВ ВНУТРИ ДОСКИ (в %) ---
// "вправо 30%" = это "от правого края 30%" => left = 70%
// const DICE_POS = {
//   my:  { top: 50, left: 43 }, // ближе к "своей" стороне (низ/право)
//   opp: { top: 50, left: 26 }, // ближе к "чужой" стороне (верх/лево)
// };

// НАСТРОЙКА ПОЗИЦИИ КУБИКОВ — top/left в процентах относительно board-wrapper.
// ==========================
// НАСТРОЙКА ПОЗИЦИИ КУБИКОВ
// ==========================
const mySide =
  (!isSpectator && !readOnly && (playerColor === 'white' || playerColor === 'black'))
    ? playerColor
    : null;

const myRole  =
  mySide === 'white' ? 'player1'
  : mySide === 'black' ? 'player2'
  : null;

  const myTournamentColor = mySide; // то же самое
const opponentTournamentColor =
  myTournamentColor === 'white' ? 'black' :
  myTournamentColor === 'black' ? 'white' :
  null;


// flipped у тебя включается так же в className: (myTournamentColor === 'black')
const isBoardFlipped = myTournamentColor === 'black';

// ✅ ТВОИ "настроенные под чёрных" координаты (то, что ты писал: top/left числа)
const DICE_POS_BLACK = {
  my:  { top: 50, left: 56 },
  opp: { top: 50, left: 26 },
};

// ✅ ДЛЯ БЕЛОГО ВИДА обычно нужно зеркально поменять местами (иначе “кубики оппа на моей стороне”)
const DICE_POS_WHITE = {
  my:  { top: 50, left: 58 },
  opp: { top: 50, left: 24 },
};
    const DICE_POS = isBoardFlipped ? DICE_POS_BLACK : DICE_POS_WHITE;




// берём позицию под текущую сторону (my/opp)
const pos = DICE_POS[diceSideNow] ?? DICE_POS.my;
debugLog('FLIP=', isBoardFlipped, 'diceSideNow=', diceSideNow, 'pos=', pos);

// ✅ ВАЖНО: теперь style реально использует pos (а не левый '26%')
const diceOverlayStyle = {
  position: 'absolute',
  left: `${pos.left}%`,
  top: `${pos.top}%`,
  transform: 'translate(-50%, -50%)',
  zIndex: 50,
};


// ===== DEBUG: почему не показываем кубики =====
const hasDice = Array.isArray(diceToShow) && diceToShow.length > 0;
const showDiceNow = isReplayMode
  ? hasDice
  : (
      hasDice &&
      (
        // зритель видит всегда, если кубики есть
        isSpectatorForDice ||
        // если не мой ход (в т.ч. “ожидание подтверждения”) — показываем
        !isMyTurnForDice ||
        // если мой ход — показываем только после моего броска
        hasRolledDice
      )
    ); 



// безопасный триммер строк
const safe = (v) => (typeof v === 'string' ? v.trim() : '');

// попробуем сохранить/прочитать имя оппонента между рендерами,
// если пропа opponentName сначала не было, а потом появился
const opponentNameLS = React.useMemo(
  () => safe(localStorage.getItem('opponentName') || ''),
  []
);

useEffect(() => {
  const opp = safe(opponentName);
  if (opp) localStorage.setItem('opponentName', opp);
}, [opponentName]);

    // --- SFX для удвоений ---
  const prevDoubleOfferRef = useRef(null);       // предыдущее значение doubleOffer
  const prevBetMultRef     = useRef(Number(betMultiplier) || 1); // предыдущий множитель
  const multAtOfferRef     = useRef(null);       // множитель на момент возникновения оффера
  const localPassClickRef = useRef(false);


  
  /* ===================== SFX ===================== */
  const { play } = useSound();

  
  // безопасный вызов звука: ловим NotSupportedError/блокировку автоплея
  // const playSfx = (key) => {
  //   try {
  //     const p = play?.(key);
  //     if (p && typeof p.then === 'function') {
  //       p.catch(() => {});
  //     }
  //   } catch (_) {}
  // };
 const playSfx = (key) => {
  if (!key || (typeof key === 'string' && key[0] === '!')) return;

  const now = Date.now();

  // во время модалки, либо если мы только что показали оффер — глушим именно 'dice'
  if (key === 'dice' && ((showDoubleDialog && doubleOffer) || now < suppressDiceUntilRef.current)) {
    return;
  }

  // если всё-таки играем 'dice' — запретим 'double' на короткое окно
  if (key === 'dice') {
    suppressDoubleUntilRef.current = now + 450; // мс
  }

  try {
    const p = play?.(key);
    if (p && typeof p.then === 'function') p.catch(() => {});
  } catch (_) {}
};



  // 👇 Отправляем на сервер, какой звук у нас сыграл локально,
  // чтобы его услышал оппонент
  const emitRemoteSfx = (key) => {
    try {
      if (!key || (typeof key === 'string' && key[0] === '!')) return;

      const tableId = Number(localStorage.getItem('currentTableId'));
      if (!Number.isFinite(tableId)) return;

      const me = username || localStorage.getItem('username') || '';

      socket.emit('short:sfx', {
        tableId,
        by: me,
        key,
      });
    } catch (e) {
      debugWarn('[short:sfx emit error]', e);
    }
  };


  const onPointClickSfx = (...args) => { playSfx('click'); onPointClick?.(...args); };
  // const handleRollDiceClickSfx = () => { playSfx('dice'); handleRollDiceClick?.(); };
  // если открыт диалог удвоения → передаём '!dice' (playSfx его проигнорирует)
const handleRollDiceClickSfx = () => {
  const mute = Boolean(showDoubleDialog && doubleOffer);
  playSfx(mute ? '!dice' : 'dice');
  handleRollDiceClick?.();
};
 
const handleDropSfx = (e, idx) => { playSfx('drop'); handleDrop?.(e, idx); };

// ⬇️ НОВАЯ ВЕРСИЯ
const handleDragStartSfx = (e, a, b, fromBar) => {
  // пытаемся вытащить цвет шашки из className ('checker white' / 'checker black')
  let checkerColor = null;
  const cls = e?.target?.classList;
  if (cls) {
    if (cls.contains('white')) checkerColor = 'white';
    else if (cls.contains('black')) checkerColor = 'black';
  }

  // ❌ запрет при не своём ходе или если трогаем чужую шашку
  if (!canDragChecker(checkerColor)) {
    return;
  }

  // всё ок — играем звук и даём родному обработчику отработать
  playSfx('click');         // локальный звук
  emitRemoteSfx('click');   // звук оппоненту
  handleDragStart?.(e, a, b, fromBar);
};

  const handleUndoMoveSfx = () => {
  // локальный звук
  playSfx('cancel');
  // отправляем оппоненту
  emitRemoteSfx('cancel');
  // твоя логика отмены хода
  handleUndoMove?.();
};

  const handleConfirmMoveSfx = () => { play('ok'); confirmMove?.(); };
  const navigate = useNavigate();
  const { tableId: tableIdFromUrl } = useParams();




  // Зритель:
  //  - есть параметр ?spectator (любое значение: 1, true, yes, пустой) 
  //  - ИЛИ включён readOnly-режим


// ===== МАТЧ: копим PR по партиям и считаем общий PR матча =====
const [matchPrByGameKey, setMatchPrByGameKey] = useState({}); 
// формат: { [matchGameKey]: { pr: {...}, weight: number } }

const [prScope, setPrScope] = useState('match'); 
// 'match' | 'game'  (что показывать в панели PR)


  // 👇 helper для чтения игроков из localStorage
const readPlayersFromLS = (tableIdFromUrl) => {
  try {
    const id = tableIdFromUrl ?? localStorage.getItem('currentTableId');
    if (!id) return null;
    const raw = localStorage.getItem(`short:players:${id}`);
    return raw ? JSON.parse(raw) : null; // { white: {username, avatar}, black: {...} }
  } catch {
    return null;
  }
};

// 👇 теперь это состояние, а не useMemo
const [playersLS, setPlayersLS] = React.useState(() =>
  readPlayersFromLS(tableIdFromUrl)
);

// когда меняется стол или прилетает событие из лобби — перечитываем
useEffect(() => {
  // сразу перечитать при смене tableId
  setPlayersLS(readPlayersFromLS(tableIdFromUrl));

  // обработчик нашего пользовательского события
  const handlePlayersChanged = (e) => {
    const currId = tableIdFromUrl ?? localStorage.getItem('currentTableId');
    const detailId = e.detail?.tableId;

    // если событие про другой стол — игнорируем
    if (detailId && String(detailId) !== String(currId)) return;

    setPlayersLS(readPlayersFromLS(tableIdFromUrl));
  };

  window.addEventListener('short:players-changed', handlePlayersChanged);

  return () => {
    window.removeEventListener('short:players-changed', handlePlayersChanged);
  };
}, [tableIdFromUrl]);




useEffect(() => {
  setGnuStats(null);
}, [tableIdFromUrl]);


// при смене стола — сбрасываем накопление по матчу
useEffect(() => {
  setMatchPrByGameKey({});
  setPrScope('match');
}, [tableIdFromUrl]);

// когда по текущей партии появился PR — запоминаем его в матчевую "копилку"
useEffect(() => {
  if (!matchGameKey) return;

  const prNow = gnuAnalysis?.pr || gnuStats;
  if (!prNow) return;

  const weight = Array.isArray(confirmedTurns) ? confirmedTurns.length : 1;

  setMatchPrByGameKey((prev) => ({
    ...prev,
    [matchGameKey]: {
      pr: prNow,
      weight: weight > 0 ? weight : 1,
    },
  }));
}, [matchGameKey, gnuAnalysis?.pr, gnuStats, confirmedTurns]);



// при смене стола сбрасываем снимок
useEffect(() => {
  const id = String(tableIdFromUrl ?? localStorage.getItem('currentTableId') ?? '');
  if (initialTableIdRef.current !== id) {
    initialTableIdRef.current = id;
    initialBoardRef.current = null;
  }
}, [tableIdFromUrl]);






const matchPrAgg = React.useMemo(() => {
  const entries = Object.values(matchPrByGameKey || {});
  if (!entries.length) return null;

  // средневзвешенное: sum(value * weight) / sum(weight)
  const wavg = (getter) => {
    let sum = 0;
    let wsum = 0;

    for (const e of entries) {
      const w = Number(e?.weight) || 1;
      const v = getter(e?.pr);
      const n = Number(v);

      if (!Number.isFinite(n)) continue;
      sum += n * w;
      wsum += w;
    }

    if (!wsum) return null;
    return Math.round((sum / wsum) * 1000) / 1000; // округление до 0.001
  };

  // понимаем формат: split (white/black) или простой
  const isSplit = entries.some((e) => e?.pr?.white || e?.pr?.black);

  if (!isSplit) {
    return {
      chequer: wavg((pr) => pr?.chequer),
      cube: wavg((pr) => pr?.cube),
      luck: wavg((pr) => pr?.luck),
      overall: wavg((pr) => pr?.overall),
    };
  }

  const side = (s) => ({
    chequer: wavg((pr) => pr?.[s]?.chequer),
    cube: wavg((pr) => pr?.[s]?.cube),
    luck: wavg((pr) => pr?.[s]?.luck),
    overall: wavg((pr) => pr?.[s]?.overall),
  });

  return {
    white: side('white'),
    black: side('black'),
  };
}, [matchPrByGameKey]);




// === СИНХРОНИЗАЦИЯ КУБИКОВ ПЕРВОГО БРОСКА С НОВЫМ СТОЛОМ ===
useEffect(() => {
  // если сменился стол, сначала гасим отображение
  if (tableIdFromUrl !== prevTableIdForFirstRef.current) {
    prevTableIdForFirstRef.current = tableIdFromUrl;
    setFirstRollsVisible({ p1: null, p2: null });
  }

  // считаем, что пришли актуальные значения,
  // если вообще есть хоть какой-то валидный бросок
  const p1 =
    Number.isInteger(firstRolls?.player1Roll)
      ? firstRolls.player1Roll
      : (Number.isInteger(player1Roll) ? player1Roll : null);

  const p2 =
    Number.isInteger(firstRolls?.player2Roll)
      ? firstRolls.player2Roll
      : (Number.isInteger(player2Roll) ? player2Roll : null);

  // если ничего нет — не трогаем (остаемся на null/null)
  if (p1 == null && p2 == null) return;

  // обновляем видимые значения
  setFirstRollsVisible({ p1, p2 });
}, [tableIdFromUrl, firstRolls, player1Roll, player2Roll]);

// 👇 ДОБАВЬ ЭТОТ СТЕЙТ И РЕФ
const [firstRollsVisible, setFirstRollsVisible] = useState({ p1: null, p2: null });
const prevTableIdForFirstRef = useRef(tableIdFromUrl);


const getDisplayName = (side) => {
  const me  = safe(username);
  const opp = safe(opponentName) || opponentNameLS;

  const whiteFromProps = safe(player1Name);
  const blackFromProps = safe(player2Name);
  const whiteFromLS    = safe(playersLS?.white?.username);
  const blackFromLS    = safe(playersLS?.black?.username);

  if (side === 'white') {
    if (mySide === 'white' && me) return me;         // моя сторона
    if (whiteFromProps) return whiteFromProps;       // пришло пропом
    if (whiteFromLS)    return whiteFromLS;          // взяли из LS
    if (opp && mySide === 'black') return opp;       // оппонент как фолбэк
    return translate('game.common.waiting');
  }

  if (side === 'black') {
    if (mySide === 'black' && me) return me;
    if (blackFromProps) return blackFromProps;
    if (blackFromLS)    return blackFromLS;
    if (opp && mySide === 'white') return opp;
    return translate('game.common.waiting');
  }

  return translate('game.common.waiting');
}; 

  // ✅ не подставляем цвет, если сервер ещё не сказал
// const mySide =
//   (playerColor === 'white' || playerColor === 'black') ? playerColor : null;

// const myRole  = (mySide === 'white' ? 'player1' : (mySide === 'black' ? 'player2' : null));
// const oppRole = (myRole === 'player1' ? 'player2' : (myRole === 'player2' ? 'player1' : null));
// ✅ моя сторона только если я ИГРОК, а не зритель/readonly


const oppRole =
  myRole === 'player1' ? 'player2'
  : myRole === 'player2' ? 'player1'
  : null;

// я игрок только если:
// 1) у меня есть цвет
// 2) я НЕ в режиме spectator/readOnly
const iAmPlayer = !!mySide && !isSpectator;

// "чистый" зритель — помечен как spectator/readOnly и при этом не считается игроком
const iAmPureSpectator = isSpectator && !iAmPlayer;










// 🔊 оппонент бросил кубики → сыграть 'dice'
const prevOppDiceRef = useRef('');

useEffect(() => {
  // чей сейчас ход — оппонента?
  const oppTurn = Boolean(oppRole && currentTurn === oppRole);

  // есть ли кубики на столе
  const hasDice = Array.isArray(rolledDice) && rolledDice.length > 0;
  const key = hasDice ? String(rolledDice.join(',')) : '';

  if (oppTurn && hasDice && key !== prevOppDiceRef.current) {
    // важно: используем твою функцию-обёртку
    playSfx('dice');
  }

  // сохраняем снимок, чтобы не дублировать
  prevOppDiceRef.current = key;
}, [rolledDice, currentTurn, oppRole]);



// 🔊 оппонент двигает шашку → сыграть 'drop' при изменении доски
const prevBoardKeyRef = useRef('');

useEffect(() => {
  const oppTurn = Boolean(oppRole && currentTurn === oppRole);

  // лёгкий ключ доски: достаточно stringify массива
  const key = JSON.stringify(board || []);

  // пропускаем первый рендер (чтобы не хлопать при первичной загрузке)
  if (!prevBoardKeyRef.current) {
    prevBoardKeyRef.current = key;
    return;
  }

  if (oppTurn && key !== prevBoardKeyRef.current) {
    playSfx('drop');
  }

  prevBoardKeyRef.current = key;
}, [board, currentTurn, oppRole]);





useEffect(() => {
  // если ход не выбран — реплей не считаем
  // (null = живой режим). 0 — это валидный выбор (стартовая позиция)
  if (selectedMoveNum === null) return;

  const turns = Array.isArray(analysisTurns) ? analysisTurns : [];

  const targetIndex = selectedMoveNum - 1; // 0-based

  // selectedMoveNum = 0 -> targetIndex = -1 (просто покажем стартовую позицию)
  if (selectedMoveNum > 0) {
    const targetTurn = turns[targetIndex];
    if (!targetTurn) return;
  }

  // ---------- helpers ----------
  const deepClone = (x) => JSON.parse(JSON.stringify(x));

  // нормализация from/to (могут прийти числа или строки)
  const norm = (v) => {
    if (v == null) return null;

    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "bar") return "bar";
      if (s === "off") return "off";
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }

    if (typeof v === "number") {
      return Number.isFinite(v) ? v : null;
    }

    return null;
  };

  const isPoint = (x) => typeof x === "number" && x >= 0 && x <= 23;
  const isSpecial = (x) => typeof x === "number" && x >= 24;

  // Определим какие числа чаще встречаются как "from>=24" и как "to>=24"
  // Это помогает, если у тебя в истории разные коды (24/25 или 25/26 и т.п.)
  const detectCodes = () => {
    const fromCnt = new Map();
    const toCnt = new Map();

    for (let i = 0; i <= targetIndex; i++) {
      const t = turns[i];
      if (!t || !Array.isArray(t.moves)) continue;

      for (const m of t.moves) {
        const f = norm(m?.from);
        const to = norm(m?.to);

        if (isSpecial(f)) fromCnt.set(f, (fromCnt.get(f) || 0) + 1);
        if (isSpecial(to)) toCnt.set(to, (toCnt.get(to) || 0) + 1);
      }
    }

    const pickMax = (map) => {
      let bestKey = null;
      let bestVal = -1;
      for (const [k, v] of map.entries()) {
        if (v > bestVal) {
          bestVal = v;
          bestKey = k;
        }
      }
      return bestKey;
    };

    // Фолбэки: "классика" — bar=24 off=25 (но мы всё равно подстрахуемся ниже)
    const barCode = pickMax(fromCnt) ?? 24;
    const offCode = pickMax(toCnt) ?? 25;

    return { barCode, offCode };
  };

  const { barCode, offCode } = detectCodes();

  // ВАЖНО: если спец-значение стоит в from → это BAR, если в to → это OFF.
  // Даже если barCode === offCode (например 25), позиция (from/to) решает.
  const isBar = (x, pos) => {
    if (x === "bar") return true;
    if (typeof x === "number") {
      if (x === barCode) return true;
      // позиционный фолбэк: любое спец-значение в from считаем баром,
      // если оно не похоже на offCode
      if (pos === "from" && x >= 24) return true;
    }
    return false;
  };

  const isOff = (x, pos) => {
    if (x === "off") return true;
    if (typeof x === "number") {
      if (x === offCode) return true;
      // позиционный фолбэк: любое спец-значение в to считаем off
      if (pos === "to" && x >= 24) return true;
    }
    return false;
  };

  // ---------- 1) стартовая доска ----------
  // ВАЖНО: старт всегда ДО 1-го хода (не берем текущий `board`, иначе "Старт" может стать позицией после 1-го хода)
  let snapshot = getStartSnapshot();
  if (!Array.isArray(snapshot) || snapshot.length !== 24) {
    snapshot = Array.from({ length: 24 }, () => []);
  }

  // ---------- 2) счётчики бара и off ----------
  const cap = { white: 0, black: 0 }; // побитые на баре
  const off = { white: 0, black: 0 }; // выведенные

  try {
    // проигрываем ходы 0..targetIndex
    for (let i = 0; i <= targetIndex; i++) {
      const t = turns[i];
      if (!t || !Array.isArray(t.moves)) continue;

      const moverColor = t.player === "player1" ? "white" : "black";
      const oppColor = moverColor === "white" ? "black" : "white";

      for (const m of t.moves) {
        const from = norm(m?.from);
        const to = norm(m?.to);

        if (from == null || to == null) continue;

        // --- снимаем шашку с from ---
        if (isBar(from, "from")) {
          // ход с бара -> уменьшаем побитых у ходящего
          cap[moverColor] = Math.max(0, (cap[moverColor] || 0) - 1);
        } else if (isPoint(from)) {
          const st = Array.isArray(snapshot[from]) ? snapshot[from] : [];
          if (st.length > 0) st.pop();
          snapshot[from] = st;
        }

        // --- кладём шашку на to ---
        if (isPoint(to)) {
          const st = Array.isArray(snapshot[to]) ? snapshot[to] : [];

          // удар: на точке ровно 1 шашка соперника
          const topColor = String(st?.[st.length - 1]?.color || "").toLowerCase();
          if (st.length === 1 && topColor === oppColor) {
            st.pop();
            cap[oppColor] = (cap[oppColor] || 0) + 1; // побитая ушла на бар
          }

          st.push({ color: moverColor });
          snapshot[to] = st;
        } else if (isOff(to, "to")) {
          off[moverColor] = (off[moverColor] || 0) + 1;
        } else if (isBar(to, "to")) {
          // на всякий случай (обычно такого не будет)
          cap[moverColor] = (cap[moverColor] || 0) + 1;
        }
      }
    }

    // ---------- 3) применяем в UI ----------
    setDisplayBoard(snapshot);
    setDisplayCaptured(cap);
    setDisplayOffCount(off);
  } catch (err) {
    debugWarn("[Analysis replay error]", err);
  }
}, [selectedMoveNum, confirmedTurns, boardState, board, tableIdFromUrl]);




// 🔊 оппонент закончил ход → ход переключился на меня → сыграть 'ok'
const prevTurnRef = useRef(currentTurn);

useEffect(() => {
  const prev = prevTurnRef.current;
  const now  = currentTurn;

  if (prev && now && prev !== now) {
    // было: ход оппонента → стало: мой ход
    if (oppRole && myRole && prev === oppRole && now === myRole) {
      playSfx('ok');
    }
  }

  prevTurnRef.current = now;
}, [currentTurn, myRole, oppRole]);







useEffect(() => {
  const onOppUndo = (payload) => {
    try {
      // если есть tableId в payload — фильтруем по текущему столу
      const currTableId = Number(tableIdFromUrl ?? localStorage.getItem('currentTableId'));
      if (Number(payload?.tableId) && Number(payload.tableId) !== currTableId) return;

      // если в payload есть инициатор — не играем звук на свой собственный undo
      if (payload?.by && String(payload.by) === String(username)) return;

      playSfx('cancel'); // 🔊 звук отмены
    } catch {}
  };

  socket.on('moveUndone', onOppUndo);   // ← имя события подставь своё, если другое
  return () => socket.off('moveUndone', onOppUndo);
}, [tableIdFromUrl, username]);





// 🔊 Получаем звуковые события от оппонента
useEffect(() => {
  const currTableId = Number(tableIdFromUrl ?? localStorage.getItem('currentTableId'));

  const onRemoteSfx = (payload) => {
    try {
      if (!payload) return;

      // Если у нас есть tableId и у события он есть — тогда фильтруем.
      if (
        Number.isFinite(currTableId) &&
        Number.isFinite(Number(payload.tableId)) &&
        Number(payload.tableId) !== currTableId
      ) {
        return;
      }

      const me = username || localStorage.getItem('username') || '';
      // своё же событие нам не нужно — мы уже сыграли звук локально
      if (payload.by && String(payload.by) === String(me)) return;

      const key = payload.key;
      if (!key || typeof key !== 'string') return;

      // проигрываем тот же звук, что и у оппонента
      playSfx(key);
    } catch (e) {
      debugWarn('[short:sfx handler error]', e);
    }
  };

  socket.on('short:sfx', onRemoteSfx);
  return () => socket.off('short:sfx', onRemoteSfx);
}, [tableIdFromUrl, username]);




  /* ===== «Good luck» — сыграть ровно один раз, когда автобросок выставил оба кубика ===== */
  const goodluckPlayedRef = useRef(false);
  const gestureHandlerRef = useRef(null);
  const suppressClickUntilRef = useRef(0);


const bothPlayersSeated = React.useMemo(() => {
  const w = getDisplayName('white');
  const b = getDisplayName('black');
  return w && w !== translate('game.common.waiting') && b && b !== translate('game.common.waiting');
}, [
  player1Name, player2Name,
  mySide, opponentName, opponentNameLS, playersLS,
]);


const prevBothSeatedRef = useRef(false);


// Можно ли показать кнопку "Закрыть стол":
// - я игрок
// - игра ещё не закончилась
// - либо второй игрок ещё не сел, либо первый ход ещё не завершён
const canShowCloseTableBtn =
  iAmPlayer &&
  !gameFinished &&
  (!bothPlayersSeated || !firstRollComplete);

// Можно ли показать кнопку "Сдаться":
// - я игрок
// - игра не закончена
// - оба игрока уже сидят
// - первый ход завершён (игра реально стартовала)
const canShowResignBtn =
  iAmPlayer &&
  !gameFinished &&
  bothPlayersSeated &&
  firstRollComplete;


// ⬇️ ДОБАВЬ СРАЗУ ПОСЛЕ bothPlayersSeated
// сидят ли по факту игроки (по твоей же функции имён)
const whiteSeated = getDisplayName('white') !== translate('game.common.waiting');
const blackSeated = getDisplayName('black') !== translate('game.common.waiting');

// сидит ли конкретно мой оппонент
const opponentSeated =
  mySide === 'white' ? blackSeated :
  mySide === 'black' ? whiteSeated :
  false;

// опционально: индикатор, что партия уже стартовала (не обязателен для баннеров, но полезен)
const gameStarted =
  bothPlayersSeated && (
    firstMoveComplete ||
    Number.isInteger(firstRolls?.player1Roll) ||
    Number.isInteger(firstRolls?.player2Roll)
  );
// ⬆️ ДОБАВЛЕНО
// Позицию выбираем по ЦВЕТУ кубиков (это решает кейс "у белых кубики на противоположной")
const diceSideForPos = (() => {
  if (myTournamentColor && opponentTournamentColor) {
    if (diceColorNow === myTournamentColor) return 'my';
    if (diceColorNow === opponentTournamentColor) return 'opp';
  }
  // fallback, если мы зритель/нет цвета и т.п.
  return diceSideNow; // 'my' | 'opp'
})();

const basePos = DICE_POS[diceSideForPos] || DICE_POS.my;

// Если доска перевёрнута (rotate 180), инвертируем проценты,
// чтобы на экране кубики остались на "правильной" стороне.
// let pos = isBoardFlipped
//   ? { top: 100 - basePos.top, left: 100 - basePos.left }
//   : basePos;


  // ↓↓↓ опускаем кубики ТОЛЬКО когда ты играешь БЕЛЫМИ и показываются белые кубики
const WHITE_DICE_DROP = 4; // процентов. если мало — поставь 5–7

// if (!isBoardFlipped && myTournamentColor === 'white' && diceColorNow === 'white') {
//   pos = {
//     ...pos,
//     top: Math.min(95, pos.top + WHITE_DICE_DROP),
//   };
// }





useEffect(() => {
    if (boardVb.w <= 0 || boardVb.h <= 0) {
    const root = boardRef.current;
    if (root) {
      const rect = root.getBoundingClientRect();
      if (rect.width > 50 && rect.height > 50) {
        setBoardVb((prev) => {
          if (prev.w === rect.width && prev.h === rect.height) return prev;
          return { w: rect.width, h: rect.height };
        });
      }
    }
    setGnuArrows([]);
setPlayedArrows([]);
setPlayedArrowTone(null);
return;

  }

  // стрелки нужны только для реального хода 1..N
  if (selectedMoveNum == null || selectedMoveNum <= 0) {
  setGnuArrows([]);
setPlayedArrows([]);
setPlayedArrowTone(null);
return;

  }

  const g = gnuByMove[selectedMoveNum];
 const t = analysisTurns?.[selectedMoveNum - 1];


  if (!g?.bestMovesText || !t) {
   setGnuArrows([]);
setPlayedArrows([]);
setPlayedArrowTone(null);
return;

  }

  // кто делал ход (для правильного парсинга и зеркалирования)
  const moverColor = getMoverColorForMove(selectedMoveNum, t);


  // 1) лучший ход GNU -> пары
  const bestPairs = parseBestMovesPairs(g.bestMovesText, moverColor);

  // 2) фактически сыгранный ход -> пары
  const actualPairs = turnMovesToPairs(t.moves);


  const describePoint = (pointIndex) => {
  const el = getPointElementByIndex(pointIndex);

  if (!el) {
    return {
      pointIndex,
      found: false,
    };
  }

  const rect = el.getBoundingClientRect();

  return {
    pointIndex,
    found: true,
    className: String(el.className || ''),
    text: el.querySelector('span')?.textContent || '',
    rect: {
      left: Math.round(rect.left),
      top: Math.round(rect.top),
      right: Math.round(rect.right),
      bottom: Math.round(rect.bottom),
    },
    anchor: getArrowAnchorForPoint(
  pointIndex,
  moverColor
),
  };
};

console.log('[ARROW TRACE]', {
  selectedMoveNum,
  turnPlayer: t?.player,
  turnColor: t?.color,
  moverColor,

  rawBestText: g?.bestMovesText,

  displayedBestText:
    convertGnuBestMovesTextForUi(
      g?.bestMovesText,
      moverColor
    ),

  bestPairs,
  actualPairs,

  boardFlipped:
    boardRef.current?.classList.contains('flipped'),

  boardClass:
    boardRef.current?.className,

  bestElements: bestPairs.map(({ from, to }) => ({
    from: describePoint(from),
    to: describePoint(to),
  })),
});
  // 3) fallback-качество: если Alert нет, но лучший ход не совпал — считаем inaccuracy
  const sameAsBest = bestPairs.length > 0
    ? samePairMultiset(actualPairs, bestPairs)
    : true;

  const qFinal =
    (!g.quality || g.quality === 'ok')
      ? (bestPairs.length > 0 && !sameAsBest ? 'inaccuracy' : 'ok')
      : g.quality;

 // --- helper: строим SVG-стрелки по парам ходов ---
const buildArrowsFromPairs = (pairs, moverColor) => {
  if (!Array.isArray(pairs) || pairs.length === 0) return [];

  // 1) посчитаем сколько раз встречается каждая пара from->to
  const counts = new Map();
  for (const p of pairs) {
    const k = `${p.from}-${p.to}`;
    counts.set(k, (counts.get(k) || 0) + 1);
  }

  // 2) рисуем одинаковые стрелки параллельно (со сдвигом)
  const used = new Map();
  const arrows = [];

  for (const { from, to } of pairs) {
  const a = getArrowAnchorForPoint(from, moverColor);
const b = getArrowAnchorForPoint(to, moverColor);
    if (!a || !b) continue;

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (!len || len < 1) continue;

    // единичный вектор направления
    const ux = dx / len;
    const uy = dy / len;

    // нормаль (для смещения параллельных стрелок)
    const nx = -uy;
    const ny = ux;

    // сдвиг для повторяющихся стрелок
    const key = `${from}-${to}`;
    const total = counts.get(key) || 1;
    const idx = used.get(key) || 0;
    used.set(key, idx + 1);

    // const GAP = 12;
    const scaleK = boardVb.w > 0 ? boardVb.w / 1020 : 1;
const GAP = Math.max(10, Math.min(18, 12 * scaleK));
    const shift = (idx - (total - 1) / 2) * GAP;

    const sx = a.x + nx * shift;
    const sy = a.y + ny * shift;
    const tx = b.x + nx * shift;
    const ty = b.y + ny * shift;

    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;

    // дуга уходит "наружу" от центра доски
    const cX = boardVb.w / 2;
    const cY = boardVb.h / 2;
    const vx = mx - cX;
    const vy = my - cY;

    // если нормаль смотрит "внутрь" — переворачиваем
// Дугу направляем внутрь доски.
// vx/vy направлены от центра доски наружу.
// Если нормаль тоже смотрит наружу, разворачиваем её.
let nnx = nx;
let nny = ny;

if (vx * nnx + vy * nny > 0) {
  nnx = -nnx;
  nny = -nny;
}

const curveBase = Math.min(
  170 * scaleK,
  Math.max(60 * scaleK, len * 0.22)
);

// Дополнительная страховка:
// контрольная точка кривой не может выйти за границы доски.
const safePad = Math.max(12, 20 * scaleK);

const rawCx = mx + nnx * curveBase;
const rawCy = my + nny * curveBase;

const cx = Math.max(
  safePad,
  Math.min(boardVb.w - safePad, rawCx)
);

const cy = Math.max(
  safePad,
  Math.min(boardVb.h - safePad, rawCy)
);

    const d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${tx.toFixed(1)} ${ty.toFixed(1)}`;

    arrows.push({ d, sx, sy, tx, ty });
  }

  return arrows;
};

// ✅ Стрелки подсказки GNU рисуем ТОЛЬКО если наш ход отличается от лучшего
if (!sameAsBest && bestPairs.length > 0) {
  setGnuArrows(
  buildArrowsFromPairs(bestPairs, moverColor)
);
} else {
  setGnuArrows([]);
}

// ✅ Наш ход — рисуем только если ошибка (не ok и не совпало с лучшим)
if (qFinal === 'ok' || sameAsBest) {
  setPlayedArrows([]);
  setPlayedArrowTone(null);
} else {
  // ВАЖНО: если часть хода совпала с лучшим, НЕ подсвечиваем её как ошибку.
  // Рисуем "наши" стрелки только для тех пар, которые отличаются от лучшего.
  const playedPairsToDraw = (bestPairs.length > 0)
    ? splitActualPairsByBest(actualPairs, bestPairs).unmatchedActual
    : actualPairs;

 setPlayedArrows(
  buildArrowsFromPairs(playedPairsToDraw, moverColor)
);

  // негрубая ошибка -> жёлтая, грубая -> красная
  const tone =
    (qFinal === 'inaccuracy' || qFinal === 'doubtful') ? 'yellow' : 'red';

  setPlayedArrowTone(tone);
}

}, [  selectedMoveNum,
  gnuByMove,
  confirmedTurns,
  myTournamentColor,
  displayBoard,
  boardVb.w,
  boardVb.h]);



// useEffect(() => {
//   // ✅ в режиме просмотра ходов (включая старт = 0) ничего не трогаем
//   if (isReplayMode) return;

//   // ✅ если ход закончился (hasRolledDice=false), старые кубики убираем
//   if (!hasRolledDice && Array.isArray(rolledDice) && rolledDice.length > 0) {
//     setRolledDice([]);
//   }
// }, [hasRolledDice, rolledDice, isReplayMode, setRolledDice]);
useEffect(() => {
  // ✅ в режиме просмотра ходов (включая старт = 0) ничего не трогаем
  if (isReplayMode) return;

  // ⚠️ hasRolledDice = "Я уже бросал?" (только про себя).
  // На стороне соперника во время МОЕГО хода hasRolledDice будет false,
  // но кости соперника при этом должны показываться.
  // Поэтому чистим кости только в начале МОЕГО хода, когда я ещё не бросал.
  const myRole =
    myTournamentColor === 'white' ? 'player1' :
    myTournamentColor === 'black' ? 'player2' :
    null;

  const isMyTurnNow = !!myRole && currentTurn === myRole;

  if (isMyTurnNow && !hasRolledDice && Array.isArray(rolledDice) && rolledDice.length > 0) {
    setRolledDice([]);
  }
}, [isReplayMode, myTournamentColor, currentTurn, hasRolledDice, rolledDice, setRolledDice]);


  // ОКОНЧАНИЕ ТАЧ-ПЕРЕТАСКИВАНИЯ
const handleTouchEnd = (e) => {
  const wasMoved = touchMovedRef.current;
  // было ли вообще перетаскивание
  const wasDragging = !!touchDrag.current.dragging;

  // 🔥 1) СНАЧАЛА ВСЕГДА убираем фантом и чистим состояние
  cleanupTouchDrag();
touchMovedRef.current = false;
  // если ничего не тянули — дальше просто выходим
  if (!wasDragging) return;

  // 🔥 дальше логика дропа (она уже НЕ влияет на фантом — его нет)
  if (!e.changedTouches || e.changedTouches.length === 0) return;

  const { clientX, clientY } = e.changedTouches[0];

  // куда отпустили палец
  const elem = document.elementFromPoint(clientX, clientY);
  if (!elem) return;

  // ищем ближайшую точку (point-container / point-containerd)
  const pointDiv = elem.closest('.point-container, .point-containerd');
  if (!pointDiv) return;

  // ищем класс вида p0 / p1 / r0 / r1
  const cls = Array.from(pointDiv.classList)
    .find((c) => /^p\d+$/.test(c) || /^r\d+$/.test(c));
  if (!cls) return;

  const raw = parseInt(cls.slice(1), 10);
  if (!Number.isFinite(raw)) return;

  // верхний или нижний ряд
  const isTop = pointDiv.classList.contains('point-container');

  let dropIndex;

  if (myTournamentColor === 'white') {
    // как у тебя в onDrop для белых:
    // top:  24 - raw - 1
    // bottom: raw
    dropIndex = isTop ? 24 - raw - 1 : raw;
  } else {
    // как у тебя в onDrop для чёрных:
    // top: raw
    // bottom: 24 - raw - 1
    dropIndex = isTop ? raw : 24 - raw - 1;
  }

  // синтетический drop-ивент с тем же "виртуальным" dataTransfer
  const dropEvent = {
    preventDefault() {},
    stopPropagation() {},
    dataTransfer: {
      getData(type) {
        const key = type || 'text';
        return touchDataRef.current[key] ?? '';
      },
      setData(type, value) {
        const key = type || 'text';
        touchDataRef.current[key] = String(value ?? '');
      },
      clearData(type) {
        if (!type) {
          touchDataRef.current = {};
        } else {
          delete touchDataRef.current[type];
        }
      },
      effectAllowed: 'move',
      dropEffect: 'move',
    },
  };

  // вызываем тот же обработчик, что и при обычном дропе мышью
  handleDropSfx(dropEvent, dropIndex);

  touchDataRef.current = {};
};
 
  
  useEffect(() => {
  const curr = doubleOffer ?? null;
  const prev = prevDoubleOfferRef.current;

  // Новое предложение: появилось curr, которого раньше не было или оно изменилось
  const changed = JSON.stringify(curr) !== JSON.stringify(prev);
if (curr && changed) {
  multAtOfferRef.current = Number(betMultiplier) || 1;

  const now = Date.now();
  // если только что был 'dice' — не играем 'double'
  if (now >= suppressDoubleUntilRef.current) {
    play('double').catch(() => {});
  }

  // и наоборот: после появления оффера немного приглушим 'dice'
  suppressDiceUntilRef.current = now + 450; // мс
}


  prevDoubleOfferRef.current = curr;
}, [doubleOffer, betMultiplier, play]);


useEffect(() => {
  const prev = prevBetMultRef.current;
  const curr = Number(betMultiplier) || 1;

  if (curr > prev) {
    // мультипликатор вырос → оффер принят
    play('take').catch(() => {});
  }

  prevBetMultRef.current = curr;
}, [betMultiplier, play]);







useEffect(() => {
  const prev = prevDoubleOfferRef.current;
  const curr = doubleOffer ?? null;

  if (prev && !curr) {
    const multNow = Number(betMultiplier) || 1;
    const multAtOffer = multAtOfferRef.current ?? multNow;

    if (multNow === multAtOffer) {
      // если это мы только что нажали Pass — не дублируем звук
      if (localPassClickRef.current) {
        localPassClickRef.current = false;
      } else {
        play('pass').catch(() => {});
      }
    }

    multAtOfferRef.current = null;
  }
}, [doubleOffer, betMultiplier, play]);

const handleDeclineDoubleClickSfx = () => {
  localPassClickRef.current = true; // сообщаем эффекту, что звук уже сыгран локально
  playSfx('pass');                   // мгновенный локальный звук
  onDeclineDouble?.();               // дальше — твоя логика отказа
};




  useEffect(() => {
     // стоп-условия
  if (!bothPlayersSeated) return;
  if (firstMoveComplete) return;

    const ready =
      Number.isInteger(firstRolls?.player1Roll) &&
      Number.isInteger(firstRolls?.player2Roll);

    if (!ready) return;
    if (goodluckPlayedRef.current) return;
    if (readOnly || gameFinished) return;

    // подчистим старый слушатель, если вдруг есть
    if (gestureHandlerRef.current) {
      window.removeEventListener('pointerdown', gestureHandlerRef.current);
      gestureHandlerRef.current = null;
    }

    const onFirstGesture = async () => {
      if (goodluckPlayedRef.current) return;
      try {
        const r = play?.('goodluck');
        if (r && typeof r.then === 'function') await r;
      } catch (_) {
        // игнор
      } finally {
        goodluckPlayedRef.current = true;
        suppressClickUntilRef.current = Date.now() + 250; // чтобы не наложился звук клика
        window.removeEventListener('pointerdown', onFirstGesture);
        gestureHandlerRef.current = null;
      }
    };

    // пробуем сразу
    let immediatePlayed = false;
    try {
      const maybe = play?.('goodluck');
      if (maybe && typeof maybe.then === 'function') {
        maybe.then(() => {
          immediatePlayed = true;
          goodluckPlayedRef.current = true;
          suppressClickUntilRef.current = Date.now() + 250;
        }).catch(() => {
          // автоплей заблокирован — ждём жест
          if (!goodluckPlayedRef.current) {
            gestureHandlerRef.current = onFirstGesture;
            window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
          }
        });
      } else {
        // неизвестно, сработало ли — подстрахуемся жестом
        gestureHandlerRef.current = onFirstGesture;
        window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
      }
    } catch (_) {
      // если бросило синхронно — просто ждём жест
      gestureHandlerRef.current = onFirstGesture;
      window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
    }

    return () => {
      if (gestureHandlerRef.current) {
        window.removeEventListener('pointerdown', gestureHandlerRef.current);
        gestureHandlerRef.current = null;
      }
    };
  }, [firstRolls, readOnly, gameFinished, play]);

  /* приглушаем «click», если только что сыгрался goodluck на первый жест */
  const onPointClickSfxGuarded = (...args) => {
    if (Date.now() > suppressClickUntilRef.current) playSfx('click');
    onPointClick?.(...args);
  };

  const handleCheckerTapMove = (e, pointIndex, realIndex, checkerColor) => {
  if (!isTouchDevice) return;

  // если палец реально тащили — это НЕ тап
  if (touchMovedRef.current) {
    touchMovedRef.current = false;
    return;
  }

  e.preventDefault();
  e.stopPropagation();

  if (!canDragChecker(checkerColor)) {
    cleanupTouchDrag();
    touchDataRef.current = {};
    touchMovedRef.current = false;
    return;
  }

  cleanupTouchDrag();
  touchDataRef.current = {};
  touchMovedRef.current = false;

  onPointClickSfxGuarded(pointIndex, realIndex);
};

  /* ============ Куб удвоения ============ */
  const cubeMult = Number(betMultiplier) > 0 ? Number(betMultiplier) : 1;
  const cubeFaceForUi = Math.max(
    2,
    Math.pow(2, Math.ceil(Math.log2(cubeMult)))
  );


const cubeImageMap = {
  2: '/img/doubling-cube-2-top.png',
  4: '/img/doubling-cube-4-top.png',
  8: '/img/doubling-cube-8-top.png',
  16: '/img/doubling-cube-16-top.png',
  32: '/img/doubling-cube-32-top.png',
  64: '/img/doubling-cube-64-top.png',
};

const cubeImageSrc = isCrawford
  ? cubeImageMap[2]
  : (cubeImageMap[cubeFaceForUi] || null);

const crawfordMessage = translate('game.short.crawfordNoDouble', {
  defaultValue: 'В этой партии нельзя удваивать: действует правило Кроуфорда.',
});

const showCrawfordBlockedHint = () => {
  if (crawfordHintTimerRef.current) {
    clearTimeout(crawfordHintTimerRef.current);
  }

  setShowCrawfordHint(true);
  crawfordHintTimerRef.current = setTimeout(() => {
    setShowCrawfordHint(false);
    crawfordHintTimerRef.current = null;
  }, 2800);
};


  const autoHandledRef = useRef(false);
  const autoTimerRef = useRef(null);


  function cubePositionClass() {
  if (doubleOffer) {
    if (doubleOffer.to === username)   return 'cube-my-rail';
    if (doubleOffer.from === username) return 'cube-opponent-rail';
  }
  if (!cubeOwner)             return 'cube-neutral-short'; // центр как и было
  if (cubeOwner === username) return 'cube-my-rail';
  return 'cube-opponent-rail';
}

// function cubePositionClass() {
//   if (!cubeOwner)             return 'cube-neutral-short';
//   if (cubeOwner === username) return 'cube-my-rail-short';
//   return 'cube-opponent-rail-short';
// }

  function DisabledWrap({ children }) {
    return readOnly
      ? <div >{children}</div>
      : <>{children}</>;
  }


const firstBlack  = Array.isArray(displayBoard) && displayBoard.length >= 24 ? displayBoard.slice(0, 12) : [];
const secondBlack = Array.isArray(displayBoard) && displayBoard.length >= 24 ? displayBoard.slice(12).reverse() : [];
const firstWhite  = Array.isArray(displayBoard) && displayBoard.length >= 24 ? displayBoard.slice(12).reverse() : [];
const secondWhite = Array.isArray(displayBoard) && displayBoard.length >= 24 ? displayBoard.slice(0, 12) : [];

  /* ============ Готовность игрока ============ */
  const playerReady = myTournamentColor === 'white'
    ? (readyStatus?.player1 || false)
    : (readyStatus?.player2 || false);

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };










  useEffect(() => {
  // играем звук победы/поражения ровно один раз, когда есть итог
  if (resultSfxPlayedRef.current) return;
  if (!gameResult) return;

  // запускаем при показе модалки или фактическом окончании партии
  if (!(showResultModal || gameFinished)) return;

  const me = localStorage.getItem('username') || '';
  const iWin = Boolean(me && gameResult.winnerUsername && me === gameResult.winnerUsername);

  play(iWin ? 'winner' : 'loser').catch(() => {/* тихо игнорим блокировку автоплея */});
  resultSfxPlayedRef.current = true;
}, [showResultModal, gameFinished, gameResult, play]);









// ⬇️ ДОБАВЬ ОТДЕЛЬНЫЙ useEffect
useEffect(() => {
  // берём ID стола из URL или из LS (как у тебя уже принято)
  const tableId = Number(tableIdFromUrl ?? localStorage.getItem('currentTableId'));
  if (!Number.isFinite(tableId)) return;

  // 1) гарантированно подставим токен и подключимся (ничего не ломает)
  ensureSocketAuthAndConnect();

  // 2) локальная связь: показываем/скрываем баннер «Нет связи»
  const onConnect = () => {
    setIsSocketUp(true);
    // сразу же перевходим и просим снапшот
    try { rejoinAndResync(tableId); } catch {}
  };
  const onDisconnect = () => setIsSocketUp(false);



 const onPresence = (payload) => {
  try {
    if (!payload) return;
    const currTableId = Number(tableIdFromUrl ?? localStorage.getItem('currentTableId'));
    if (Number(payload.tableId) !== currTableId) return;
    if (!oppRole) return; // ждём, пока определится моя роль

    const online = !!payload.presence?.[oppRole]?.online;

    // если оппонента ещё нет за столом — не показываем баннер
    if (opponentSeated) {
      setOppOnline(online);
    }
  } catch {}
};




  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  socket.on('playerPresenceChanged', onPresence);

  // если уже подключены — вручную дёрнем onConnect (на случай, когда эффект повесился позднее)
  if (socket.connected) onConnect();

  return () => {
    socket.off('connect', onConnect);
    socket.off('disconnect', onDisconnect);
    socket.off('playerPresenceChanged', onPresence);
  };
}, [tableIdFromUrl, oppRole, opponentSeated]);
// ⬆️ ДОБАВЛЕНО




useEffect(() => {
  const onGnuReady = (payload) => {
    try {
      // на всякий случай фильтруем по текущему столу
      const currTableId = Number(tableIdFromUrl ?? localStorage.getItem('currentTableId'));
      if (Number.isFinite(currTableId) && Number(payload?.tableId) !== currTableId) return;

      // payload.pr — то, что ты добавишь на сервере
      if (payload?.pr) {
        setGnuStats(payload.pr);
      }
    } catch (e) {
      debugWarn('[GNU PR] parse payload error', e);
    }
  };

  socket.on('short_game_analysis_ready', onGnuReady);
  return () => socket.off('short_game_analysis_ready', onGnuReady);
}, [tableIdFromUrl]);



useEffect(() => {
  debugLog('[SHORT/MOUNT]', {
    tableId: tableIdFromUrl,
    username,
    playerColor,
    opponentColor,
    myTournamentColor: (playerColor === 'white' || playerColor === 'black') ? playerColor : 'white',
    tournamentPlayer1Name,
    tournamentPlayer2Name,
    player1Avatar,
    player2Avatar,
    player1Rating,
    player2Rating,
  });
}, []); 


useEffect(() => {
  const bothWhite = playerColor === 'white' && (opponentColor === 'white' || !opponentColor);
  debugLog('[SHORT/COLOR-UPDATE]', {
    playerColor, opponentColor, bothWhite,
    ts: Date.now()
  });
  if (bothWhite) debugWarn('[!!!] ОБА БЕЛЫЕ — фиксирую кадр');
}, [playerColor, opponentColor]);


useEffect(() => {
  const noP1Name = !String(tournamentPlayer1Name || '').trim();
  const noP2Name = !String(tournamentPlayer2Name || '').trim();
  const defAvaP1 = !player1Avatar;
  const defAvaP2 = !player2Avatar;

  debugLog('[SHORT/IDENTITY-UPDATE]', {
    tournamentPlayer1Name, tournamentPlayer2Name,
    player1Avatar, player2Avatar,
    noP1Name, noP2Name, defAvaP1, defAvaP2
  });

  if ((noP1Name || noP2Name) || (defAvaP1 || defAvaP2)) {
    debugWarn('[!!!] ОТСУТСТВУЮТ ИМЕНА/АВЫ при текущих цветах:', { playerColor, opponentColor });
  }
}, [tournamentPlayer1Name, tournamentPlayer2Name, player1Avatar, player2Avatar, playerColor, opponentColor]);


useEffect(() => {
  if (Array.isArray(board) && board.length >= 24) {
    debugLog('[SHORT/BOARD-READY]', { len: board.length, time: Date.now(), playerColor, opponentColor });
  }
}, [board]);




useEffect(() => {
  debugLog('[SHORT/TURN]', {
    currentTurn,
    myIsWhite: playerColor === 'white',
    myTurnExpected: currentTurn === (playerColor === 'white' ? 'player1' : 'player2'),
    hasRolledDice, firstMoveComplete
  });
}, [currentTurn, playerColor, hasRolledDice, firstMoveComplete]);





useEffect(() => {
  const mySideLocal = (playerColor === 'white' ? 'white' : 'black');

  const localAva = (() => {
    const raw = localStorage.getItem('avatar');
    return raw ? String(raw).replace(/\\/g, '/') : null;
  })();

  const railWhite = player1Avatar || (mySideLocal === 'white' ? localAva : null);
  const railBlack = player2Avatar || (mySideLocal === 'black' ? localAva : null);

  debugLog('[SHORT/RAILS]', {
    railWhite,
    railBlack,
    nameWhite: getDisplayName('white'),
    nameBlack: getDisplayName('black'),
    playerColor,
    opponentColor,
  });
}, [
  player1Avatar,
  player2Avatar,
  playerColor,
  opponentColor,
  tournamentPlayer1Name,
  tournamentPlayer2Name,
  username, // если getDisplayName зависит от него
]);



  /* ============ Ставка / матч ============ */
  const baseStake = React.useMemo(() => {
    const id = tableIdFromUrl ?? localStorage.getItem('currentTableId');
    if (!id) return 1;
    const raw = localStorage.getItem(`stake:short:${id}`) || localStorage.getItem(`stake:long:${id}`);
    if (!raw) return 1;
    try {
      const obj = JSON.parse(raw);
      return Number(obj.base) > 0 ? Number(obj.base) : 1;
    } catch {
      return 1;
    }
  }, [tableIdFromUrl]);

  const totalStake = baseStake * cubeMult;
  const isMatch = Number(targetPoints) > 1;
  const uiMoneyStake = isMatch ? baseStake : totalStake;

  const mTarget =
    Number.isFinite(Number(targetPoints)) && Number(targetPoints) > 0
      ? Number(targetPoints)
      : 1;
  const fallbackS1 = Number.isFinite(Number(scoreP1)) ? Number(scoreP1) : 0;
const fallbackS2 = Number.isFinite(Number(scoreP2)) ? Number(scoreP2) : 0;

const selectedMatchGame =
  Array.isArray(matchGames) && matchGames.length > 0
    ? matchGames[Math.max(0, Math.min(matchGames.length - 1, Number(selectedMatchGameIndex) || 0))]
    : null;

const headerScoreP1 =
  selectedMatchGame?.scoreAfter?.player1 != null
    ? Number(selectedMatchGame.scoreAfter.player1)
    : fallbackS1;

const headerScoreP2 =
  selectedMatchGame?.scoreAfter?.player2 != null
    ? Number(selectedMatchGame.scoreAfter.player2)
    : fallbackS2;

const s1 = Number.isFinite(headerScoreP1) ? headerScoreP1 : 0;
const s2 = Number.isFinite(headerScoreP2) ? headerScoreP2 : 0;

  const money = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;

  /* ============ Dice images ============ */
  const diceImages = {
    white: {
      1: '/img/k1-white-1.png',
      2: '/img/k1-white-2.png',
      3: '/img/k1-white-3.png',
      4: '/img/k1-white-4.png',
      5: '/img/k1-white-5.png',
      6: '/img/k1-white-6.png',
    },
    black: {
      1: '/img/k1-black-1.png',
      2: '/img/k1-black-2.png',
      3: '/img/k1-black-3.png',
      4: '/img/k1-black-4.png',
      5: '/img/k1-black-5.png',
      6: '/img/k1-black-6.png',
    },
  };


// ===== helper: нарисовать кубики =====
const renderDiceTray = (diceArr, color) => {
  if (!Array.isArray(diceArr) || diceArr.length === 0) return null;

  const c = (color === 'black' ? 'black' : 'white'); // защита от null

  return (
    <div className="dice-tray">
      {diceArr.map((d, i) => (
        <img
          key={`${c}-${d}-${i}`}
          className="dice-img"
          src={diceImages[c]?.[d]}
          alt={`dice-${d}`}
          draggable={false}
        />
      ))}
    </div>
  );
};



const firstP1 = firstRollsVisible.p1;
const firstP2 = firstRollsVisible.p2;

 

// ход сейчас мой, только если у меня вообще есть сторона
const myTurn = !!mySide &&
  currentTurn === (mySide === 'white' ? 'player1' : 'player2');



  // можно ли сейчас вообще трогать шашку (мышь/тач)
const canDragChecker = (checkerColor) => {
  // мой цвет, который выдал сервер
  const myColorNow =
    playerColor === 'white' || playerColor === 'black' ? playerColor : null;

  // если это не мой ход, я наблюдатель или партия уже закончена — сразу запрет
  if (!myTurn || readOnly || gameFinished) return false;
  if (!myColorNow) return false;

  // если нам передали цвет шашки — разрешаем только свои
  if (checkerColor && checkerColor !== myColorNow) return false;

  return true;
};

// те же условия, при которых у тебя вообще показывается кнопка "Бросить кубики"
const canShowRollBtn =
  !gameFinished &&
  firstMoveComplete &&
  myTurn &&
  !hasRolledDice &&
  !confirmInProgress;

  
useEffect(() => {
  // если автРолл включён и кнопка только что "стала активной" → жмём автоматически
 if (autoRoll && canShowRollBtn && !prevCanShowRef.current && !(showDoubleDialog && doubleOffer)) {
  setTimeout(() => {
    try { handleRollDiceClickSfx(); } catch {}
  }, 30);
}

  prevCanShowRef.current = canShowRollBtn;
}, [autoRoll, canShowRollBtn, handleRollDiceClickSfx]);


const mobileTapLockRef = React.useRef(0);

const runMobileTap = React.useCallback((fn) => (e) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  const now = Date.now();

  // защита от двойного вызова:
  // сначала onTouchEnd, потом браузер может ещё дать onClick
  if (now - mobileTapLockRef.current < 350) {
    return;
  }

  mobileTapLockRef.current = now;
  fn?.();
}, []);



// === ЖЁСТКИЙ СБРОС КУБИКОВ/ЗВУКОВ ПРИ НОВОМ СТОЛЕ ИЛИ СИГНАЛЕ ИЗ ЛОББИ ===
useEffect(() => {
  const resetVisual = () => {
    try {
      setRolledDice([]);                  // убрать текущие кубики
    } catch {}
    resultSfxPlayedRef.current = false;   // разрешить заново проиграть win/lose
    goodluckPlayedRef.current = false;    // не считать, что goodluck уже был
    suppressDiceUntilRef.current = 0;
    suppressDoubleUntilRef.current = 0;
  };

  // 1) при монтировании/смене стола
  resetVisual();

  // 2) по внешнему сигналу из Лобби
  window.addEventListener('short:reset-dice', resetVisual);
  return () => window.removeEventListener('short:reset-dice', resetVisual);
}, [setRolledDice, tableIdFromUrl]);




// === АВТО-ПОДТВЕРЖДЕНИЕ ХОДА ===
 const canShowOkBtn =
   !gameFinished &&
   myTurn &&
   hasRolledDice &&
   numberMove === 0;

 const prevCanOkRef = React.useRef(false);
 useEffect(() => {
   if (!readOnly && autoConfirm && canShowOkBtn && !prevCanOkRef.current) {
     const t = setTimeout(() => { try { handleConfirmMoveSfx(); } catch {} }, 120);
     return () => clearTimeout(t);
   }
   prevCanOkRef.current = canShowOkBtn;
 }, [readOnly, autoConfirm, canShowOkBtn, handleConfirmMoveSfx]);

  function Avatar({
  src,
  alt = "",
  size = 96,
  textFallback = "BQ",
  className = "",
}) {
  // const API_URL =
  //   import.meta?.env?.VITE_API_URL || process.env.REACT_APP_API_URL;
  // const SOCKET_URL =
  //   import.meta?.env?.VITE_SOCKET_URL || process.env.REACT_APP_SOCKET_URL;
const API_URL = process.env.REACT_APP_API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

  // вычисляем origin бекенда
  const BASE = React.useMemo(() => {
    const candidates = [API_URL, SOCKET_URL, window.location.origin].filter(Boolean);
    for (const u of candidates) {
      try {
        return new URL(u, window.location.origin).origin;
      } catch (_) {}
    }
    return window.location.origin;
  }, [API_URL, SOCKET_URL]);

  const fixedSrc = React.useMemo(() => {
    if (!src) return null;
    let x = String(src).trim().replace(/\\/g, "/");

    // если не абсолютный URL, превращаем в абсолютный
    if (!/^https?:\/\//i.test(x)) {
      x = x.startsWith("/") ? x : `/${x}`;
      x = `${BASE}${x}`;
    }

    // заменяем localhost/127.0.0.1 на боевой домен
    x = x.replace(/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i, BASE);

    // поднимаем до https при необходимости
    if (window.location.protocol === "https:" && x.startsWith("http://")) {
      x = x.replace(/^http:\/\//i, "https://");
    }

    return x;
  }, [src, BASE]);

  const [ok, setOk] = React.useState(Boolean(fixedSrc));
  React.useEffect(() => setOk(Boolean(fixedSrc)), [fixedSrc]);

  if (ok) {
    return (
      <img
        className={`rail-ava ${className}`}
        src={fixedSrc}
        alt={alt}
        onError={() => setOk(false)}
     
      />
    );
  }

  // фолбэк, если картинка не загрузилась
  return (
    <div
      className={`rail-ava rail-ava--fallback ${className}`}
  
      aria-label={alt}
      role="img"
    >
      {textFallback}
    </div>
  );
}



  const isP1Turn = currentTurn === 'player1';
  const isP2Turn = currentTurn === 'player2';
  // const mySide = (playerColor === 'white' ? 'white' : 'black');



const selectedTurn = (isReplayMode && selectedMoveNum > 0)
  ? analysisTurns?.[selectedMoveNum - 1]
  : null;

const selectedMoverColor =
  (isReplayMode && selectedMoveNum > 0)
    ? getMoverColorForMove(selectedMoveNum, selectedTurn)
    : 'white';


const bestUiTextSelected =
  (isReplayMode && selectedMoveNum > 0 && gnuByMove[selectedMoveNum]?.bestMovesText)
    ? convertGnuBestMovesTextForUi(gnuByMove[selectedMoveNum].bestMovesText, selectedMoverColor)
    : '';





// 👇 Подхватываем вашу роль/цвет, если их ещё не выставили
// 👇 Подхватываем вашу роль/цвет, если их ещё не выставили
useEffect(() => {
  // если мы зритель (или readOnly), то гарантированно НЕ считаемся игроком
  // и обнуляем локальные цвета, которые могли остаться от прошлой партии
  if (isSpectator || readOnly) {
    if (playerColor === 'white' || playerColor === 'black') {
      setPlayerColor(null);
   
    }
    return;
  }

  // если цвет уже есть — ничего не делаем
  if (playerColor === 'white' || playerColor === 'black') return;

  try {
    const raw = localStorage.getItem('short:youAre');
    if (!raw) return;

    const saved = JSON.parse(raw);

    // safety: сверим стол
    const urlId   = Number(tableIdFromUrl ?? localStorage.getItem('currentTableId'));
    const savedId = Number(saved?.tableId);
    if (!Number.isFinite(urlId) || urlId !== savedId) return;

    const clr = saved?.color;
    if (clr === 'white' || clr === 'black') {
      setPlayerColor(clr);
     
      // localStorage.removeItem('short:youAre'); // если захочешь чистить
    }
  } catch { /* no-op */ }
}, [isSpectator, readOnly, tableIdFromUrl, playerColor, setPlayerColor, ]);






useEffect(() => {
  debugLog('[SHORT/COLOR]', { playerColor, opponentColor, myTournamentColor, opponentTournamentColor });
}, [playerColor, opponentColor, myTournamentColor, opponentTournamentColor]);



const allTurnsRows = Array.isArray(uiRows) ? uiRows : [];
const canToggleTurnsExpand = allTurnsRows.length > COLLAPSED_TURNS_ROWS;

const turnsRowsForRender = isTurnsExpanded
  ? allTurnsRows
  : allTurnsRows.slice(0, COLLAPSED_TURNS_ROWS);



  const myAvatarSrc = React.useMemo(() => {
    const raw = localStorage.getItem('avatar');
    if (!raw) return null;
    return String(raw).replace(/\\/g, '/');
  }, []);

  // const railSrc = {
  //   white: player1Avatar || (mySide === 'white' ? myAvatarSrc : null),
  //   black: player2Avatar || (mySide === 'black' ? myAvatarSrc : null),
  // };
const railSrc = {
  white: player1Avatar || (mySide === 'white' ? myAvatarSrc : null),
  black: player2Avatar || (mySide === 'black' ? myAvatarSrc : null),
};



  // данные для мобильного HUD
const whiteUi = {
  name: getDisplayName('white'),
  avatar: railSrc.white,
  rating: typeof player1Rating === 'number' ? player1Rating : '—',
};

const blackUi = {
  name: getDisplayName('black'),
  avatar: railSrc.black,
  rating: typeof player2Rating === 'number' ? player2Rating : '—',
};

/*
 * В телефонном horizontal свой игрок ВСЕГДА снизу,
 * оппонент ВСЕГДА сверху независимо от цвета шашек.
 * В portrait / desktop сохраняем старый порядок white -> black.
 */
const landscapeTopUi =
  cashPhoneLandscapeMode && myTournamentColor === 'white'
    ? blackUi
    : whiteUi;

const landscapeBottomUi =
  cashPhoneLandscapeMode && myTournamentColor === 'white'
    ? whiteUi
    : blackUi;

const landscapeTopTimeRef =
  cashPhoneLandscapeMode && myTournamentColor === 'white'
    ? mobilePlayer2TimeRef
    : mobilePlayer1TimeRef;

const landscapeBottomTimeRef =
  cashPhoneLandscapeMode && myTournamentColor === 'white'
    ? mobilePlayer1TimeRef
    : mobilePlayer2TimeRef;









useEffect(() => {
  debugLog('[NAMES/CHECK]', {
    mySide,
    username,
    opponentName,
    opponentNameLS,
    tP1: player1Name,
    tP2: player2Name,
    whiteShown: getDisplayName('white'),
    blackShown: getDisplayName('black'),
  });
}, [mySide, username, opponentName, opponentNameLS, tournamentPlayer1Name, tournamentPlayer2Name]);



  // чей сейчас ход — для доступности куба
  // const isMyTurn =
  //   currentTurn === (playerColor === 'white' ? 'player1' : 'player2');
// чей сейчас ход — используем уже посчитанный myTurn
const isMyTurn = myTurn;

// Порталы нужны, чтобы модалки реально перекрывали ВСЮ страницу,
// а не только скейлящийся контейнер (иначе при transform/scale fixed «ломается»).
const canUsePortal = typeof document !== 'undefined';
const maybePortal = (node) => (canUsePortal ? createPortal(node, document.body) : node);


const boardSettingsDialog = showBoardSettings
  ? maybePortal(
      <div
        className="short-board-settings-overlay"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeBoardSettings();
        }}
      >
        <section
          className="short-board-settings-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="short-board-settings-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className="short-board-settings-header">
            <div>
              <p className="short-board-settings-eyebrow">
                {translate('game.short.boardTheme', { defaultValue: 'Тема доски' })}
              </p>
              <h2 id="short-board-settings-title">
                {translate('game.short.chooseBoard', { defaultValue: 'Выберите дизайн доски' })}
              </h2>
            </div>

            <button
              type="button"
              className="short-board-settings-close"
              onClick={closeBoardSettings}
              aria-label={translate('game.common.close', { defaultValue: 'Закрыть' })}
            >
              <span aria-hidden="true">×</span>
            </button>
          </header>

          <div className="short-board-skin-grid">
            {SHORT_BOARD_SKIN_OPTIONS.map((option) => {
              const isSelected = shortBoardSkin === option.id;
              const label = translate(option.labelKey, {
                defaultValue: option.defaultLabel,
              });

              return (
                <button
                  key={option.id}
                  type="button"
                  className={`short-board-skin-card ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => selectShortBoardSkin(option.id)}
                  aria-pressed={isSelected}
                >
                  <span className="short-board-skin-preview">
                    <img src={option.src} alt="" draggable="false" />
                    {isSelected && (
                      <span className="short-board-skin-check" aria-hidden="true">
                        <CheckIcon />
                      </span>
                    )}
                  </span>
                  <span className="short-board-skin-name">{label}</span>
                </button>
              );
            })}
          </div>

          <p className="short-board-settings-note">
            {translate('game.short.boardSavedAutomatically', {
              defaultValue: 'Выбранная доска сохранится на этом устройстве.',
            })}
          </p>
        </section>
      </div>
    )
  : null;


const showShortBoardQuickControls = Boolean(
  !showResultModal &&
  !gameFinished
);

/*
 * На мобильном панель должна находиться сразу под видимой доской,
 * а не у нижнего края viewport. boardRef.getBoundingClientRect()
 * уже учитывает transform/scale, поэтому позиция остаётся правильной
 * на разных iPhone, Android и при изменении масштаба.
 */
const [shortBoardQuickTop, setShortBoardQuickTop] = useState(null);

useLayoutEffect(() => {
  if (!showShortBoardQuickControls || typeof window === 'undefined') {
    setShortBoardQuickTop(null);
    return undefined;
  }

  let rafId = 0;
  let secondRafId = 0;
  let resizeObserver = null;

  const updateQuickControlsPosition = () => {
    const boardNode = boardRef.current;
    if (!boardNode) return;

    const rect = boardNode.getBoundingClientRect();
    if (!Number.isFinite(rect.bottom) || rect.width <= 0 || rect.height <= 0) {
      return;
    }

    const compactPhone = window.innerWidth <= 480;
    const dockHeight = compactPhone ? 67 : 76;
    const gapAfterBoard = compactPhone ? 7 : 10;
    const viewportPadding = 8;

    const desiredTop = rect.bottom + gapAfterBoard;
    const highestAllowedTop =
      window.innerHeight - dockHeight - viewportPadding;

    const nextTop = Math.max(
      viewportPadding,
      Math.min(desiredTop, highestAllowedTop)
    );

    setShortBoardQuickTop((previousTop) => {
      const roundedTop = Math.round(nextTop);
      return previousTop === roundedTop ? previousTop : roundedTop;
    });
  };

  const scheduleUpdate = () => {
    cancelAnimationFrame(rafId);
    cancelAnimationFrame(secondRafId);

    rafId = requestAnimationFrame(() => {
      updateQuickControlsPosition();

      // Второй кадр нужен после применения scale к мобильной доске.
      secondRafId = requestAnimationFrame(updateQuickControlsPosition);
    });
  };

  scheduleUpdate();

  if (typeof ResizeObserver !== 'undefined' && boardRef.current) {
    resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(boardRef.current);
  }

  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('orientationchange', scheduleUpdate);

  return () => {
    cancelAnimationFrame(rafId);
    cancelAnimationFrame(secondRafId);
    resizeObserver?.disconnect();
    window.removeEventListener('resize', scheduleUpdate);
    window.removeEventListener('orientationchange', scheduleUpdate);
  };
}, [
  showShortBoardQuickControls,
  boardScale,
  isMobile,
  isMobileWidth,
  isPortrait,
  forceLandscape,
]);

const shortLiveBarActionButton = (() => {
  if (!shortLiveGame) return null;

  const baseStyle = {
    width: 28,
    height: 28,
    minWidth: 28,
    minHeight: 28,
    padding: 0,
    margin: 0,
    borderRadius: 6,
    border: '1px solid rgba(228,184,91,0.38)',
    background: 'rgba(14,14,14,0.96)',
    color: '#ffd7d7',
    display: 'grid',
    placeItems: 'center',
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1,
    cursor: 'pointer',
    boxSizing: 'border-box',
  };

  if (iAmPureSpectator || !iAmPlayer || canShowCloseTableBtn) {
    const title = iAmPureSpectator
      ? translate('game.short.exitSpectator')
      : canShowCloseTableBtn
      ? translate('game.short.closeTable')
      : translate('game.common.exit');

    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleLeaveTableClean();
        }}
        title={title}
        aria-label={title}
        style={baseStyle}
      >
        ←
      </button>
    );
  }

  if (canShowResignBtn) {
    const title = translate('game.common.surrender');

    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setShowResignDialog(true);
        }}
        title={title}
        aria-label={title}
        style={{ ...baseStyle, color: '#ffb7b7' }}
      >
        ⚑
      </button>
    );
  }

  return null;
})();

const shortBoardQuickControlButtons = (
  <>
    {!isSpectator && (
      <>
        <button
          type="button"
          className={`short-board-control-btn ${autoRoll ? 'is-active' : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            toggleAutoRoll?.();
          }}
          disabled={readOnly || typeof toggleAutoRoll !== 'function'}
          aria-pressed={Boolean(autoRoll)}
          aria-label={translate('game.common.autoRoll', { defaultValue: 'Автобросок' })}
          title={translate('game.common.autoRoll', { defaultValue: 'Автобросок' })}
        >
          <span className="short-board-control-icon"><AutoRollIcon /></span>
          <span className="short-board-control-text">
            {translate('game.common.autoRoll', { defaultValue: 'Автобросок' })}
          </span>
          <span className="short-board-control-indicator" aria-hidden="true" />
        </button>

        <button
          type="button"
          className={`short-board-control-btn ${autoConfirm ? 'is-active' : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            toggleAutoConfirm?.();
          }}
          disabled={readOnly || typeof toggleAutoConfirm !== 'function'}
          aria-pressed={Boolean(autoConfirm)}
          aria-label={translate('game.common.autoMove', { defaultValue: 'Автоход' })}
          title={translate('game.common.autoMove', { defaultValue: 'Автоход' })}
        >
          <span className="short-board-control-icon"><AutoMoveIcon /></span>
          <span className="short-board-control-text">
            {translate('game.common.autoMove', { defaultValue: 'Автоход' })}
          </span>
          <span className="short-board-control-indicator" aria-hidden="true" />
        </button>
      </>
    )}

    <button
      type="button"
      className="short-board-control-btn short-board-control-btn--settings"
      onClick={(event) => {
        event.stopPropagation();
        setShowBoardSettings(true);
      }}
      aria-label={translate('game.short.boardSettings', { defaultValue: 'Выбрать дизайн доски' })}
      title={translate('game.short.boardSettings', { defaultValue: 'Выбрать дизайн доски' })}
    >
      <span className="short-board-control-icon"><SettingsIcon /></span>
      <span className="short-board-control-text">
        {translate('game.common.settings', { defaultValue: 'Настройки' })}
      </span>
    </button>
  </>
);

/*
 * ВАЖНО:
 * Панель всегда рендерится через portal в document.body.
 * Нельзя помещать её внутрь масштабируемого mobile-контейнера:
 * position: fixed внутри родителя с transform становится привязанным
 * к этому родителю, масштабируется и уезжает за видимую область.
 */
const shortBoardQuickControls =
  showShortBoardQuickControls && !shortLiveGame
  ? maybePortal(
      <div
        className="short-board-control-dock short-board-control-dock--portal short-board-control-dock--near-board"
        style={{
          '--short-board-quick-top': `${shortBoardQuickTop ?? 0}px`,
          visibility: shortBoardQuickTop == null ? 'hidden' : 'visible',
        }}
        role="group"
        aria-label={translate('game.short.quickSettings', { defaultValue: 'Быстрые настройки игры' })}
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        onDragStart={(event) => event.preventDefault()}
      >
        {shortBoardQuickControlButtons}
      </div>
    )
  : null;

  const isCubeEnabled = Boolean(
    !hideCube &&
    !isCrawford &&
    firstMoveComplete &&
    !hasRolledDice &&
    isMyTurn &&
    canOfferDouble &&
    !gameFinished
  );

  /* ============ Double dialog ============ */
  const doubleDialog =
    showDoubleDialog && doubleOffer
      ? maybePortal(
          <div className="double-mask" role="dialog" aria-modal="true" aria-label={translate('game.short.doubleTitle')}>
            <div className="double-dialog">
              <p>
                <strong>{doubleOffer.from}</strong> {translate('game.short.offersIncreaseStake')}
                <b>×{doubleOffer.proposedMultiplier}</b>
              </p>

              <div className="btn-row">
                <button className="btn-take" onClick={onAcceptDouble}>{translate('game.short.take')}</button>
                <button className="btn-pass" onClick={handleDeclineDoubleClickSfx}>{translate('game.short.pass')}</button>
              </div>
            </div>
          </div>
        )
      : null;



  /* ============ Безопасные имена в модалке результата ============ */
  const winnerNameRaw = gameResult?.winnerUsername && typeof gameResult.winnerUsername === 'string'
    ? gameResult.winnerUsername.trim()
    : '';
  const loserNameRaw = gameResult?.loserUsername && typeof gameResult.loserUsername === 'string'
    ? gameResult.loserUsername.trim()
    : '';

  let modalWinnerName = winnerNameRaw || tournamentPlayer1Name || translate('game.common.winner');
  let modalLoserName =
    loserNameRaw
    || ([tournamentPlayer1Name, tournamentPlayer2Name].filter(Boolean).find(n => n !== modalWinnerName))
    || opponentName
    || translate('game.common.loser');

  if (modalWinnerName === modalLoserName) {
    const candidates = [tournamentPlayer1Name, tournamentPlayer2Name, opponentName].filter(Boolean);
    const other = candidates.find(n => n !== modalWinnerName);
    if (other) modalLoserName = other;
  }

  /* ============ Данные для новой модалки результата (в стиле «Winner / Loser») ============ */
  const _normName = (s) => String(s || '').trim().toLowerCase();

  const _sideByName = (n) => {
    const nn = _normName(n);
    if (!nn) return null;
    if (nn === _normName(whiteUi?.name)) return 'white';
    if (nn === _normName(blackUi?.name)) return 'black';
    return null;
  };

  const winnerShownName = (gameResult?.winnerUsername || modalWinnerName || '').trim();
  const loserShownName  = (gameResult?.loserUsername  || modalLoserName  || '').trim();

  const winnerSide = _sideByName(winnerShownName);
  const loserSide  = _sideByName(loserShownName) || (winnerSide ? (winnerSide === 'white' ? 'black' : 'white') : null);

  // аватарки: сперва из gameResult, затем фолбэк из рельс
  const winnerAvatarSrc = gameResult?.winnerAvatar
    || (winnerSide === 'white' ? railSrc?.white : winnerSide === 'black' ? railSrc?.black : null);
  const loserAvatarSrc = gameResult?.loserAvatar
    || (loserSide === 'white' ? railSrc?.white : loserSide === 'black' ? railSrc?.black : null);

  // PR приходят позже (после загрузки GNU). Показываем «—», пока нет.
  const prSource = gnuAnalysis?.pr || gnuStats;

  // поддержка двух форматов:
  // 1) pr = { chequer, cube, luck, overall } — общий PR (без разбивки по сторонам)
  // 2) pr = { white:{...}, black:{...} } — PR отдельно для белых/черных
  const prIsSplit = Boolean(prSource && (prSource.white || prSource.black));

  // ✅ Эти данные появляются чуть позже, когда GNU догрузится.
  // Поэтому первые секунды prWinner/prLoser могут быть null — в UI покажем «—».
  const prWinner = prIsSplit && winnerSide ? prSource?.[winnerSide] : (!prIsSplit ? prSource : null);
  const prLoser  = prIsSplit && loserSide  ? prSource?.[loserSide]  : (!prIsSplit ? prSource : null);

  const winnerPrOverall = prWinner?.overall ?? null;
  const loserPrOverall  = prLoser?.overall  ?? null;

  const levelByPr = (overall) => {
    const n = Number(overall);
    if (!Number.isFinite(n)) return '—';
    // чем меньше PR, тем лучше
    if (n <= 2.5) return 'Grand Master';
    if (n <= 5.0) return 'Master';
    if (n <= 8.0) return 'Advanced';
    if (n <= 12.0) return 'Intermediate';
    return 'Beginner';
  };

  const fmtPr = (v) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n.toFixed(2);
  };

  const fmtMoney = (v) => {
    if (v == null || v === '') return '—';
    // Балансы/ставки приходят уже готовыми значениями — не ломаем формат.
    return `$${v}`;
  };

  const fmtSignedMoney = (v, sign) => {
    if (v == null || v === '') return '—';
    const s = Number(v);
    if (!Number.isFinite(s)) return `${sign}$${v}`;
    const abs = Math.abs(s);
    return `${sign}$${abs}`;
  };

  // Счёт в центре:
  // - если счёт матча уже обновлён (не 0–0), показываем его
  // - если всё ещё 0–0 (часто так бывает в момент показа модалки), показываем 1–0 по факту победителя
  const sP1 = Number(scoreP1);
  const sP2 = Number(scoreP2);
  const winnerIsBlack = winnerSide === 'black' || (winnerSide == null && loserSide === 'white');

  const hasScore = Number.isFinite(sP1) && Number.isFinite(sP2) && (sP1 !== 0 || sP2 !== 0);
  const rawP1 = hasScore ? sP1 : (winnerIsBlack ? 0 : 1);
  const rawP2 = hasScore ? sP2 : (winnerIsBlack ? 1 : 0);

  const winnerScore = winnerIsBlack ? rawP2 : rawP1;
  const loserScore  = winnerIsBlack ? rawP1 : rawP2;

  const loserLossAmount = gameResult?.stake ?? gameResult?.finalStake ?? gameResult?.winnerAmount;






const isMeWhite = mySide === 'white';
const isMeBlack = mySide === 'black';
const isSpectatorDesktop = !isMeWhite && !isMeBlack;

const topUi = isMeWhite ? blackUi : whiteUi;
const bottomUi = isMeWhite ? whiteUi : blackUi;

const topTurnActive = isMeWhite ? isP2Turn : isP1Turn;
const bottomTurnActive = isMeWhite ? isP1Turn : isP2Turn;

const topTimeRef = isMeWhite ? player2TimeRef : player1TimeRef;
const bottomTimeRef = isMeWhite ? player1TimeRef : player2TimeRef;






const isCoarsePointer = useMediaQuery(
  "(hover: none) and (pointer: coarse)"
);

const isNarrow = useMediaQuery("(max-width: 900px)");

const isResultModalMobile =
  isMobile ||
  isNarrow ||
  isCoarsePointer ||
  isTouchDevice;

/*
 * Цвет шашки может прийти с сервера/из снимка в разных форматах:
 *   { color: 'white' }, { player: 'player2' }, 'black', 1, 2 и т.д.
 * Нельзя подставлять цвет текущего игрока как fallback — иначе у белого
 * клиента все неопознанные шашки становятся белыми, а у чёрного — чёрными.
 */
const resolveCheckerColor = (checker) => {
  if (checker?.isWhite === true) return 'white';
  if (checker?.isBlack === true) return 'black';

  const rawColor =
    (typeof checker === 'string' || typeof checker === 'number')
      ? checker
      : checker?.color ??
        checker?.colour ??
        checker?.checkerColor ??
        checker?.playerColor ??
        checker?.side ??
        checker?.player ??
        checker?.owner ??
        checker?.team;

  const value = String(rawColor ?? '').trim().toLowerCase();

  if (
    value === 'white' ||
    value === 'w' ||
    value === 'player1' ||
    value === '1' ||
    value.includes('white') ||
    value.includes('player1')
  ) {
    return 'white';
  }

  if (
    value === 'black' ||
    value === 'b' ||
    value === 'player2' ||
    value === '2' ||
    value.includes('black') ||
    value.includes('player2')
  ) {
    return 'black';
  }

  // Страховка: не маскируем ошибку цветом стороны игрока.
  // В консоли будет видно реальный неизвестный формат шашки.
  console.warn('[BoardDisplay] Не удалось определить цвет шашки:', checker);
  return 'white';
};

  /* ============ Отрисовка пойнтов ============ */
  const renderTournamentPoints = () => {
    if (myTournamentColor === 'white') {
      return (
        <>
          <div className="top-row">
            {(firstWhite || []).map((point, index) => (
              <div
                key={index}
                className={`point-container p${index}`}
                onClick={() => onPointClickSfxGuarded(24 - index - 1, 24 - index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropSfx(e, 24 - index - 1)}
              >
                <span>{24 - index}</span>
                {(() => {
                  const checkers = point || [];
                  const visible = checkers.slice(0, 5);
                  const total = checkers.length;
                  return visible.map((checker, i) => {
                    const checkerColor = resolveCheckerColor(checker);
                    const showCount = i === visible.length - 1 && total > 5;
                    return (
                <div
  key={i}
  className={`checker ${checkerColor} ${showCount ? 'checker--stack-count' : ''}`}
  draggable="true"
  onDragStart={(e) => handleDragStartSfx(e, 24 - index - 1, i)}
  onTouchStart={(e) =>
  handleTouchStart(e, 24 - index - 1, i, checkerColor)
}
onTouchEnd={(e) =>
  handleCheckerTapMove(e, 24 - index - 1, 24 - index, checkerColor)
}
>
                        {showCount && (
                          <span className="short-stack-count" style={{
                            position: 'absolute', color: 'white', fontWeight: 'bold', fontSize: '16px',
                            pointerEvents: 'none', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'
                          }}>
                            {total}
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            ))}
          </div>


    


          <div className="bottom-row">
            {(secondWhite || []).map((point, index) => (
              <div
                key={index}
                className={`point-containerd r${index}`}
                onClick={() => onPointClickSfxGuarded(index, index + 1)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropSfx(e, index)}
              >
                {(() => {
                  const checkers = point || [];
                  const visible = checkers.slice(0, 5);
                  const total = checkers.length;
                  return visible.map((checker, i) => {
                    const checkerColor = resolveCheckerColor(checker);
                    const showCount = i === 0 && total > 5;
                    return (
   <div
  key={i}
  className={`checker ${checkerColor} ${showCount ? 'checker--stack-count' : ''}`}
  draggable="true"
  onDragStart={(e) => handleDragStartSfx(e, index, i)}
  onTouchStart={(e) =>
  handleTouchStart(e, index, i, checkerColor)
}
onTouchEnd={(e) =>
  handleCheckerTapMove(e, index, index + 1, checkerColor)
}
>
                        {showCount && (
                          <span className="short-stack-count" style={{
                            position: 'absolute', color: 'white', fontWeight: 'bold', fontSize: '16px',
                            pointerEvents: 'none', top: '35%', left: '50%', transform: 'translate(-50%, -50%)'
                          }}>
                            {total}
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
                <span>{index + 1}</span>
              </div>
            ))}
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className="top-row">
            {(firstBlack || []).map((point, index) => (
              <div
                key={index}
                className={`point-container p${index}`}
                onClick={() => onPointClickSfxGuarded(index, index + 1)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropSfx(e, index)}
              >
                <span>{index + 1}</span>
                {(() => {
                  const checkers = point || [];
                  const visible = checkers.slice(0, 5);
                  const total = checkers.length;
                  return visible.map((checker, i) => {
                    const checkerColor = resolveCheckerColor(checker);
                    const showCount = i === visible.length - 1 && total > 5;
                    return (
                <div
  key={i}
  className={`checker ${checkerColor} ${showCount ? 'checker--stack-count' : ''}`}
  draggable="true"
  onDragStart={(e) => handleDragStart(e, index, i)}
 onTouchStart={(e) =>
  handleTouchStart(e, index, i, checkerColor)
}
onTouchEnd={(e) =>
  handleCheckerTapMove(e, index, index + 1, checkerColor)
}
>
                        {showCount && (
                          <span className="short-stack-count" style={{
                            position: 'absolute', color: 'white', fontWeight: 'bold', fontSize: '16px',
                            pointerEvents: 'none', lineHeight: '1',
                            top: '50%', left: '50%', transform: 'translate(-50%, -50%)'
                          }}>
                            {total}
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            ))}
          </div>

          <div className="bottom-row">
            {(secondBlack || []).map((point, index) => (
              <div
                key={index}
                className={`point-containerd r${index}`}
                onClick={() => onPointClickSfxGuarded(24 - index - 1, 24 - index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropSfx(e, 24 - index - 1)}
              >
                {(() => {
                  const checkers = point || [];
                  const visible = checkers.slice(0, 5);
                  const total = checkers.length;
                  return visible.map((checker, i) => {
                    const checkerColor = resolveCheckerColor(checker);
                    const showCount = i === 0 && total > 5;
                    return (
                 <div
  key={i}
  className={`checker ${checkerColor} ${showCount ? 'checker--stack-count' : ''}`}
  draggable="true"
  onDragStart={(e) => handleDragStartSfx(e, 24 - index - 1, i)}
 onTouchStart={(e) =>
  handleTouchStart(e, 24 - index - 1, i, checkerColor)
}
onTouchEnd={(e) =>
  handleCheckerTapMove(e, 24 - index - 1, 24 - index, checkerColor)
}
>
                        {showCount && (
                          <span className="short-stack-count" style={{
                            position: 'absolute', color: 'white', fontWeight: 'bold', fontSize: '16px',
                            pointerEvents: 'none', lineHeight: '1',
                            top: '35%', left: '50%', transform: 'translate(-50%, -50%)'
                          }}>
                            {total}
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
                <span >{24 - index}</span>
              </div>
            ))}
          </div>
        </>
      );
    }
  };




// ✅ ВСЕГДА чистим кубики/первый бросок перед уходом со стола
const resetUiBeforeExit = () => {
  try {
    // убрать кубики и броски
    setRolledDice([]);
    setFirstRollsVisible({ p1: null, p2: null });

    prevOppDiceRef.current = '';
    prevBoardKeyRef.current = '';
    resultSfxPlayedRef.current = false;
    goodluckPlayedRef.current = false;

    window.dispatchEvent(new CustomEvent('short:reset-dice'));

    // ✅ ОЧИЩАЕМ localStorage от стола
    localStorage.removeItem('currentTableId');

    // Можно на всякий случай очистить и сессию, если сохраняется где-то ещё
    sessionStorage.removeItem('currentTableId');

  } catch (e) {
    debugWarn('[resetUiBeforeExit] reset error', e);
  }
};


// обычный выход (как раньше)
const handleLeaveTableClean = () => {
  resetUiBeforeExit();
  handleLeaveTable?.();
};

// ✅ выход ИЗ АНАЛИЗА/ПОСЛЕ ИГРЫ — с логикой бонуса проигравшему
const handleExitFromAnalysis = () => {
  resetUiBeforeExit();

  if (typeof handleCloseResultModal === 'function') {
    // ВАЖНО: это тот самый хендлер из ShortBackgammonBoard.jsx,
    // который делает pending только проигравшему и navigate('/') правильно
    void handleCloseResultModal();
    return;
  }

  // запасной вариант, если вдруг проп не передали
  handleLeaveTable?.();
};







useEffect(() => {
  // если оба игрока НЕ сидят — первого броска быть не должно
  if (!bothPlayersSeated) {
    setFirstRollsVisible({ p1: null, p2: null });
  }
}, [bothPlayersSeated]);



const [hideLeftAnalysis, setHideLeftAnalysis] = useState(() => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 1366;
});

useEffect(() => {
  const handleLeftAnalysisResize = () => {
    setHideLeftAnalysis(window.innerWidth <= 1366);
  };

  handleLeftAnalysisResize();
  window.addEventListener('resize', handleLeftAnalysisResize);

  return () => {
    window.removeEventListener('resize', handleLeftAnalysisResize);
  };
}, []);

const hasAnalysisData =
  (Array.isArray(analysisTurns) && analysisTurns.length > 0) ||
  !!gnuAnalysis ||
  (Array.isArray(matchGames) && matchGames.length > 0);

useEffect(() => {
  let rafId = 0;

  const updateAnalysisViewport = () => {
    cancelAnimationFrame(rafId);

    rafId = requestAnimationFrame(() => {
      const vv = window.visualViewport;
      const vw = Math.round(vv?.width || window.innerWidth || 1366);
      const vh = Math.round(vv?.height || window.innerHeight || 768);

      // <=1366: tablet/compact — доска сверху, анализ под ней.
      // >=1367: desktop — анализ слева, доска справа.
      const stacked = vw <= 1366;

      const horizontalPadding = vw <= 767 ? 12 : 24;
      const sideReserve = !stacked && vw <= 1366 ? 263 : 0;
      const scaleX = (vw - horizontalPadding - sideReserve) / 1020;

      const boardWrapper = document.querySelector(
        '.short-cash-viewport--live .board-wrapper'
      );

      const boardTop = boardWrapper
        ? boardWrapper.getBoundingClientRect().top
        : 216;

      const bottomGap = 20;
      const availableHeight = Math.max(vh - boardTop - bottomGap, 260);

      const scaleY = stacked
        ? availableHeight / 760
        : (vh - 16) / 760;

      // <=767: старое телефонное поведение.
      // 768..1366: доска сверху, анализ ниже, поэтому масштаб
      // зависит от ширины, а не от высоты окна.
      const nextScale =
        vw <= 767
          ? Math.max(0.34, Math.min(1, scaleX, scaleY))
          : vw <= 1366
          ? Math.max(0.34, Math.min(1, scaleX))
          : 1;

      setAnalysisViewport((prev) => {
        if (
          prev.stacked === stacked &&
          Math.abs(prev.scale - nextScale) < 0.002
        ) {
          return prev;
        }

        return {
          stacked,
          scale: nextScale,
        };
      });
    });
  };

  updateAnalysisViewport();
  window.addEventListener('resize', updateAnalysisViewport);
  window.visualViewport?.addEventListener('resize', updateAnalysisViewport);

  return () => {
    cancelAnimationFrame(rafId);
    window.removeEventListener('resize', updateAnalysisViewport);
    window.visualViewport?.removeEventListener('resize', updateAnalysisViewport);
  };
}, []);

const analysisEnabled = hasAnalysisData;

const analysisLayoutWidth =
  typeof window !== 'undefined'
    ? Math.round(window.visualViewport?.width || window.innerWidth || 1366)
    : 1366;

const analysisLayoutHeight =
  typeof window !== 'undefined'
    ? Math.round(window.visualViewport?.height || window.innerHeight || 768)
    : 768;

// SHORT CASH / TABLET PORTRAIT-COMPACT.
// Отдельный флаг нужен, чтобы tablet никогда не наследовал жёсткий
// live-lock (height:100dvh + overflow:hidden).
const shortCashTabletScrollMode =
  analysisLayoutWidth >= 768 &&
  analysisLayoutWidth <= 1366 &&
  analysisLayoutHeight > 600 &&
  !cashPhoneLandscapeMode;

// На tablet панель ходов должна существовать всегда.
// Иначе при пустом/ещё не пришедшем GNU analysis JSX вообще не создаёт
// .left-panels, и прокручивать физически нечего.
const effectiveAnalysisEnabled =
  analysisEnabled || shortCashTabletScrollMode;

/*
 * SHORT CASH / PHONE PORTRAIT — реальное заполнение ширины.
 *
 * В проекте сохранён legacy viewport: на iPhone реальный screen.width
 * может быть 390/430/440px, а layout viewport при этом около 980px.
 *
 * Поэтому:
 * - сам РЕЖИМ телефона определяем по screen.width / screen.height;
 * - масштаб доски считаем по layout viewport, НЕ по screen.width.
 *
 * Это важно: браузер уже сам ужимает legacy-layout до физической ширины
 * телефона. Если ещё раз масштабировать по 440px — получится двойное
 * уменьшение.
 *
 * Базовый .board = 1020px, padding 20px + 20px, поэтому видимая ширина
 * PNG внутри доски = 980px.
 */
const isShortCashRoute =
  typeof window !== 'undefined' &&
  /\/ShortBackgammonBoard(?:\/|$)/i.test(window.location?.pathname || '');

const shortCashPhysicalWidth =
  typeof window !== 'undefined'
    ? Math.round(window.screen?.width || analysisLayoutWidth)
    : analysisLayoutWidth;

const shortCashPhysicalHeight =
  typeof window !== 'undefined'
    ? Math.round(window.screen?.height || analysisLayoutHeight)
    : analysisLayoutHeight;

const shortCashPhonePortraitFill =
  isShortCashRoute &&
  shortCashPhysicalWidth <= 767 &&
  shortCashPhysicalHeight > shortCashPhysicalWidth &&
  !cashPhoneLandscapeMode;

const SHORT_BOARD_BASE_SIDE_PADDING = 20;
const SHORT_BOARD_VISIBLE_BASE_WIDTH =
  1020 - (SHORT_BOARD_BASE_SIDE_PADDING * 2);

const shortCashPhonePortraitLayoutWidth =
  shortCashPhonePortraitFill && typeof window !== 'undefined'
    ? Math.round(window.innerWidth || analysisLayoutWidth)
    : analysisLayoutWidth;

const shortCashPhonePortraitScale = shortCashPhonePortraitFill
  ? shortCashPhonePortraitLayoutWidth / SHORT_BOARD_VISIBLE_BASE_WIDTH
  : analysisViewport.scale;

const shortCashPhonePortraitShiftX = shortCashPhonePortraitFill
  ? -(SHORT_BOARD_BASE_SIDE_PADDING * shortCashPhonePortraitScale) + 20
  : 0;

/*
 * После окончания партии / в replay на notebook 980..1200
 * резервируем реальную колонку слева под:
 * партии матча, ходы, PR/Luck и текст анализа.
 *
 * Во время LIVE это НЕ включается.
 */
// TABLET / COMPACT:
 // анализ всегда идёт ПОД доской.
 // Старый промежуточный режим [анализ слева][доска справа] отключён.
const stackedAnalysisSideReview = false;

// <=1200 — stacked;
// >1200 — используем ту же двухколоночную архитектуру, что на ПК.
// Горизонтальный телефон по-прежнему живёт в своей отдельной системе.
const desktopAnalysisLayout =
  analysisEnabled &&
  !analysisViewport.stacked &&
  !cashPhoneLandscapeMode;

const stackedAnalysisLayout =
  effectiveAnalysisEnabled &&
  analysisViewport.stacked &&
  !cashPhoneLandscapeMode;

const stackedAnalysisSideWidth = stackedAnalysisSideReview
  ? Math.max(260, Math.min(300, Math.round(analysisLayoutWidth * 0.25)))
  : 0;

const stackedAnalysisSideGap = stackedAnalysisSideReview ? 8 : 0;

const stackedAnalysisSideScale = stackedAnalysisSideReview
  ? Math.max(
      0.34,
      Math.min(
        1,
        (analysisLayoutWidth -
          24 -
          stackedAnalysisSideWidth -
          stackedAnalysisSideGap) /
          1020,
        (analysisLayoutHeight - 104) / 760
      )
    )
  : analysisViewport.scale;

const analysisBoardScale = shortCashPhonePortraitFill
  ? shortCashPhonePortraitScale
  : stackedAnalysisSideReview
  ? stackedAnalysisSideScale
  : analysisViewport.scale;


// 980..1200px: чуть поднимаем весь cash-блок, чтобы нижние цифры
// полностью вошли в viewport. Phone portrait/landscape не затрагиваем.
const shortCashCompactLiftUp =
  analysisLayoutWidth >= 980 &&
  analysisLayoutWidth <= 1200 &&
  analysisLayoutHeight > 600 &&
  !cashPhoneLandscapeMode &&
  !shortCashPhonePortraitFill;




const capturedForUi = isReplayMode ? displayCaptured : capturedCheckers;

const whiteOffArr = isReplayMode
  ? Array.from({ length: displayOffCount.white || 0 })
  : (Array.isArray(whiteOffBoard) ? whiteOffBoard : []);

const blackOffArr = isReplayMode
  ? Array.from({ length: displayOffCount.black || 0 })
  : (Array.isArray(blackOffBoard) ? blackOffBoard : []);

const stackedBoardWrapperStyle = stackedAnalysisLayout
  ? {
      order: 1,
      width: `${1020 * analysisBoardScale}px`,
      minWidth: `${1020 * analysisBoardScale}px`,
      maxWidth: `${1020 * analysisBoardScale}px`,
      height: `${760 * analysisBoardScale}px`,
      minHeight: `${760 * analysisBoardScale}px`,
      maxHeight: `${760 * analysisBoardScale}px`,
      margin: '0 auto',
      paddingLeft: 0,
      overflow: 'visible',
      display: 'block',
      position: 'relative',
    }
  : undefined;

const stackedBoardStyle = stackedAnalysisLayout
  ? {
      width: '1020px',
      maxWidth: 'none',
      position: 'relative',
      left: 0,
      top: 0,
      margin: 0,
      transform: `scale(${analysisBoardScale})`,
      transformOrigin: 'top left',
    }
  : undefined;

const stackedPanelsStyle = stackedAnalysisLayout
  ? {
      order: 2,
      width: `${1020 * analysisBoardScale}px`,
      minWidth: `${1020 * analysisBoardScale}px`,
      maxWidth: `${1020 * analysisBoardScale}px`,
      margin: '0 auto',
      position: 'relative',
      left: 'auto',
      top: 'auto',
      right: 'auto',
    }
  : undefined;
 



    return (
<DisabledWrap>
  <div
    onWheelCapture={(event) => {
      if (!shortCashTabletScrollMode) return;

      const node = event.currentTarget;
      if (!node) return;

      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
      if (maxScroll <= 0) return;

      const nextTop = Math.max(
        0,
        Math.min(maxScroll, node.scrollTop + event.deltaY)
      );

      if (nextTop !== node.scrollTop) {
        node.scrollTop = nextTop;
        event.preventDefault();
      }
    }}
    data-tablet-scroll={shortCashTabletScrollMode ? '1' : '0'}
    className={`short-cash-viewport ${
      !gameFinished &&
      !showResultModal &&
      !shortCashTabletScrollMode
        ? 'short-cash-viewport--live'
        : ''
    } ${
      shortCashTabletScrollMode
        ? 'short-cash-viewport--tablet-scroll'
        : ''
    } ${
      cashPhoneLandscapeMode ? 'short-cash-viewport--phone-landscape' : ''
    } ${
      shortCashPhonePortraitFill
        ? 'short-cash-viewport--phone-portrait-fill'
        : ''
    }`}
  >
    <div
      className={`short-cash-viewport-inner ${
        cashPhoneLandscapeMode
          ? 'short-cash-viewport-inner--phone-landscape'
          : ''
      }`}
      style={
        stackedAnalysisLayout || cashPhoneLandscapeMode
          ? undefined
          : {
              transform: `scale(${boardScale})`,
              transformOrigin: 'top center',
            }
      }
    >
              <div
                className={`short-cash ${
                  desktopAnalysisLayout ? 'short-cash--analysis' : ''
                } ${
                  cashPhoneLandscapeMode ? 'short-cash--phone-landscape' : ''
                } ${
                  shortCashPhonePortraitFill
                    ? 'short-cash--phone-portrait-fill'
                    : ''
                }`}
                style={{
                  '--analysis-board-scale': analysisBoardScale,
                  marginTop: shortCashCompactLiftUp ? '-5px' : undefined,
                  ...(shortCashPhonePortraitFill
                    ? {
                        '--short-phone-portrait-fill-scale':
                          shortCashPhonePortraitScale,
                        '--short-phone-portrait-shift-x':
                          `${shortCashPhonePortraitShiftX}px`,
                      }
                    : {}),
                }}
              > 
                {/* Оверлей удвоения */}
                {doubleDialog}
                {boardSettingsDialog}
                {shortBoardQuickControls}

  {(stackedAnalysisLayout || cashPhoneLandscapeMode) && (
  <div
    className={`short-mobile-hud ${
      cashPhoneLandscapeMode ? 'short-mobile-hud--phone-landscape' : ''
    }`}
  >
    <div className="smh-item smh-item--left">
      <Avatar
        src={landscapeTopUi.avatar}
        alt={landscapeTopUi.name}
        textFallback="BQ"
        className="tah-avatar smh-avatar"
      />

      <div className="smh-name">{landscapeTopUi.name}</div>
      <div className="smh-elo">ELO {landscapeTopUi.rating}</div>
      <div className="smh-time" ref={landscapeTopTimeRef}>
        05:00
      </div>
    </div>

    <div className="smh-center">
      <div className="smh-score">
        <span className="smh-score-box">{s1}</span>
        <span className="smh-score-sep">:</span>
        <span className="smh-score-box">{s2}</span>
      </div>

      <div className="smh-stake">{translate('game.short.stake')} {money(uiMoneyStake)}</div>
      <div className="smh-match">{translate('game.short.matchTo')} {mTarget}</div>
    </div>

    <div className="smh-item smh-item--right">
      <Avatar
        src={landscapeBottomUi.avatar}
        alt={landscapeBottomUi.name}
        textFallback="BQ"
        className="tah-avatar smh-avatar"
      />

      <div className="smh-name">{landscapeBottomUi.name}</div>
      <div className="smh-elo">ELO {landscapeBottomUi.rating}</div>
      <div className="smh-time" ref={landscapeBottomTimeRef}>
        05:00
      </div>
    </div>
  </div>
)}
  
                {/* MATCH HUD — как в длинных */}
                {/* центр-HUD для длинных */}



{/* TOP HUD — аватары вместо Ставка/Матч (мобилку не трогаем) */}
{!stackedAnalysisLayout && !isMobileWidth && !desktopAnalysisLayout && (
    <div className="right-side-hud">
    {/* ОППОНЕНТ СВЕРХУ */}
    <div className={`rsh-player rsh-player-top ${topTurnActive ? 'tah-active' : ''}`}>
      <Avatar
        src={topUi.avatar}
        alt={topUi.name}
        textFallback="BQ"
        className="tah-avatar"
      />

      <div className="rsh-meta">
        <div className="tah-headline">
          <div className="tah-name" title={topUi.name}>{topUi.name}</div>
          {topTurnActive && <span className="tah-turn-dot" />}
        </div>

        <div className="tah-sub">ELO {topUi.rating}</div>

        <div className="tah-time" ref={topTimeRef}>
          05:00
        </div>
      </div>
    </div>

    {/* ЦЕНТРАЛЬНЫЙ БЛОК */}
    <div className="rsh-center">
      <div className="tah-score">
        <span className="tah-score-num">{s1}</span>
        <span className="tah-score-sep">:</span>
        <span className="tah-score-num">{s2}</span>
      </div>

   <div className="tah-stake">
  {translate('game.short.stake')} {money(uiMoneyStake)}
</div>

<div className="tah-match">
  {translate('game.short.matchTo')} {mTarget}
</div>
    </div>

    {/* Я СНИЗУ */}
    <div className={`rsh-player rsh-player-bottom ${bottomTurnActive ? 'tah-active' : ''}`}>
      <Avatar
        src={bottomUi.avatar}
        alt={bottomUi.name}
        textFallback="BQ"
        className="tah-avatar"
      />

      <div className="rsh-meta">
        <div className="tah-headline">
          <div className="tah-name" title={bottomUi.name}>{bottomUi.name}</div>
          {bottomTurnActive && <span className="tah-turn-dot" />}
        </div>

        <div className="tah-sub">ELO {bottomUi.rating}</div>

        <div className="tah-time" ref={bottomTimeRef}>
          05:00
        </div>
      </div>
    </div>
  </div>
)}

 



             
                {/* Панель кнопок только для Short Cash */}
                                {/* Панель кнопок только для Short Cash */}
         
 {!shortLiveGame && (isMobile || isTablet || isMobileWidth || isTouchDevice) && (
  <button
    type="button"
    onClick={
      iAmPureSpectator
        ? handleLeaveTableClean
        : !iAmPlayer
        ? handleLeaveTableClean
        : (gameFinished || showResultModal)
        ? handleExitFromAnalysis
        : canShowCloseTableBtn
        ? handleLeaveTableClean
        : canShowResignBtn
        ? () => setShowResignDialog(true)
        : undefined
    }
    title={
      iAmPureSpectator
        ? translate('game.short.exitSpectator')
        : !iAmPlayer
        ? translate('game.common.exit')
        : (gameFinished || showResultModal)
        ? translate('game.short.exitTable')
        : canShowCloseTableBtn
        ? translate('game.short.closeTable')
        : canShowResignBtn
        ? translate('game.common.surrender')
        : ''
    }
    style={{
      position: 'fixed',
      left: '14px',
      bottom: '16px',
      zIndex: 99999,
      width: '58px',
      height: '58px',
      borderRadius: '999px',
      border: 'none',
      background: '#ff6b6b',
      color: '#fff',
      fontSize: '34px',
      fontWeight: 900,
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 10px 24px rgba(0,0,0,0.34)',
      cursor: 'pointer',
      padding: 0
    }}
  >
    ←
  </button>
)}

{!shortLiveGame && !(isMobile || isTablet || isMobileWidth || isTouchDevice) && (
  <div className="short-actions mobile-hide">
    {iAmPureSpectator ? (
      <button
        type="button"
        className="short-btn short-btn--ghost"
        onClick={handleLeaveTableClean}
      >
        {translate('game.short.exitSpectator')}
      </button>
    ) : !iAmPlayer ? (
      <button
        type="button"
        className="short-btn short-btn--ghost"
        onClick={handleLeaveTableClean}
      >
        {translate('game.common.exit')}
      </button>
    ) : (gameFinished || showResultModal) ? (
      <button
        type="button"
        className="short-btn short-btn--danger"
        onClick={handleExitFromAnalysis}
        title={translate('game.short.exitTable')}
      >
        {translate('game.common.exit')}
      </button>
    ) : canShowCloseTableBtn ? (
      <button
        type="button"
        className="short-btn short-btn--danger"
        onClick={handleLeaveTableClean}
      >
        {translate('game.short.closeTable')}
      </button>
    ) : canShowResignBtn ? (
      <button
        type="button"
        className="short-btn short-btn--ghost"
        onClick={() => setShowResignDialog(true)}
      >
        {translate('game.common.surrender')}
      </button>
    ) : null}
  </div>
)} 

<div
  className={`short-stage ${
    desktopAnalysisLayout ? 'short-stage--analysis' : ''
  } ${
    stackedAnalysisLayout ? 'short-stage--analysis-stacked' : ''
  } ${
    stackedAnalysisSideReview ? 'short-stage--analysis-side-review' : ''
  }`}
  style={
    stackedAnalysisLayout
      ? {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 12,
          width: '100%',
          paddingLeft: 0,
          '--analysis-board-scale': analysisBoardScale,
          '--analysis-side-width': `${stackedAnalysisSideWidth}px`,
          '--analysis-side-gap': `${stackedAnalysisSideGap}px`,
        }
      : desktopAnalysisLayout
      ? {
          '--analysis-board-scale': analysisBoardScale,
        }
      : undefined
  }
>
  {effectiveAnalysisEnabled && !cashPhoneLandscapeMode && (
  <div className="left-panels" style={stackedPanelsStyle}>
{desktopAnalysisLayout && !stackedAnalysisLayout && (
  <div className="analysis-top-hud analysis-top-hud--inside-left">
    <div className="analysis-top-player analysis-top-player--left">
      <div className="analysis-top-avatar">
        <Avatar
          src={topUi.avatar}
          alt={topUi.name}
          textFallback="BQ"
          className="tah-avatar"
        />
      </div>

      <div className="analysis-top-meta">
        <div className="analysis-top-name" title={topUi.name}>
          {topUi.name}
        </div>
        <div className="analysis-top-rating">ELO {topUi.rating}</div>
        <div className="analysis-top-time" ref={topTimeRef}>05:00</div>
      </div>
    </div>

    <div className="analysis-top-center">
      <div className="analysis-top-score">
        <span>{s1}</span>
        <span className="analysis-top-score-sep">:</span>
        <span>{s2}</span>
      </div>
      <div className="analysis-top-stake">{translate('game.short.stake')} {money(uiMoneyStake)}</div>
      <div className="analysis-top-match">{translate('game.short.matchTo')} {mTarget}</div>
    </div>

    <div className="analysis-top-player analysis-top-player--right">
      <div className="analysis-top-meta analysis-top-meta--right">
        <div className="analysis-top-name" title={bottomUi.name}>
          {bottomUi.name}
        </div>
        <div className="analysis-top-rating">ELO {bottomUi.rating}</div>
        <div className="analysis-top-time" ref={bottomTimeRef}>05:00</div>
      </div>

      <div className="analysis-top-avatar">
        <Avatar
          src={bottomUi.avatar}
          alt={bottomUi.name}
          textFallback="BQ"
          className="tah-avatar"
        />
      </div>
    </div>
  </div>
)}
    {/* ===== Навигация по партиям матча (только в анализе матча) ===== */}
{Array.isArray(matchGames) && matchGames.length > 0 && typeof onSelectMatchGame === 'function' && (
  <div className="match-games-section">
    <div className="match-games-title">{translate('game.analysis.title')}</div>

    <div className="match-games-subtitle">
      {translate('game.analysis.help')}
    </div>

   {(() => {
  const safeIndex = Math.max(
    0,
    Math.min(matchGames.length - 1, Number(selectedMatchGameIndex) || 0)
  );

  const currentGame = matchGames[safeIndex];
  const currentScore =
    currentGame?.scoreAfterStr ??
    `${Number(currentGame?.scoreAfter?.player1 || 0)}-${Number(currentGame?.scoreAfter?.player2 || 0)}`;

  return (
    <div className="match-games-dropdown" ref={matchGamesDropdownRef}>
      <button
        type="button"
        className={`match-games-dropdown__trigger ${isMatchGamesOpen ? 'is-open' : ''}`}
        onClick={() => setIsMatchGamesOpen((prev) => !prev)}
        aria-expanded={isMatchGamesOpen}
      >
        <span className="match-games-dropdown__trigger-left">
          <span className="match-games-dropdown__current-title">
            {translate('game.analysis.game')} {safeIndex + 1}
          </span>
          <span className="match-games-dropdown__current-score">
            · {currentScore}
          </span>
        </span>

        <span className="match-games-dropdown__trigger-right">
          {safeIndex + 1} / {matchGames.length}
        </span>

        <span className="match-games-dropdown__chevron">
          {isMatchGamesOpen ? '▴' : '▾'}
        </span>
      </button>

      {isMatchGamesOpen && (
        <div className="match-games-dropdown__menu">
          {matchGames.map((g, i) => {
            const score =
              g?.scoreAfterStr ??
              `${Number(g?.scoreAfter?.player1 || 0)}-${Number(g?.scoreAfter?.player2 || 0)}`;

            const isActive = i === safeIndex;

            return (
              <button
                key={i}
                type="button"
                className={`match-games-dropdown__option ${isActive ? 'is-active' : ''}`}
                onClick={() => {
                  onSelectMatchGame(i);
                  setIsMatchGamesOpen(false);
                }}
                title={translate('game.analysis.gameNumber', { number: i + 1 })}
              >
                <span className="match-games-dropdown__option-main">
                  <span className="match-games-dropdown__option-title">
                    {translate('game.analysis.game')} {i + 1}
                  </span>
                  <span className="match-games-dropdown__option-score">
                    {score}
                  </span>
                </span>

                {isActive && (
                  <span className="match-games-dropdown__option-check">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
})()}
  </div>
)}

   

  {(
    (Array.isArray(analysisTurns) && analysisTurns.length > 0) ||
    shortCashTabletScrollMode
  ) && (

    <div className="turns-panel">
  {/* Верхняя строка: заголовок + счетчик */}
  <div className="turns-header">
    <div className="turns-title">{translate('game.analysis.moves')}</div>

    <div className="turns-header-right">
      <div className="turns-counter">
        {isReplayMode
          ? (selectedMoveNum === 0
              ? translate('game.analysis.startOfTotal', { total: totalMoves })
              : translate('game.analysis.moveOfTotal', { move: selectedMoveNum, total: totalMoves })
            )
          : translate('game.analysis.totalMoves', { total: totalMoves })}
      </div>

      {canToggleTurnsExpand && (
        <button
          type="button"
          className="turns-expand-btn"
          onClick={handleToggleTurnsExpand}
          aria-expanded={isTurnsExpanded}
          title={isTurnsExpanded ? translate('game.analysis.collapseMoves') : translate('game.analysis.expandMoves')}
        >
          {isTurnsExpanded ? translate('game.analysis.collapse') : translate('game.analysis.expand')}
        </button>
      )}
    </div>
  </div>

  {/* Список ходов: все строки всегда в DOM, меняем только высоту блока */}
<div
  className={`turns-list ${isTurnsExpanded ? 'turns-list--expanded' : 'turns-list--collapsed'}`}
  ref={setTurnsListNode}
  onScroll={handleTurnsListScroll}
  onMouseDownCapture={rememberTurnsListScroll}
  onTouchStartCapture={rememberTurnsListScroll}
>
  {(() => {
    // После игры тоже хотим видеть полный список и иметь "Старт"
  const rowsSrc = turnsRowsForRender;


  // Все реальные ходы, существующие в полной истории.
const realMoveNums = new Set(
  allTurnsRows
    .filter((row) => row?.kind === 'turn')
    .map((row) => Number(row.moveNum))
    .filter(Number.isFinite)
);

// Чтобы Double и TAKE не создавали две одинаковые meta-строки.
const renderedCubeMetaMoveNums = new Set();

const fmtFrom = (p) => {
  if (p == null) return '?';

  const text = String(p).trim().toLowerCase();

  if (text === 'bar') return 'bar';

  const n = Number(p);

  // В истории игры BAR может приходить как 24 или 25.
  // Поле from позволяет однозначно понять, что это именно BAR.
  if (Number.isFinite(n) && n >= 24) return 'bar';

  if (Number.isInteger(n) && n >= 0 && n <= 23) {
    return String(n + 1);
  }

  return '?';
};

const fmtTo = (p) => {
  if (p == null) return '?';

  const text = String(p).trim().toLowerCase();

  if (text === 'off') return 'off';

  const n = Number(p);

  // Специальное значение в поле to означает снятие шашки.
  if (Number.isFinite(n) && n >= 24) return 'off';

  if (Number.isInteger(n) && n >= 0 && n <= 23) {
    return String(n + 1);
  }

  return '?';
};

const fmtMoves = (moves) =>
  (Array.isArray(moves) ? moves : [])
    .map((m) => {
      const diceText =
        m?.diceUsed != null
          ? `(${m.diceUsed})`
          : '';

      return `${fmtFrom(m?.from)}/${fmtTo(m?.to)}${diceText}`;
    })
    .join(' ');

    const getDiceStr = (turn) => {
      const d = getTurnDiceArr(turn);
      return (Array.isArray(d) && d.length) ? `[${d.join(',')}]` : `[—]`;
    };

    // Вынесем сборку cube UI в функцию, чтобы использовать и для meta-строк
    const buildCubeUi = (moveNum) => {
      const cubeMove = getCubeMoveForMoveNum(moveNum);

      const gnuDoublerNorm = normDoubler(cubeMove?.doubler);
      const gnuSaysDouble = gnuDoublerNorm === 'double';
      const gnuSaysNoDouble = gnuDoublerNorm === 'no double';
      const gnuRespNorm = normResponder(cubeMove?.responder);

      const playedFacts = getPlayedCubeFacts(moveNum);
      const playedDouble = (playedFacts?.offered === true);
      const playedRespNorm = normResponder(playedFacts?.response);

      const allowMissedDoubleHint = canNavigateTurns; // реплей/после игры
      const isMissedDouble = allowMissedDoubleHint && gnuSaysDouble && !playedDouble;

      const isWrongResponse =
        !!playedRespNorm && !!gnuRespNorm && (playedRespNorm !== gnuRespNorm);

      const isBadDouble = playedDouble && gnuSaysNoDouble;

      const showCubeMark =
        playedDouble ||
        !!playedRespNorm ||
        isMissedDouble;

      const cubeLineText = (() => {
        if (playedDouble) {
          const respPart = playedRespNorm ? ` → ${playedRespNorm.toUpperCase()}` : '';
          if (gnuSaysNoDouble) return translate('game.analysis.cubeWrongNoDouble', { response: respPart });
          if (playedRespNorm && isWrongResponse) {
            return translate('game.analysis.cubeWrongResponse', { played: `Double${respPart}`, recommended: gnuRespNorm.toUpperCase() });
          }
          return `Cube: Double${respPart}`;
        }

        if (isMissedDouble) return translate('game.analysis.cubeWrongDouble');

        if (playedRespNorm) {
          if (isWrongResponse) return translate('game.analysis.cubeWrongResponse', { played: playedRespNorm.toUpperCase(), recommended: gnuRespNorm.toUpperCase() });
          return `Cube: ${playedRespNorm.toUpperCase()}`;
        }

        return '';
      })();

      const cubeMarkClass =
        (isBadDouble || isWrongResponse) ? 'turn-cube-inline--bad'
          : (isMissedDouble ? 'turn-cube-inline--missed' : 'turn-cube-inline--ok');

      const cubeBtnClass =
        (isBadDouble || isWrongResponse) ? 'turn-cube-btn--bad'
          : (isMissedDouble ? 'turn-cube-btn--missed' : '');

      return {
        showCubeMark,
        cubeLineText,
        cubeMarkClass,
        cubeBtnClass,
      };
    };

    const rows = [];

    // ✅ Строка "Старт" доступна после игры/в реплее
   
      const isStartSelected = selectedMoveNum === 0;

      rows.push(
        <div
          key="__start__"
          className={`turn-row turn-row--start ${isStartSelected ? 'turn-row--selected' : ''}`}
        onClick={() => {
  if (!canNavigateTurns) return;

rememberTurnsListScroll();

  setCubeDetailsMoveNum(null);
  setSelectedMoveNum(0);
}}
          title={translate('game.analysis.startPositionZero')}
          style={{
            color: isStartSelected ? '#ffffff' : '#d6d6d6',
            cursor: canNavigateTurns ? 'pointer' : 'default',
            opacity: canNavigateTurns ? 1 : 0.85,
          }}
        >
          <span className="turn-player-wrap">
            <b className="turn-player">{translate('game.analysis.start')}</b>
          </span>

          <span className="turn-dice">[—]</span>

          {(() => {
            const ui = buildCubeUi(0);
            if (!ui.showCubeMark || !ui.cubeLineText) return null;

            return (
              <>
                <span
                  className={`turn-cube-inline ${ui.cubeMarkClass}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canNavigateTurns) return;
                    setCubeDetailsMoveNum((prev) => (prev === 0 ? null : 0));
                  }}
                  title={translate('game.analysis.clickCubeStats')}
                  style={{ cursor: 'pointer' }}
                >
                  {ui.cubeLineText}
                </span>

                <button
                  type="button"
                  className={`turn-cube-btn ${ui.cubeBtnClass}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canNavigateTurns) return;
                    setCubeDetailsMoveNum((prev) => (prev === 0 ? null : 0));
                  }}
                  title={translate('game.analysis.clickCubeStats')}
                >
                  <CubeSvg size={16} />
                </button>
              </>
            );
          })()}

          <span className="turn-moves-wrap">
            <span className="turn-moves">{translate('game.analysis.initialPosition')}</span>
          </span>
        </div>
      );
    

    // ✅ Основной список: uiRows (turn/meta)
    for (const r of rowsSrc) {
      // --------------------
      // META-строки (куб/служебное)
      // --------------------
      if (r.kind === 'meta') {
        const attachMoveNum = Number.isFinite(Number(r.attachMoveNum)) ? Number(r.attachMoveNum) : 0;

        // Если после куба состоялся настоящий ход,
// куб уже будет показан на строке этого хода.
if (realMoveNums.has(attachMoveNum)) {
  continue;
}

// Если offer и take создали две meta-записи,
// отображаем только одну строку куба.
if (renderedCubeMetaMoveNums.has(attachMoveNum)) {
  continue;
}

renderedCubeMetaMoveNums.add(attachMoveNum);
        // ВАЖНО: meta-строка НЕ должна менять selectedMoveNum,
        // иначе ты снова "прыгнешь" на другой ход и появятся стрелки "не там".
        const ui = buildCubeUi(attachMoveNum);

        // если это meta вообще не про куб и нечего показывать — можно пропустить
        // (или оставь, если хочешь видеть любые служебные строки)
        if (!ui.showCubeMark || !ui.cubeLineText) continue;

        const isMetaSelected = cubeDetailsMoveNum === attachMoveNum;

        rows.push(
          <div
            key={`meta-${r.rawIndex}`}
            className={`turn-row turn-row--meta ${isMetaSelected ? 'turn-row--selected' : ''}`}
            onClick={() => {
              if (!canNavigateTurns) return;
              setCubeDetailsMoveNum((prev) => (prev === attachMoveNum ? null : attachMoveNum));
            }}
            title={translate('game.analysis.cubeEvent')}
            style={{
              color: '#d6d6d6',
              cursor: canNavigateTurns ? 'pointer' : 'default',
              opacity: 0.95,
            }}
          >
            <span className="turn-player-wrap">
              <b className="turn-player">{translate('game.analysis.cube')}</b>
            </span>

            <span className="turn-dice">[—]</span>

            <span className={`turn-cube-inline ${ui.cubeMarkClass}`}>
              {ui.cubeLineText}
            </span>

            <button
              type="button"
              className={`turn-cube-btn ${ui.cubeBtnClass}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!canNavigateTurns) return;
                setCubeDetailsMoveNum((prev) => (prev === attachMoveNum ? null : attachMoveNum));
              }}
              title={translate('game.analysis.clickCubeStats')}
            >
              <CubeSvg size={16} />
            </button>

            <span className="turn-moves-wrap">
              <span className="turn-moves">—</span>
            </span>
          </div>
        );

        continue;
      }

      // --------------------
      // TURN-строки (реальные ходы)
      // --------------------
      const t = r.t;
      const moveNum = r.moveNum; // ✅ ВАЖНО: это "реальный" номер хода (без куба)

      const g = gnuByMove?.[moveNum];

      const moverColorForLine = getMoverColorForMove(moveNum, t);

      const bestUiTextForLine = g?.bestMovesText
        ? convertGnuBestMovesTextForUi(g.bestMovesText, moverColorForLine)
        : '';

      const bestPairs = g?.bestMovesText
        ? parseBestMovesPairs(g.bestMovesText, moverColorForLine)
        : [];

      const actualPairs = turnMovesToPairs(t?.moves);

      const gnuQ = g?.quality || 'ok';
      const sameAsBest = bestPairs.length > 0 ? samePairMultiset(actualPairs, bestPairs) : true;

      const qFinal =
        (gnuQ === 'ok' && bestPairs.length > 0 && !sameAsBest)
          ? 'inaccuracy'
          : gnuQ;

      const lineColor = colorForQuality(qFinal);
      const baseColor = '#d6d6d6';
      const finalColor = lineColor || baseColor;

      const isSelected = selectedMoveNum === moveNum;

      // Куб для этого хода (если был)
      const cubeUi = buildCubeUi(moveNum);

      rows.push(
        <div
          key={`turn-${r.rawIndex}`}
          className={`turn-row ${isSelected ? 'turn-row--selected' : ''}`}
          onClick={() => {
  if (!canNavigateTurns) return;

rememberTurnsListScroll();

  setCubeDetailsMoveNum(null);
  setSelectedMoveNum(moveNum);
}}
          title={bestUiTextForLine ? translate('game.analysis.bestMove', { move: bestUiTextForLine }) : ''}
          style={{
            color: finalColor,
            cursor: canNavigateTurns ? 'pointer' : 'default',
            opacity: canNavigateTurns ? 1 : 0.85,
          }}
        >
          <span className="turn-player-wrap">
            <CheckerSvg
              color={moverColorForLine}
              size={16}
              className="turn-checker"
            />
            <b className="turn-player">{t?.player}</b>
          </span>

          <span className="turn-dice">{getDiceStr(t)}</span>

          {cubeUi.showCubeMark && cubeUi.cubeLineText && (
            <span
              className={`turn-cube-inline ${cubeUi.cubeMarkClass}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!canNavigateTurns) return;
                setCubeDetailsMoveNum((prev) => (prev === moveNum ? null : moveNum));
              }}
              title={translate('game.analysis.clickCubeStats')}
              style={{ cursor: 'pointer' }}
            >
              {cubeUi.cubeLineText}
            </span>
          )}

          {cubeUi.showCubeMark && (
            <button
              type="button"
              className={`turn-cube-btn ${cubeUi.cubeBtnClass}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!canNavigateTurns) return;
                setCubeDetailsMoveNum((prev) => (prev === moveNum ? null : moveNum));
              }}
              title={translate('game.analysis.clickCubeStats')}
            >
              <CubeSvg size={16} />
            </button>
          )}

          <span className="turn-moves-wrap">
            <span className="turn-moves">{fmtMoves(t?.moves)}</span>

            {bestUiTextForLine && qFinal !== 'ok' && (
              <span className="turn-gnu"> | GNU: {bestUiTextForLine}</span>
            )}
          </span>
        </div>
      );
    }

    return rows;
  })()}
</div>



  {/* Лучший ход для выбранного (отдельно, не внутри скролла) */}
  {selectedMoveNum != null && selectedMoveNum > 0 && gnuByMove[selectedMoveNum]?.bestMovesText && (
    <div className="turns-best">
      <div className="turns-best-label">{translate('game.analysis.bestGnuMove')}</div>
      <div className="turns-best-value">{bestUiTextSelected}</div>
    </div>
  )}




{cubeDetailsMoveNum != null && (
  <div className="cube-details">
    {(() => {
      const m = getCubeMoveForMoveNum(cubeDetailsMoveNum) || null;


      const gnuD = normDoubler(m?.doubler);
      const resp = m ? responderRu(m?.responder) : '—';
      const winPct = m ? getWinPctFromProbs(m) : null;

      // факт в партии (из cubeEvents)
      const playedFacts = getPlayedCubeFacts(cubeDetailsMoveNum);

      const playedD =
        playedFacts?.offered === true ? 'double' :
        playedFacts?.offered === false ? 'no double' :
        null;

      const playedResp = normResponder(playedFacts?.response);

      return (
        <>
          <div className="cube-details__head">
            <div className="cube-details__title">
              {translate('game.analysis.cubeOnMove')}{cubeDetailsMoveNum}
            </div>

            <button
              type="button"
              className="cube-details__close"
              onClick={() => setCubeDetailsMoveNum(null)}
              title={translate('game.common.close')}
            >
              ✕
            </button>
          </div>

          <div className="cube-details__row">
            <b>{translate('game.analysis.actualGame')}</b>{' '}
            {playedD === 'double' ? translate('game.analysis.wasDouble')
              : playedD === 'no double' ? translate('game.analysis.noDouble')
              : translate('game.analysis.noData')}
            {playedResp ? translate('game.analysis.responseSuffix', { response: playedResp.toUpperCase() }) : ''}
          </div>

          <div className="cube-details__row">
            <b>{translate('game.analysis.prPenalty')}</b>{' '}
            {playedFacts?.prLoss != null ? String(playedFacts.prLoss) : '—'}
          </div>

          <div style={{ height: 1, background: 'rgba(255,255,255,0.12)', margin: '8px 0' }} />

          <div className="cube-details__row">
            <b>{translate('game.analysis.gnuRecommendation')}</b>{' '}
            {m
              ? (gnuD ? gnuD : '—')
              : translate('game.analysis.noGnuData')}
          </div>

          <div className="cube-details__row">
            <b>{translate('game.analysis.gnuResponse')}</b> {resp}
          </div>

          <div className="cube-details__row">
            <b>Win% (GNU):</b>{' '}
            {winPct != null ? `${winPct}%` : '—'}
          </div>

          <div className="cube-details__row">
            <b>Percent:</b>{' '}
            {m?.percent != null ? String(m.percent) : '—'}
          </div>

          <div className="cube-details__row">
            <b>Eq:</b>{' '}
            {m?.cubelessEquity != null ? String(m.cubelessEquity) : '—'}
          </div>
        </>
      );
    })()}
  </div>
)}






  {/* Нижняя панель кнопок (как на скрине) */}
  <div className="turns-footer">
  <div className="turns-nav">
    <button
      type="button"
      className="turns-btn"
      onClick={() => {
        if (!canNavigateTurns) return; // 🔒 блок во время игры
        rememberTurnsListScroll();
        const snap = getStartSnapshot();
        setDisplayBoard(snap);
        setDisplayCaptured({ white: 0, black: 0 });
        setDisplayOffCount({ white: 0, black: 0 });
        setSelectedMoveNum(0);
      }}
      disabled={!totalMoves || !canNavigateTurns}
      title={!canNavigateTurns ? translate('game.analysis.availableAfterGame') : translate('game.analysis.startPosition')}
    >
      ⏮
    </button>

    <button
      type="button"
      className="turns-btn"
      onClick={() => {
        if (!canNavigateTurns) return; // 🔒 блок во время игры
        selectPrevMove();
      }}
      disabled={!totalMoves || !canNavigateTurns || selectedMoveNum === 0}
      title={!canNavigateTurns ? translate('game.analysis.availableAfterGame') : translate('game.analysis.previousMove')}
    >
      ◀
    </button>

    <button
      type="button"
      className="turns-btn"
      onClick={() => {
        if (!canNavigateTurns) return; // 🔒 блок во время игры
        selectNextMove();
      }}
      disabled={
        !totalMoves ||
        !canNavigateTurns ||
        selectedMoveNum === null ||
        selectedMoveNum >= totalMoves
      }
      title={!canNavigateTurns ? translate('game.analysis.availableAfterGame') : translate('game.analysis.nextMove')}
    >
      ▶
    </button>

    <button
      type="button"
      className="turns-btn"
      onClick={() => {
        if (!canNavigateTurns) return; // 🔒 блок во время игры
        rememberTurnsListScroll();
        setSelectedMoveNum(null);
      }}
      disabled={!totalMoves || !canNavigateTurns || selectedMoveNum === null}
      title={!canNavigateTurns ? translate('game.analysis.availableAfterGame') : translate('game.analysis.returnCurrent')}
    >
      ⏭
    </button>
  </div>
</div>

</div>



)}


{/* ✅ PR / Luck — выводим слева под списком ходов */}
{(
  gnuAnalysis?.status === 'waiting' ||
  gnuAnalysis?.status === 'ready' ||
  gnuAnalysis?.status === 'error' ||
  !!gnuAnalysis?.pr ||
  !!gnuStats
) && (
 <div className="pr-wrap">
    {gnuAnalysis?.status === 'waiting' && (
      <div style={{ fontSize: 12, opacity: 0.8 }}>
        {translate('game.analysis.calculating')}
      </div>
    )}

    {gnuAnalysis?.status === 'error' && (
      <div style={{ fontSize: 12, opacity: 0.85, whiteSpace: 'pre-wrap' }}>
        {translate('game.analysis.gnuError')} {gnuAnalysis?.error || '—'}
      </div>
    )}

    {(gnuAnalysis?.pr || gnuStats) && (
     <div className="pr-panel">


{false && Array.isArray(gnuAnalysis?.cubeMoves) && gnuAnalysis.cubeMoves.length > 0 && (
  <div style={{ marginTop: 10 }}>
    <div className="pr-title">{translate('game.analysis.cubeGnu')}</div>

    <div style={{ display: 'grid', gap: 6, fontSize: 12, whiteSpace: 'pre-wrap' }}>
      {gnuAnalysis.cubeMoves.map((m, idx) => {
        // ===== helpers прямо тут, чтобы не гадать по форматам =====
        const isPrimitive = (v) =>
          v === null ||
          v === undefined ||
          typeof v === 'string' ||
          typeof v === 'number' ||
          typeof v === 'boolean';

        const toInlineText = (v) => {
          if (v === null || v === undefined) return '';
          if (typeof v === 'string') return v;
          if (typeof v === 'number' || typeof v === 'boolean') return String(v);

          // массив -> "a, b, c"
          if (Array.isArray(v)) {
            const arr = v.map(toInlineText).filter(Boolean);
            return arr.join(', ');
          }

          // объект -> JSON (коротко)
          if (typeof v === 'object') {
            try {
              const s = JSON.stringify(v);
              if (!s) return '';
              return s.length > 140 ? s.slice(0, 140) + '…' : s;
            } catch {
              return '';
            }
          }

          return String(v);
        };

        const pickFirstPrimitive = (...vals) => {
          for (const v of vals) {
            if (isPrimitive(v) && v !== '' && v !== null && v !== undefined) {
              return String(v);
            }
          }
          return '';
        };

        const fmtNum = (x) => {
          const n = Number(x);
          if (!Number.isFinite(n)) return '';
          return n.toFixed(3);
        };

        // ===== вытаскиваем поля максимально “по-взрослому” =====
        const moveNo = m?.moveNo ?? m?.ply ?? m?.turn ?? m?.move ?? (idx + 1);
        const who = pickFirstPrimitive(m?.player, m?.side);

        // dice у тебя в логе строкой '53' / '41'
        const diceText = pickFirstPrimitive(m?.dice) || toInlineText(m?.dice);

        // В твоих данных есть cubelessEquity (в логе видно!)
        const eqText =
          fmtNum(m?.cubelessEquity) ||
          fmtNum(m?.equity) ||
          fmtNum(m?.eq) ||
          fmtNum(m?.equityAfter) ||
          fmtNum(m?.equity_before);

        // ВАЖНО: action показываем ТОЛЬКО если это примитив.
        // Если action/type/decision — объект, он сюда НЕ попадёт, и [object Object] исчезнет.
        const actionText = pickFirstPrimitive(
          m?.cubeAction,
          m?.cubeDecision,
          m?.decision,
          m?.action,
          m?.type
        );

        // Если хочешь всё-таки видеть объект — покажем его отдельной строкой “details”
        const actionDetails =
          !actionText
            ? (
                (m?.cubeAction && !isPrimitive(m.cubeAction)) ? toInlineText(m.cubeAction) :
                (m?.cubeDecision && !isPrimitive(m.cubeDecision)) ? toInlineText(m.cubeDecision) :
                (m?.decision && !isPrimitive(m.decision)) ? toInlineText(m.decision) :
                (m?.action && !isPrimitive(m.action)) ? toInlineText(m.action) :
                (m?.type && !isPrimitive(m.type)) ? toInlineText(m.type) :
                ''
              )
            : '';

        // Любой “сырой текст”
        const extraText =
          (typeof m?.text === 'string' ? m.text : '') ||
          (typeof m?.raw === 'string' ? m.raw : '') ||
          (typeof m?.line === 'string' ? m.line : '');

        return (
          <div key={m?.id ?? idx} style={{ opacity: 0.95 }}>
            <b>#{moveNo}</b>
            {who ? ` ${who}` : ''}
            {diceText ? ` [${diceText}]` : ''}
            {eqText ? ` — Eq: ${eqText}` : ''}

            {/* actionText — безопасно (строка/число), НЕ объект */}
            {actionText ? ` — ${actionText}` : ''}

            {/* actionDetails — если action был объектом, покажем JSON-строкой */}
            {actionDetails ? `\n${actionDetails}` : ''}

            {extraText ? `\n${extraText}` : ''}
          </div>
        );
      })}
    </div>
  </div>
)}

{isMatch && Array.isArray(matchGames) && matchGames.length > 1 && (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
    <button
      type="button"
      className={`rail-btn ${prScope === 'match' ? 'on' : ''}`}
      onClick={() => setPrScope('match')}
      title={translate('game.analysis.showMatchPr')}
    >
      {translate('game.analysis.match')}
    </button>

    <button
      type="button"
      className={`rail-btn ${prScope === 'game' ? 'on' : ''}`}
      onClick={() => setPrScope('game')}
      title={translate('game.analysis.showGamePr')}
    >
      {translate('game.analysis.game')}
    </button>

    <span style={{ fontSize: 12, opacity: 0.75 }}>
      {translate('game.analysis.prCollected')} {Object.keys(matchPrByGameKey || {}).length}/{matchGames.length}
    </span>
  </div>
)}


        <div className="pr-title">PR / Luck</div>


        {(() => {
          const prGame = gnuAnalysis?.pr || gnuStats;

// если матч и выбрано “Матч” — показываем агрегированный
const pr =
  (isMatch && Array.isArray(matchGames) && matchGames.length > 1 && prScope === 'match')
    ? matchPrAgg
    : prGame;

    if (!pr) {
  return <div style={{ fontSize: 12, opacity: 0.8 }}>{translate('game.analysis.noPrData')}</div>;
}



        const Row = ({ label, value }) => {
  const l = String(label || '').toLowerCase();

  const isOverall = l.includes('общий');
  const isLuck    = l.includes('удача');

  const rowClass =
    isOverall ? 'pr-row pr-row--overall'
    : isLuck  ? 'pr-row pr-row--luck'
    : 'pr-row';

  return (
    <div className={rowClass}>
      <span className="pr-label">{label}</span>
      <span className="pr-value">{value ?? '—'}</span>
    </div>
  );
};


          // поддержка двух форматов:
          // 1) pr = { chequer, cube, luck, overall }
          // 2) pr = { white:{...}, black:{...} }
          const isSplit = pr && (pr.white || pr.black);

          if (!isSplit) {
            return (
              <div style={{ display: 'grid', gap: 4 }}>
                <Row label={translate('game.analysis.checkersPr')} value={pr?.chequer} />
                <Row label={translate('game.analysis.cubePr')} value={pr?.cube} />
                <Row label={translate('game.common.luck')} value={pr?.luck} />
                <Row label={translate('game.analysis.overallPr')} value={pr?.overall} />
              </div>
            );
          }

    return (
  <div className="pr-sides">
    {/* Левая колонка — Белые */}
    <div className="pr-side">
      <div className="pr-side-title">{translate('game.common.white')}</div>

      <div className="pr-side-rows">
        <Row label={translate('game.analysis.checkersPr')} value={pr.white?.chequer} />
        <Row label={translate('game.analysis.cubePr')} value={pr.white?.cube} />
        <Row label={translate('game.common.luck')} value={pr.white?.luck} />
        <Row label={translate('game.analysis.overallPr')} value={pr.white?.overall} />
      </div>
    </div>

    {/* Правая колонка — Черные */}
    <div className="pr-side pr-side--black">
      <div className="pr-side-title">{translate('game.common.black')}</div>

      <div className="pr-black-scroll pr-side-rows">
        <Row label={translate('game.analysis.checkersPr')} value={pr.black?.chequer} />
        <Row label={translate('game.analysis.cubePr')} value={pr.black?.cube} />
        <Row label={translate('game.common.luck')} value={pr.black?.luck} />
        <Row label={translate('game.analysis.overallPr')} value={pr.black?.overall} />
      </div>
    </div>
  </div>
);

        })()}
      </div>
    )}
  </div>
)}
 </div>
  )}
                <div
                  className={`board-wrapper ${
                    cashPhoneLandscapeMode
                      ? 'board-wrapper--phone-landscape'
                      : ''
                  }`}
                  style={
                    cashPhoneLandscapeMode
                      ? undefined
                      : stackedBoardWrapperStyle
                  }
                >
                  {/* Левая «рельса» (белые) */}
                  {!isMobile && (
                <div className="rl">
  <div
    className={`player-rail rail-left mobile-hide ${
      isP1Turn ? 'rail--active' : ''
    }`}
  >


                  
                    </div>
                  </div>
                  )}
                  {/* Правая «рельса» (чёрные) */}
                  {!isMobile && (
<div className="rr">
  <div
    className={`player-rail rail-right mobile-hide ${
      isP2Turn ? 'rail--active' : ''
    }`}
  >


                     
                    </div>                   
                  </div>
)}






                  {/* Содержимое доски */}
                 <div className="short-board-wrap">
                  <div className="board-col">
                    {/* ===== Кубики соперника (верх) ===== */}

                {showDiceNow && (firstMoveComplete || isReplayMode) && (
  <div className="dice-display">
    {diceToShow.map((val, idx) => {

      // В режиме просмотра истории — цвет определяется тем, кто ходил в выбранном ходе
      if (isReplayMode) {
        const color = replayDiceColor || 'white'; // fallback
        const diceSrc = diceImages[color]?.[val];
        return (
          <img
            key={`replay-${selectedMoveNum ?? 'live'}-${idx}-${val}`}
            src={diceSrc}
            alt={`Dice ${val}`}
          />
        );
      }

      // В режиме живой игры — как у тебя было
      const isCurrentPlayer =
        currentTurn === (playerColor === 'white' ? 'player1' : 'player2');

      const diceColor = isCurrentPlayer ? playerColor : opponentTournamentColor;
      const diceSrc = diceImages[diceColor]?.[val];
    
      return (
        <img
          key={`live-${idx}-${val}`}
          src={diceSrc}
          alt={`Dice ${val}`}
        />
      );
    })}
  </div>
)}
              <div
  ref={boardRef}
  className={`board short-board board-skin-${shortBoardSkin} ${
    myTournamentColor === 'black' ? 'flipped' : ''
  } ${
    cashPhoneLandscapeMode ? 'short-board--phone-landscape' : ''
  }`}
  style={cashPhoneLandscapeMode ? undefined : stackedBoardStyle}
  onTouchMove={handleTouchMove}
  onTouchEnd={handleTouchEnd}
>
  {/* Фон доски (ОДИН!) */}
  <img
    src={shortBoardImageSrc}
    alt={shortBoardImageAlt}
    className="board-bg"
    draggable="false"
  />


  {/* Кубики поверх доски */}
  {showDiceNow && (
    <div className="dice-overlay" style={diceOverlayStyle}>
      {renderDiceTray(diceToShow, diceColorNow)}
    </div>
  )}

     
 
                    {/* Куб удвоения */}
                {!hideCube && (
  <div
    className={`doubling-cube ${cubeImageSrc ? 'doubling-cube--image' : 'doubling-cube--sharp'} ${cubePositionClass()} ${
      !isCubeEnabled && !isCrawford ? 'disabled' : ''
    } ${isCrawford ? 'doubling-cube--crawford' : ''}`}
    onClick={(event) => {
      event.stopPropagation();

      if (isCrawford) {
        showCrawfordBlockedHint();
        return;
      }

      if (isCubeEnabled) {
        onOfferDouble?.();
      }
    }}
    onKeyDown={(event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();

      if (isCrawford) {
        showCrawfordBlockedHint();
        return;
      }

      if (isCubeEnabled) {
        onOfferDouble?.();
      }
    }}
    role="button"
    tabIndex={isCrawford || isCubeEnabled ? 0 : -1}
    aria-disabled={!isCubeEnabled}
    aria-label={isCrawford ? crawfordMessage : `Doubling cube ${cubeFaceForUi}`}
    title={
      isCrawford
        ? crawfordMessage
        : !firstMoveComplete
        ? translate('game.short.cubeAfterFirstMove')
        : hasRolledDice
        ? translate('game.short.cubeBeforeRoll')
        : !isMyTurn
        ? translate('game.short.cubeYourTurn')
        : undefined
    }
    style={isCrawford ? { cursor: 'not-allowed', pointerEvents: 'auto' } : undefined}
  >
    {cubeImageSrc ? (
      <>
        <img
          src={cubeImageSrc}
          alt={isCrawford ? 'Crawford cube' : `Doubling cube ${cubeFaceForUi}`}
          className="cube-face-image"
          draggable="false"
        />

        {isCrawford && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              zIndex: 4,
              transform: 'translate(-50%, -50%)',
              minWidth: '58%',
              minHeight: '58%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              padding: '1px 3px 2px',
              border: '1px solid rgba(232, 179, 92, 0.92)',
              borderRadius: '3px',
              background: 'linear-gradient(145deg, #3a2112 0%, #17100b 58%, #0c0907 100%)',
              color: '#f2ce88',
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 'clamp(15px, 2.1vw, 24px)',
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.7px',
              textShadow: '0 1px 0 #6c3f18, 0 2px 2px rgba(0, 0, 0, 0.9)',
              boxShadow: 'inset 0 0 0 1px rgba(255, 218, 145, 0.16)',
              pointerEvents: 'none',
            }}
          >
            Cr
          </span>
        )}
      </>
    ) : (
      <div
        className="cube-face cube-face--sharp"
        aria-label={`Doubling cube ${cubeFaceForUi}`}
      >
        <span className="cube-face-value">
          {cubeFaceForUi}
        </span>
      </div>
    )}

    {isCrawford && showCrawfordHint && (
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 'calc(100% + 10px)',
          zIndex: 1000,
          transform: 'translateX(-50%)',
          width: 'max-content',
          maxWidth: '260px',
          padding: '7px 10px',
          border: '1px solid rgba(232, 179, 92, 0.8)',
          borderRadius: '7px',
          background: 'rgba(20, 16, 13, 0.97)',
          color: '#fff4dc',
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: 1.25,
          textAlign: 'center',
          whiteSpace: 'normal',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.45)',
          pointerEvents: 'none',
        }}
      >
        {crawfordMessage}
      </div>
    )}
  </div>
)}
  
  {shortLiveGame && showShortBoardQuickControls && (
    <div
      className="short-live-bar-controls"
      role="group"
      aria-label={translate('game.short.quickSettings', {
        defaultValue: 'Быстрые настройки игры',
      })}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onDragStart={(event) => event.preventDefault()}
      style={{
        position: 'absolute',
        left: cashPhoneLandscapeMode ? '44.57%' : '45%',

        // PHONE LANDSCAPE:
        // две кнопки над кубом + две под кубом.
        // В остальных режимах оставляем прежнее положение.
        top: cashPhoneLandscapeMode
          ? 'calc(50% - 80px)'
          : 'calc(50% + 34px)',

        transform: 'translateX(-50%)',
        zIndex: 170,
        width: 34,

        padding: cashPhoneLandscapeMode ? 0 : 2,
        gap: 2,

        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',

        border: cashPhoneLandscapeMode
          ? '0'
          : '1px solid rgba(228,184,91,0.26)',
        borderRadius: 8,
        background: cashPhoneLandscapeMode
          ? 'transparent'
          : 'rgba(9,10,12,0.82)',
        boxShadow: cashPhoneLandscapeMode
          ? 'none'
          : '0 5px 16px rgba(0,0,0,0.30)',

        boxSizing: 'border-box',
      }}
    >
      {/* ============================================================
          PHONE LANDSCAPE:
          НАД КУБОМ:
          1) Сдаться
          2) Настройки
          ============================================================ */}
      {shortLiveBarActionButton}

      {cashPhoneLandscapeMode && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowBoardSettings(true);
          }}
          aria-label={translate('game.short.boardSettings', {
            defaultValue: 'Выбрать дизайн доски',
          })}
          title={translate('game.common.settings', {
            defaultValue: 'Настройки',
          })}
          style={{
            width: 28,
            height: 28,
            minWidth: 28,
            minHeight: 28,
            padding: 4,
            margin: 0,
            borderRadius: 6,
            border: '1px solid rgba(228,184,91,0.38)',
            background: 'rgba(14,14,14,0.96)',
            color: '#f4e5c0',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              width: 17,
              height: 17,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <SettingsIcon />
          </span>
        </button>
      )}

      {/* Пустое место ровно под куб удвоения.
          Меняет только горизонтальный телефон. */}
      {cashPhoneLandscapeMode && (
        <span
          aria-hidden="true"
          style={{
            display: 'block',
            width: 1,
            height: 44,
            minHeight: 44,
            flex: '0 0 44px',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* ============================================================
          PHONE LANDSCAPE:
          ПОД КУБОМ:
          1) Автоход
          2) Автобросок
          ============================================================ */}
      {!isSpectator && cashPhoneLandscapeMode && (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleAutoConfirm?.();
            }}
            disabled={readOnly || typeof toggleAutoConfirm !== 'function'}
            aria-pressed={Boolean(autoConfirm)}
            aria-label={translate('game.common.autoMove', {
              defaultValue: 'Автоход',
            })}
            title={translate('game.common.autoMove', {
              defaultValue: 'Автоход',
            })}
            style={{
              position: 'relative',
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              padding: 4,
              margin: 0,
              borderRadius: 6,
              border: autoConfirm
                ? '1px solid #f2cf75'
                : '1px solid rgba(228,184,91,0.38)',
              background: autoConfirm
                ? 'linear-gradient(180deg,#f5d98f 0%,#c99739 100%)'
                : 'rgba(14,14,14,0.96)',
              color: autoConfirm ? '#201606' : '#f4e5c0',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                width: 17,
                height: 17,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <AutoMoveIcon />
            </span>
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleAutoRoll?.();
            }}
            disabled={readOnly || typeof toggleAutoRoll !== 'function'}
            aria-pressed={Boolean(autoRoll)}
            aria-label={translate('game.common.autoRoll', {
              defaultValue: 'Автобросок',
            })}
            title={translate('game.common.autoRoll', {
              defaultValue: 'Автобросок',
            })}
            style={{
              position: 'relative',
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              padding: 4,
              margin: 0,
              borderRadius: 6,
              border: autoRoll
                ? '1px solid #f2cf75'
                : '1px solid rgba(228,184,91,0.38)',
              background: autoRoll
                ? 'linear-gradient(180deg,#f5d98f 0%,#c99739 100%)'
                : 'rgba(14,14,14,0.96)',
              color: autoRoll ? '#201606' : '#f4e5c0',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                width: 17,
                height: 17,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <AutoRollIcon />
            </span>
          </button>
        </>
      )}

      {/* Все НЕ-landscape режимы оставляем как были:
          автобросок -> автоход -> настройки. */}
      {!isSpectator && !cashPhoneLandscapeMode && (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleAutoRoll?.();
            }}
            disabled={readOnly || typeof toggleAutoRoll !== 'function'}
            aria-pressed={Boolean(autoRoll)}
            aria-label={translate('game.common.autoRoll', {
              defaultValue: 'Автобросок',
            })}
            title={translate('game.common.autoRoll', {
              defaultValue: 'Автобросок',
            })}
            style={{
              position: 'relative',
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              padding: 4,
              margin: 0,
              borderRadius: 6,
              border: autoRoll
                ? '1px solid #f2cf75'
                : '1px solid rgba(228,184,91,0.38)',
              background: autoRoll
                ? 'linear-gradient(180deg,#f5d98f 0%,#c99739 100%)'
                : 'rgba(14,14,14,0.96)',
              color: autoRoll ? '#201606' : '#f4e5c0',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                width: 17,
                height: 17,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <AutoRollIcon />
            </span>
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleAutoConfirm?.();
            }}
            disabled={readOnly || typeof toggleAutoConfirm !== 'function'}
            aria-pressed={Boolean(autoConfirm)}
            aria-label={translate('game.common.autoMove', {
              defaultValue: 'Автоход',
            })}
            title={translate('game.common.autoMove', {
              defaultValue: 'Автоход',
            })}
            style={{
              position: 'relative',
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              padding: 4,
              margin: 0,
              borderRadius: 6,
              border: autoConfirm
                ? '1px solid #f2cf75'
                : '1px solid rgba(228,184,91,0.38)',
              background: autoConfirm
                ? 'linear-gradient(180deg,#f5d98f 0%,#c99739 100%)'
                : 'rgba(14,14,14,0.96)',
              color: autoConfirm ? '#201606' : '#f4e5c0',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                width: 17,
                height: 17,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <AutoMoveIcon />
            </span>
          </button>
        </>
      )}

      {!cashPhoneLandscapeMode && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowBoardSettings(true);
          }}
          aria-label={translate('game.short.boardSettings', {
            defaultValue: 'Выбрать дизайн доски',
          })}
          title={translate('game.common.settings', {
            defaultValue: 'Настройки',
          })}
          style={{
            width: 28,
            height: 28,
            minWidth: 28,
            minHeight: 28,
            padding: 4,
            margin: 0,
            borderRadius: 6,
            border: '1px solid rgba(228,184,91,0.38)',
            background: 'rgba(14,14,14,0.96)',
            color: '#f4e5c0',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              width: 17,
              height: 17,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <SettingsIcon />
          </span>
        </button>
      )}
    </div>
  )}

                  {/* Модалка результата */}
         {/* Модалка результата */}
{/* Модалка результата */}
{showResultModal && gameResult && !hideResultModalLocal && (
  maybePortal(
    isResultModalMobile ? (
      // =========================
      // ✅ MOBILE RESULT MODAL
      // =========================
      <div
        className="mr-overlay mr-overlay--m"
        role="dialog"
        aria-modal="true"
        aria-label={translate('game.common.matchResult')}
      >
        <div
          className="mr-modal mr-modal--m"
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className="mr-m-header">
            <div className="mr-m-title">{translate('game.common.result')}</div>

          <button
  className="mr-close mr-close--m"
  onClick={closeResultModalLocal}
  
  aria-label={translate('game.common.close')}
  type="button"
>
  ✕
</button>
          </div>

          {/* BODY */}
          <div className="mr-m-body">
            {/* PLAYERS + SCORE */}
            <div className="mr-m-players">
              {/* WINNER */}
              <div className="mr-m-player mr-m-player--winner">
                <Avatar
                  src={winnerAvatarSrc}
                  alt={winnerShownName || "Winner"}
                  size={56}
                  className="mr-m-avatar"
                />

                <div className="mr-m-name">
                  {winnerShownName || translate('game.common.winner')} <span className="mr-m-crown">👑</span>
                </div>

                <div className="mr-m-substats">
                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.level')}</span>
                    <span className="mr-m-subv">{levelByPr(winnerPrOverall)}</span>
                  </div>

                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">PR</span>
                    <span className="mr-m-subv">{fmtPr(winnerPrOverall)}</span>
                  </div>

                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.luck')}</span>
                    <span className="mr-m-subv">{fmtPr(prWinner?.luck)}</span>
                  </div>

                  {/* ✅ Баланс: было → стало */}
                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.balance')}</span>
                    <span className="mr-m-subv mr-m-subv--wide">
                      {fmtMoney(gameResult.initialWinnerBalance ?? gameResult.updatedWinnerBalance)}
                      {" \u2192 "}
                      {fmtMoney(gameResult.updatedWinnerBalance)}
                    </span>
                  </div>

                  {/* ✅ Монеты */}
                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.coins')}</span>
                    <span className="mr-m-subv mr-m-pos">
                      {fmtSignedMoney(gameResult.winnerAmount, "+")}
                    </span>
                  </div>
                </div>
              </div>

              {/* SCORE */}
              <div className="mr-m-score" aria-label={translate('game.common.score')}>
                <div className="mr-m-scoreNum">{winnerScore}</div>
                <div className="mr-m-scoreDash">:</div>
                <div className="mr-m-scoreNum">{loserScore}</div>
              </div>

              {/* LOSER */}
              <div className="mr-m-player mr-m-player--loser">
                <Avatar
                  src={loserAvatarSrc}
                  alt={loserShownName || "Loser"}
                  size={56}
                  className="mr-m-avatar"
                />

                <div className="mr-m-name">{loserShownName || translate('game.common.loser')}</div>

                <div className="mr-m-substats">
                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.level')}</span>
                    <span className="mr-m-subv">{levelByPr(loserPrOverall)}</span>
                  </div>

                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">PR</span>
                    <span className="mr-m-subv">{fmtPr(loserPrOverall)}</span>
                  </div>

                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.luck')}</span>
                    <span className="mr-m-subv">{fmtPr(prLoser?.luck)}</span>
                  </div>

                  {/* ✅ Баланс: было → стало */}
                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.balance')}</span>
                    <span className="mr-m-subv mr-m-subv--wide">
                      {fmtMoney(gameResult.initialLoserBalance ?? gameResult.updatedLoserBalance)}
                      {" \u2192 "}
                      {fmtMoney(gameResult.updatedLoserBalance)}
                    </span>
                  </div>

                  {/* ✅ Монеты */}
                  <div className="mr-m-subrow">
                    <span className="mr-m-subk">{translate('game.common.coins')}</span>
                    <span className="mr-m-subv mr-m-neg">
                      {fmtSignedMoney(
                        gameResult.stake ?? gameResult.finalStake ?? loserLossAmount,
                        "-"
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
         {/* FOOTER */}
<div className="mr-m-footer">
  <button
    className="mr-btn mr-btn--m"
    type="button"
    onClick={closeResultModalLocal}
  >
    {translate('game.analysis.viewAnalysis')}
  </button>
</div>
        </div>
      </div>
    ) : (
    
      // =========================
      // ✅ СТАРАЯ ДЕСКТОПНАЯ МОДАЛКА (ТВОЯ)
      // =========================
      <div className="mr-overlay" role="dialog" aria-modal="true" aria-label={translate('game.common.matchResult')}>
        <div className="mr-modal">
          <button
            className="mr-close"
           onClick={closeResultModalLocal}
            aria-label={translate('game.common.close')}
          >
            ✕
          </button>

          <div className="mr-scroll">
            <div className="mr-title">{translate('game.common.matchResult')}</div>

            <div className="mr-topGrid">
              <div className="mr-player mr-player--winner">
                <div className="mr-role">
                  {translate('game.common.winner')} <span className="mr-crown">👑</span>
                </div>

                <div className="mr-avatarRing mr-avatarRing--winner">
                  <div className="mr-avatarInner">
                    <Avatar
                      src={winnerAvatarSrc}
                      alt={winnerShownName || "Winner"}
                      size={170}
                      className="mr-avatar"
                    />
                  </div>
                </div>

                <div className="mr-name">{winnerShownName || translate('game.common.winner')}</div>
              </div>

              <div className="mr-center">
                <div className="mr-score">
                  <div className="mr-scoreBox">{winnerScore}</div>
                  <div className="mr-scoreDash">-</div>
                  <div className="mr-scoreBox">{loserScore}</div>
                </div>
              </div>

              <div className="mr-player mr-player--loser">
                <div className="mr-role mr-role--loser">{translate('game.common.loser')}</div>

                <div className="mr-avatarRing mr-avatarRing--loser">
                  <div className="mr-avatarInner">
                    <Avatar
                      src={loserAvatarSrc}
                      alt={loserShownName || "Loser"}
                      size={170}
                      className="mr-avatar"
                    />
                  </div>
                </div>

                <div className="mr-name">{loserShownName || translate('game.common.loser')}</div>
              </div>
            </div>

            <div className="mr-stats">
              <div className="mr-statRow">
                <div className="mr-statVal mr-statVal--winner">{levelByPr(winnerPrOverall)}</div>
                <div className="mr-statLabel">{translate('game.analysis.gameLevel')}</div>
                <div className="mr-statVal mr-statVal--loser">{levelByPr(loserPrOverall)}</div>
              </div>

              <div className="mr-statRow">
                <div className="mr-statVal mr-statVal--winner">Pr: {fmtPr(winnerPrOverall)}</div>
                <div className="mr-statLabel">{translate('game.analysis.errorRate')}</div>
                <div className="mr-statVal mr-statVal--loser">{fmtPr(loserPrOverall)}</div>
              </div>

              <div className="mr-statRow">
                <div className="mr-statVal mr-statVal--winner">{fmtPr(prWinner?.luck)}</div>
                <div className="mr-statLabel">{translate('game.common.luck')}</div>
                <div className="mr-statVal mr-statVal--loser">{fmtPr(prLoser?.luck)}</div>
              </div>

              <div className="mr-statRow">
                <div className="mr-statVal mr-statVal--winner">
                  {fmtMoney(gameResult.initialWinnerBalance)} → {fmtMoney(gameResult.updatedWinnerBalance)}
                </div>
                <div className="mr-statLabel">{translate('game.common.balance')}</div>
                <div className="mr-statVal mr-statVal--loser">
                  {fmtMoney(gameResult.initialLoserBalance)} → {fmtMoney(gameResult.updatedLoserBalance)}
                </div>
              </div>

              <div className="mr-statRow">
                <div className="mr-statVal mr-statVal--winner mr-statVal--pos">
                  {fmtSignedMoney(gameResult.winnerAmount, "+")}
                </div>
                <div className="mr-statLabel">{translate('game.analysis.netCoins')}</div>
                <div className="mr-statVal mr-statVal--loser mr-statVal--neg">
                  {fmtSignedMoney(gameResult.stake ?? gameResult.finalStake ?? loserLossAmount, "-")}
                </div>
              </div>
            </div>

            <div className="mr-footer">
              <button className="mr-btn"onClick={closeResultModalLocal}>
                {translate('game.analysis.viewAnalysis')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  )
)}
 

                    {/* Первый бросок (до завершения первого хода) */}
                    {!isReplayMode && !firstMoveComplete &&
                    bothPlayersSeated &&
                      (firstP1 != null || firstP2 != null) && (
                        <div className="first-rolls-display">
                          {firstP1 != null && (
                            <img
                              src={diceImages.white[firstP1]}
                              alt={translate('game.common.diePlayer1', { value: firstP1 })}
                              className="dice-image first-roll-player1"
                            />
                          )}
                          {firstP2 != null && (
                            <img
                              src={diceImages.black[firstP2]}
                              alt={translate('game.common.diePlayer2', { value: firstP2 })}
                              className="dice-image first-roll-player2"
                            />
                          )}
                        </div>
                      )}

                    {/* Кнопка «Бросить кубики» (после первого хода, в свой ход) */}
                    {!gameFinished &&
                    !isSpectator &&
                      !autoRoll &&
                      !showDoubleDialog &&
                      !confirmInProgress &&
                      !hasRolledDice &&
                      firstMoveComplete &&
                      currentTurn ===
                        (playerColor === 'white' ? 'player1' : 'player2') && (
                        <button
                          className="roll-dice-button"
                          onClick={handleRollDiceClickSfx}
                          onTouchEnd={runMobileTap(handleRollDiceClickSfx)}
                        >
                          {translate('game.common.rollDice')}
                        </button>
                      )}

                    {/* Показ текущих кубиков */}
  

                    {/* Отмена хода */}
     <div className="my-undo-zone">
  {showUndoButton && (
    <button
      type="button"
      className="short-btn short-btn--ghost"
      onClick={handleUndoMoveSfx}
    >
      {translate('game.common.cancel')}
    </button>
  )}

  {!gameFinished &&
    canConfirmMove &&
    !autoConfirm &&
    currentTurn === (playerColor === 'white' ? 'player1' : 'player2') &&
    hasRolledDice &&
    numberMove === 0 && (
      <button
        type="button"
        className="ok-button"
        onClick={handleConfirmMoveSfx}
        onTouchEnd={runMobileTap(handleConfirmMoveSfx)}
        aria-label={translate('game.common.finishMove')}
      >
        <img src="/img/Ok.png" alt={translate('game.common.finishMove')} />
      </button>
  )}
</div>


                      

                    {/* Точки/шашки */}
                    {renderTournamentPoints()}
                    
{boardVb.w > 0 && boardVb.h > 0 && (
  <svg
    ref={arrowSvgRef}
    viewBox={`0 0 ${boardVb.w} ${boardVb.h}`}
    preserveAspectRatio="none"
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 9999,
    }}
  >
    <defs>
      <filter id="arrowGlow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="3.5" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>

      {/* GNU зелёные */}
      <linearGradient id="gnuArrowGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="rgba(60, 220, 120, 0.95)" />
        <stop offset="55%" stopColor="rgba(0, 180, 90, 0.95)" />
        <stop offset="100%" stopColor="rgba(120, 255, 180, 0.95)" />
      </linearGradient>
      <marker id="gnuArrowHead" markerWidth="18" markerHeight="18" refX="18" refY="9" orient="auto" markerUnits="userSpaceOnUse">
        <path d="M0,0 L18,9 L0,18 L4.5,9 Z" fill="rgba(0, 180, 90, 0.98)" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
      </marker>

      {/* Наш ход красный */}
      <linearGradient id="playedArrowGradRed" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="rgba(255, 80, 80, 0.95)" />
        <stop offset="55%" stopColor="rgba(255, 0, 160, 0.95)" />
        <stop offset="100%" stopColor="rgba(255, 220, 80, 0.95)" />
      </linearGradient>
      <marker id="playedArrowHeadRed" markerWidth="18" markerHeight="18" refX="18" refY="9" orient="auto" markerUnits="userSpaceOnUse">
        <path d="M0,0 L18,9 L0,18 L4.5,9 Z" fill="rgba(255,80,80,0.98)" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
      </marker>

      {/* Наш ход жёлтый */}
      <linearGradient id="playedArrowGradYellow" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="rgba(255, 235, 120, 0.95)" />
        <stop offset="55%" stopColor="rgba(255, 200, 0, 0.95)" />
        <stop offset="100%" stopColor="rgba(255, 150, 0, 0.95)" />
      </linearGradient>
      <marker id="playedArrowHeadYellow" markerWidth="18" markerHeight="18" refX="18" refY="9" orient="auto" markerUnits="userSpaceOnUse">
        <path d="M0,0 L18,9 L0,18 L4.5,9 Z" fill="rgba(255, 200, 0, 0.98)" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
      </marker>
    </defs>

    {/* 1) GNU (зелёные) — под низ */}
    {gnuArrows.map((a, i) => (
      <g key={`gnu-${i}`}>
        <circle cx={a.sx} cy={a.sy} r="6" fill="rgba(255,255,255,0.9)" opacity="0.9" />
        <circle cx={a.sx} cy={a.sy} r="4" fill="rgba(0, 180, 90, 0.95)" />

        <path
          d={a.d}
          fill="none"
          stroke="url(#gnuArrowGrad)"
          strokeWidth="10"
          strokeLinecap="round"
          opacity="0.18"
          filter="url(#arrowGlow)"
        />
        <path
          d={a.d}
          fill="none"
          stroke="url(#gnuArrowGrad)"
          strokeWidth="5"
          strokeLinecap="round"
          markerEnd="url(#gnuArrowHead)"
          opacity="0.95"
        />
      </g>
    ))}

    {/* 2) Наш ход (красный/жёлтый) — поверх */}
    {playedArrows.map((a, i) => {
      const gradId = playedArrowTone === 'yellow' ? 'playedArrowGradYellow' : 'playedArrowGradRed';
      const headId = playedArrowTone === 'yellow' ? 'playedArrowHeadYellow' : 'playedArrowHeadRed';
      const dotColor = playedArrowTone === 'yellow' ? 'rgba(255, 200, 0, 0.95)' : 'rgba(255,80,80,0.95)';

      return (
        <g key={`played-${i}`}>
          <circle cx={a.sx} cy={a.sy} r="6" fill="rgba(255,255,255,0.9)" opacity="0.9" />
          <circle cx={a.sx} cy={a.sy} r="4" fill={dotColor} />

          <path
            d={a.d}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth="10"
            strokeLinecap="round"
            opacity="0.22"
            filter="url(#arrowGlow)"
          />
          <path
            d={a.d}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth="5"
            strokeLinecap="round"
            markerEnd={`url(#${headId})`}
            opacity="0.97"
          />
        </g>
      );
    })}
  </svg>
)}




                    {/* BAR */}
                    <div className="bar">
                      <div className="bar-opponentColor">
                        {capturedForUi?.[opponentTournamentColor] > 0 && (
                          <div
                            className={`checker ${opponentTournamentColor} ${
                              opponentTournamentColor === 'black'
                                ? 'checkerBar'
                                : 'checkerWhite'
                            }`}
                            draggable="true"
                            onDragStart={(e) =>
                              handleDragStartSfx(e, 25, 0, true)                           
                            }
                               onTouchStart={(e) =>
                              handleTouchStart(e, 25, 0, opponentTournamentColor, true)
                            }
                            >
                            {capturedForUi?.[opponentTournamentColor] > 1
                              ? capturedForUi?.[opponentTournamentColor]
                              : opponentTournamentColor}
                          </div>
                        )}
                      </div>

                      <div className="bar-myColor">
                        {capturedForUi?.[myTournamentColor] > 0 && (
                          <div
                            className={`checker ${myTournamentColor} ${
                              myTournamentColor === 'black'
                                ? 'checkerBar'
                                : 'checkerWhite'
                            }`}
                            draggable="true"
                            onDragStart={(e) =>
                              handleDragStartSfx(e, 25, 0, true)
                            }

                             onTouchStart={(e) =>
                              handleTouchStart(e, 25, 0, myTournamentColor, true)
                            }
                                                  
                            onMouseDown={() => playSfx('click')}
                            onClick={() =>
                              onCheckerClick?.(25, myTournamentColor)
                            }
                          >
                            {capturedForUi?.[myTournamentColor] > 1
                              ? capturedForUi?.[myTournamentColor]
                              : myTournamentColor}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Выведенные шашки (off-board) */}
                    <div className="off-board">
                      <div className="off-board-opponent">
                        {(opponentTournamentColor === 'white'
                          ? whiteOffArr
                          : blackOffArr
                        ).map((_, idx) => (
                          <div
                            key={`opp-off-${idx}`}
                            className={`checker ${opponentTournamentColor}`}
                            // style={{
                            //   top: `${idx * -10}px`,
                            //   zIndex: idx,
                            // }}
                          />
                        ))}
                      </div>

                      <div className="off-board-my">
                        {(myTournamentColor === 'white'
                          ? whiteOffArr
                          : blackOffArr
                        ).map((_, idx) => (
                          <div
                            key={`my-off-${idx}`}
                            className={`checker ${myTournamentColor}`}
                            // style={{
                            //   top: `${idx * -10}px`,
                            //   zIndex: idx,
                            // }}
                          />
                        ))}
                      </div>

                      {/* Модалка подтверждения сдачи */}
                      {showResignDialog && (
                        maybePortal(
                          <div className="double-mask" role="dialog" aria-modal="true" aria-label={translate('game.short.resignConfirmTitle')}>
                            <div className="double-dialog">
                              <div>{translate('game.short.resignQuestionShort')}</div>
                              <div className="btn-row">
                                <button
                                  className="btn-pass"
                                  onClick={() => setShowResignDialog(false)}
                                >
                                  {translate('game.common.cancel')}
                                </button>
                                <button
                                  className="btn-take"
                                  onClick={() => {
                                    setShowResignDialog(false);
                                    onSurrenderClick?.();
                                  }}
                                >
                                  {translate('game.common.surrender')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      )}







                      
                    </div>
 
                  </div>
  








                {isSpectator && (
                  <div className="mobile-actions short-mobile-actions short-mobile-actions--spectator">
                    <button
                      type="button"
                      className="mobile-action-btn"
                      onClick={handleLeaveTableClean}
                    >
                      {translate('game.short.exitSpectator')}
                    </button>
                  </div>
                )}
           </div>
                </div>  
          
                  {/* /.board */}
                  

                  
                </div>


          
               {/* Кнопки под доской */}

                 
    
               
                </div>
         

                
                
              

                {/* /.board-wrapper */}

                       
                
               
              </div>

              {/* /.short-cash */}
          
                
            </div>
            {/* /.short-cash-viewport-inner */}
        


      </div>

      
              
      {/* /.short-cash-viewport */}
    </DisabledWrap>
    
  );


};

export default BoardDisplay;



